import axios, { AxiosHeaders, type AxiosRequestConfig } from 'axios';
import { authDebugEnabled } from '@/lib/auth-debug';
import { getApiBaseUrl, shouldUseAdminLogin } from '@/lib/env';
import { traceMeResponse, traceTokenSaved } from '@/lib/login-redirect-trace';
import {
  TOKEN_STORAGE_KEY,
  ACCESS_COOKIE_NAME,
  buildAuthCookie,
  readAuthCookieFromDocument,
  getJwtCookieName,
  getAccessCookieName,
} from '@/lib/auth-cookie';
import { canAccessOrganizerApisFromStorage } from '@/lib/organizer-access';
import { isChatUnreadPath, isSilentOrBackgroundApiRequest } from '@/lib/auth-session-guards';
import { shouldRefreshSessionAfter401 } from '@/lib/otp-delivery';
import { isPublicAuthRoute, isPublicBrowserPath } from '@/lib/public-routes';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Bypass the application's in-flight GET request deduplication. */
    skipInFlightDedup?: boolean;
    silent?: boolean;
  }
}

const AUTH_TRACE_PATHS = [
  '/admin/login',
  '/admin/login/2fa',
  '/admin/me',
  '/admin/security/2fa/status',
  '/admin/security/2fa/enable',
  '/admin/security/2fa/verify',
  '/admin/refresh',
];

function shouldTraceRequest(url: string): boolean {
  const path = url.split('?')[0];
  return AUTH_TRACE_PATHS.some(
    (trace) => path === trace || path.endsWith(trace) || path.includes('/admin/login')
  );
}

/** Re-export for consumers that build API paths (e.g. cms). */
export { getApiBaseUrl, shouldUseAdminAuth, shouldUseAdminLogin } from '@/lib/env';
export { getOrigin } from '@/lib/env';

const DEFAULT_API_TIMEOUT_MS = 45000;
export const AUTH_API_TIMEOUT_MS = 45000;
/** Session hydration via the Next.js /api proxy to production can exceed 12s. */
export const AUTH_ME_TIMEOUT_MS = 30000;

const api = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  timeout: DEFAULT_API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'X-EnthiosHub-Client': 'spa',
  },
});

let sessionExpiryHandled = false;
const pendingRequestControllers = new Set<AbortController>();
let isRefreshingQueue = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

function failedUrlNormalized(url: unknown): string {
  const raw = String(url || '').split('?')[0];
  try {
    if (/^https?:\/\//i.test(raw)) {
      const pathname = new URL(raw).pathname.replace(/\/+$/, '') || '/';
      return pathname.replace(/^\/api(?=\/)/, '') || '/';
    }
  } catch {
    /* ignore */
  }
  return raw.replace(/^\/+/, '/').replace(/\/+$/, '') || '/';
}

/** Participant quiz pages must not treat an organizer 401 as a session expiry. */
export function isPublicParticipantBrowserPath(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname || '';
  return path.startsWith('/participate') || path.startsWith('/quiz/');
}

export function isPublicApiEndpoint(rawUrl: string): boolean {
  const path = failedUrlNormalized(rawUrl);
  if (path.startsWith('/public/')) return true;
  if (path === '/health' || path.startsWith('/health/')) return true;
  if (path === '/pages' || path.startsWith('/pages/')) return true;
  if (path === '/categories' || path.startsWith('/categories/')) return true;
  if (path === '/tags' || path.startsWith('/tags/')) return true;
  if (path.startsWith('/globe/')) return true;
  if (path.startsWith('/organizer-access/')) return true;
  if (path.startsWith('/organizers/') || path.startsWith('/organizations/')) return true;
  if (path.startsWith('/privacy-policy') || path.startsWith('/terms')) return true;
  if (/^\/activity-invitations\/[^/]+$/.test(path)) return true;
  if (/^\/activity-invitations\/[^/]+\/decline$/.test(path)) return true;
  if (path.startsWith('/davs/v1/public/') || path.startsWith('/davs/v1/verify/')) return true;
  if (path === '/admin-invite/accept' || /^\/admin-invite\/[^/]+$/.test(path)) return true;
  if (/^\/(?:api\/)?admin\/(login|refresh|master-admin-recovery)(?:\/|$)/.test(path)) return true;

  // Authenticated user-only endpoints under /auth:
  if (
    path === '/auth/me' ||
    path === '/api/auth/me' ||
    path === '/auth/profile' ||
    path === '/api/auth/profile' ||
    path === '/auth/logout' ||
    path === '/api/auth/logout'
  ) {
    return false;
  }

  // All other /auth routes (/auth/otp/*, /auth/register*, /auth/login*, /auth/refresh, /auth/forgot-password, /auth/reset-password, /auth/setup-password, /auth/2fa/*, etc.)
  if (/^\/(?:api\/)?auth\//.test(path)) return true;

  // If accessing authenticated subpaths under /events, it is NOT an anonymous endpoint
  if (/\/events\/[^/]+\/(chat|activities|tickets|publish|draft|payout|workspace|analytics|settings|inbox|communications)/.test(path)) {
    return false;
  }
  if (path === '/events' || /^\/events\/[^/]+(?:\/register)?$/.test(path)) return true;

  return false;
}

function isSessionRecoveryEndpoint(url: string): boolean {
  return /^\/(?:api\/)?(?:auth\/(?:login|register|firebase|refresh|2fa)|admin\/(?:login|refresh))(?:\/|$|\?)/.test(url);
}

/** APIs that must keep working after session expiry (public event pages, marketing, forms, registration, OTP). */
function isAnonymousSafeApiEndpoint(url: string): boolean {
  return isPublicApiEndpoint(url);
}

function releaseRequestController(config: any): void {
  const controller = config?.__sessionAbortController as AbortController | undefined;
  if (controller) pendingRequestControllers.delete(controller);
}

function abortPendingRequests(): void {
  for (const controller of pendingRequestControllers) {
    controller.abort('Session expired');
  }
  pendingRequestControllers.clear();
  inFlightGetRequests.clear();
}

const inFlightGetRequests = new Map<string, Promise<unknown>>();
const rawApiGet = api.get.bind(api);

function serializeRequestParams(params: unknown): string {
  if (!params) return '';
  if (params instanceof URLSearchParams) return params.toString();

  try {
    return JSON.stringify(sortRequestParams(params));
  } catch {
    return String(params);
  }
}

function sortRequestParams(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortRequestParams);
  }
  if (!value || typeof value !== 'object' || value instanceof Date) {
    return value;
  }

  const record = value as Record<string, unknown>;
  return Object.keys(record)
    .sort()
    .reduce<Record<string, unknown>>((sorted, key) => {
      sorted[key] = sortRequestParams(record[key]);
      return sorted;
    }, {});
}

function getInFlightRequestKey(url: string, config?: AxiosRequestConfig): string {
  const baseURL = config?.baseURL ?? getApiBaseUrl();
  const params = serializeRequestParams(config?.params);
  return `${baseURL}|GET|${url}|${params}`;
}

type CachedResponse = {
  data: unknown;
  status: number;
  statusText: string;
  headers: any;
  config: AxiosRequestConfig;
  cachedAt: number;
};

const apiResponseCache = new Map<string, CachedResponse>();

export function clearApiResponseCache(filter?: string): void {
  if (!filter) {
    apiResponseCache.clear();
    return;
  }
  for (const key of apiResponseCache.keys()) {
    if (key.includes(filter)) {
      apiResponseCache.delete(key);
    }
  }
}

type ApiGetConfig = AxiosRequestConfig & {
  /** When true, always hit the network — never reuse an in-flight GET promise. */
  skipInFlightDedup?: boolean;
  /** In-memory cache TTL in ms. Returns instant cached response if still fresh. */
  cacheTtlMs?: number;
  /** Client-side silent request flag — prevents toasts/logout on error without sending CORS headers. */
  silent?: boolean;
  skipAuthRedirect?: boolean;
};

declare module 'axios' {
  interface AxiosRequestConfig {
    skipInFlightDedup?: boolean;
    suppressServerErrorToast?: boolean;
    cacheTtlMs?: number;
    silent?: boolean;
    skipAuthRedirect?: boolean;
    _retry?: boolean;
  }
}

api.get = ((url: string, config?: ApiGetConfig) => {
  const cacheTtlMs = config?.cacheTtlMs;
  const key = getInFlightRequestKey(url, config);

  if (cacheTtlMs && cacheTtlMs > 0 && !config?.skipInFlightDedup) {
    const cached = apiResponseCache.get(key);
    if (cached && Date.now() - cached.cachedAt < cacheTtlMs) {
      return Promise.resolve({
        data: cached.data,
        status: cached.status,
        statusText: cached.statusText,
        headers: cached.headers,
        config: config ?? cached.config,
      });
    }
  }

  if (config?.skipInFlightDedup) {
    const { skipInFlightDedup: _skip, cacheTtlMs: _ttl, ...axiosConfig } = config;
    return rawApiGet(url, axiosConfig);
  }

  const existing = inFlightGetRequests.get(key);
  if (existing) {
    return existing as ReturnType<typeof rawApiGet>;
  }

  const request = rawApiGet(url, config)
    .then((response) => {
      if (cacheTtlMs && cacheTtlMs > 0) {
        apiResponseCache.set(key, {
          data: response.data,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          config: response.config,
          cachedAt: Date.now(),
        });
      }
      return response;
    })
    .finally(() => {
      inFlightGetRequests.delete(key);
    });

  inFlightGetRequests.set(key, request);
  return request;
}) as typeof api.get;

/** Drop cached in-flight GETs whose key contains `urlFragment` (e.g. `/events/{id}`). */
export function invalidateInFlightGets(urlFragment: string): void {
  for (const key of inFlightGetRequests.keys()) {
    if (key.includes(urlFragment)) {
      inFlightGetRequests.delete(key);
    }
  }
  clearApiResponseCache(urlFragment);
}

function firstValidationMessage(bag: unknown): string | null {
  if (!bag || typeof bag !== 'object') return null;
  const first = Object.values(bag as Record<string, unknown>).flat()[0];
  return typeof first === 'string' && first.trim() ? first.trim() : null;
}

/** All validation messages for inline field hints or debugging (422). */
export function getApiValidationErrorLines(error: any): string[] {
  const responseData = error?.response?.data;
  const bag = responseData?.errors ?? responseData?.error?.details;
  if (!bag || typeof bag !== 'object') return [];
  const lines: string[] = [];
  for (const [field, msgs] of Object.entries(bag as Record<string, unknown>)) {
    if (Array.isArray(msgs)) {
      for (const m of msgs) {
        if (typeof m === 'string' && m.trim()) {
          lines.push(field ? `${field}: ${m.trim()}` : m.trim());
        }
      }
    } else if (typeof msgs === 'string' && msgs.trim()) {
      lines.push(field ? `${field}: ${msgs.trim()}` : msgs.trim());
    }
  }
  return lines;
}

/** Prefer field-aware validation lines for 422 toasts / form banners. */
export function formatApiValidationErrors(error: any, limit = 4): string | null {
  const lines = getApiValidationErrorLines(error);
  if (lines.length === 0) return null;
  return lines.slice(0, limit).join(' · ');
}

export function getApiErrorCode(error: any): string | null {
  const code = error?.response?.data?.error?.code ?? error?.response?.data?.code;
  return typeof code === 'string' && code.trim() ? code.trim() : null;
}

export function isOrganizerProfileRequiredError(error: unknown): boolean {
  const code = getApiErrorCode(error);
  if (code === 'ORGANIZER_PROFILE_REQUIRED') {
    return true;
  }
  const message = getApiErrorMessage(error, '').toLowerCase();
  return message.includes('organizer profile required');
}

/** Canonical platform login endpoint (non-admin accounts). */
export const AUTH_LOGIN_ENDPOINT = '/auth/login';

/** Canonical admin portal password login (AdminAuthController). */
export const ADMIN_LOGIN_ENDPOINT = '/admin/login';

/**
 * Admin login OTP completion (challenge_token + code).
 *
 * Production probe (api.enthioshub.com + admin.enthioshub.com):
 * - POST /api/admin/login/2fa → 422 on empty body (route exists)
 * - POST /api/auth/login/2fa → 404 (route does NOT exist)
 *
 * Do NOT confuse with /admin/security/2fa/verify — that is authenticator
 * setup/enrollment only (requires an authenticated setup session + { code }).
 */
export const ADMIN_LOGIN_2FA_ENDPOINT = '/admin/login/2fa';
export const ADMIN_LOGIN_2FA_RESET_SETUP_ENDPOINT = '/admin/login/2fa/reset-setup';
export const ADMIN_LOGIN_2FA_RESET_VERIFY_ENDPOINT = '/admin/login/2fa/reset-verify';

const LOGIN_HTTP_ACCEPT = (status: number) => typeof status === 'number' && status < 500;

function isJsonApiPayload(data: unknown, contentType?: string): boolean {
  const type = typeof contentType === 'string' ? contentType.toLowerCase() : '';
  if (type && !type.includes('json') && !type.includes('text/plain')) {
    return false;
  }
  if (typeof data === 'string') {
    const trimmed = data.trimStart();
    if (trimmed.startsWith('<') || trimmed.includes('<!DOCTYPE')) {
      return false;
    }
    try {
      JSON.parse(data);
      return true;
    } catch {
      return false;
    }
  }
  return data !== null && typeof data === 'object';
}

export function resolveLoginEndpoint(): typeof AUTH_LOGIN_ENDPOINT | typeof ADMIN_LOGIN_ENDPOINT {
  return shouldUseAdminLogin() ? ADMIN_LOGIN_ENDPOINT : AUTH_LOGIN_ENDPOINT;
}

function loginResponseData(data: unknown): Record<string, unknown> | null {
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
}

export function getLoginErrorCodeFromResponse(data?: unknown): string | null {
  const body = loginResponseData(data);
  const nestedError = body?.error && typeof body.error === 'object'
    ? (body.error as Record<string, unknown>).code
    : null;
  if (typeof nestedError === 'string' && nestedError.trim()) {
    return nestedError.trim();
  }
  // Some gateways flatten error codes; still treat admin-portal redirects as admin login required.
  const redirect = getLoginErrorRedirectFromResponse(data);
  if (redirect && (redirect.includes('admin=1') || redirect.includes('/login?admin'))) {
    return 'ADMIN_LOGIN_REQUIRED';
  }
  return null;
}

/** Server-provided redirect for login errors (e.g. ADMIN_LOGIN_REQUIRED → /login?admin=1). */
export function getLoginErrorRedirectFromResponse(data?: unknown): string | null {
  const body = loginResponseData(data);
  const nestedRedirect =
    body?.error && typeof body.error === 'object'
      ? (body.error as Record<string, unknown>).redirect
      : null;
  if (typeof nestedRedirect === 'string' && nestedRedirect.trim().startsWith('/')) {
    return nestedRedirect.trim();
  }
  if (typeof body?.redirect === 'string' && body.redirect.trim().startsWith('/')) {
    return body.redirect.trim();
  }
  return null;
}

/** Map login HTTP status + JSON body to a user-facing message. */
export function getLoginErrorMessageFromResponse(status: number, data?: unknown): string {
  const body = loginResponseData(data);
  const errorCode = getLoginErrorCodeFromResponse(data);
  if (errorCode === 'ACCOUNT_PENDING_APPROVAL') {
    return 'Your account is awaiting approval from your organization.';
  }
  if (errorCode === 'ACCOUNT_REJECTED') {
    return 'Your account application has been declined by your organization.';
  }
  if (errorCode === 'ADMIN_LOGIN_REQUIRED') {
    return 'Admin accounts must sign in through the admin portal.';
  }
  if (errorCode === 'ORGANIZER_APPROVAL_PENDING') {
    return 'Your Individual Organizer account is awaiting admin approval.';
  }
  if (errorCode === 'ORGANIZER_APPLICATION_REJECTED') {
    const reason = typeof body?.error === 'object' && body.error && typeof (body.error as Record<string, unknown>).reason === 'string'
      ? ` Reason: ${(body.error as Record<string, unknown>).reason}`
      : '';
    return `Your Individual Organizer application was not approved.${reason}`;
  }
  if (errorCode === 'FORBIDDEN' || errorCode === 'ACCESS_REVOKED') {
    return typeof body?.error === 'object' &&
      body.error &&
      typeof (body.error as Record<string, unknown>).message === 'string'
      ? String((body.error as Record<string, unknown>).message)
      : 'Access denied';
  }
  const fromErrors = firstValidationMessage(body?.errors);
  if (fromErrors) return fromErrors;

  const fromDetails = firstValidationMessage(body?.error && typeof body.error === 'object'
    ? (body.error as Record<string, unknown>).details
    : undefined);
  if (fromDetails) return fromDetails;

  const nestedError = body?.error && typeof body.error === 'object'
    ? (body.error as Record<string, unknown>).message
    : null;
  if (typeof nestedError === 'string' && nestedError.trim()) {
    return nestedError.trim();
  }

  if (typeof body?.message === 'string' && body.message.trim()) {
    return body.message.trim();
  }

  switch (status) {
    case 401:
      return 'Invalid email or password';
    case 403:
      return 'Access denied. If you are an admin, sign in through the admin portal.';
    case 404:
      return 'Login endpoint not found';
    case 422:
      return 'Validation failed';
    case 429: {
      const retryAfter = body?.retry_after;
      if (typeof retryAfter === 'number' && retryAfter > 0) {
        return `Too many login attempts. Try again in ${retryAfter}s.`;
      }
      return 'Too many login attempts. Please wait and try again.';
    }
    case 502:
    case 503:
      return 'Service temporarily unavailable. Please try again shortly.';
    case 504:
      return 'The request timed out. Please try again shortly.';
    default:
      if (status >= 500) {
        if (!isJsonApiPayload(data)) {
          return 'Unable to reach the API server. Make sure Laravel is running and try again.';
        }
        return 'Internal server error. Please try again shortly.';
      }
      return 'Login failed. Please try again.';
  }
}

/** Login-specific errors — never map 401/403 to "backend offline". */
export function getLoginErrorMessage(
  error: unknown,
  response?: { status: number; data?: unknown }
): string {
  if (response) {
    return getLoginErrorMessageFromResponse(response.status, response.data);
  }

  const axiosError = error as {
    response?: { status?: number; data?: unknown };
    code?: string;
    message?: string;
  };

  if (axiosError?.response?.status) {
    return getLoginErrorMessageFromResponse(axiosError.response.status, axiosError.response.data);
  }

  const code = typeof axiosError?.code === 'string' ? axiosError.code : '';
  const axiosMsg = typeof axiosError?.message === 'string' ? axiosError.message.toLowerCase() : '';
  if (code === 'ECONNABORTED' || axiosMsg.includes('timeout')) {
    return 'The server is taking too long to respond. Wait a moment and try again.';
  }

  if (!axiosError?.response) {
    return 'Unable to reach the server. Check your connection and try again.';
  }

  return 'Login failed. Please try again.';
}

const OTP_ERROR_MESSAGES: Record<string, string> = {
  provider_not_configured:
    'Email delivery is not configured. Set RESEND_API_KEY or a transactional SMTP provider (Gmail SMTP is not supported for verification emails in production).',
  email_auth_failed:
    'Mail authentication failed. Verify your SMTP credentials or Resend API key and sender domain.',
  email_smtp_unavailable:
    'SMTP is unavailable for verification emails. Configure Resend or a transactional SMTP provider instead of Gmail.',
  email_quota_exceeded: 'Email sending limit reached. Please try again later.',
  email_rate_limited: 'Too many email requests. Please wait a few minutes and try again.',
  email_temporarily_unavailable: 'SMTP server is temporarily unavailable. Please try again shortly.',
  email_failed: 'Unable to send verification email. Please try again later.',
  rate_limit_exceeded: 'Too many OTP requests. Please try again later.',
};

export function getApiErrorMessage(error: any, fallback = 'Something went wrong. Please try again.'): string {
  const status = error?.response?.status;
  const responseData = error?.response?.data;

  const validationSummary = formatApiValidationErrors(error);
  if (validationSummary) return validationSummary;

  const fromErrors = firstValidationMessage(responseData?.errors);
  if (fromErrors) return fromErrors;

  const fromDetails = firstValidationMessage(responseData?.error?.details);
  if (fromDetails) return fromDetails;

  if (responseData?.message && typeof responseData.message === 'string' && responseData.message.trim()) {
    const trimmed = responseData.message.trim();
    // Don't relabel generic API 500s as "admin" — that message is only for /api/admin/*.
    if (trimmed.toLowerCase() === 'an unexpected server error occurred.') {
      const requestId = responseData?.error?.request_id ?? responseData?.request_id ?? null;
      const endpoint =
        typeof responseData?.error?.endpoint === 'string' ? responseData.error.endpoint : '';
      const isAdmin =
        /(?:^|\s)(?:GET|POST|PUT|PATCH|DELETE)\s+api\/admin\b/i.test(endpoint) ||
        String(error?.config?.url ?? '').includes('/admin/');
      const base = isAdmin
        ? 'A server error occurred while processing this admin request.'
        : 'A server error occurred. Please try again shortly.';
      return requestId ? `${base} (Request ID: ${requestId})` : base;
    }
    return trimmed;
  }

  const otpErrorCode = typeof responseData?.error_code === 'string' ? responseData.error_code : '';
  if (otpErrorCode) {
    const otpMessage = OTP_ERROR_MESSAGES[otpErrorCode];
    if (otpMessage) return otpMessage;
  }

  if (responseData?.error?.message && typeof responseData.error.message === 'string') {
    return responseData.error.message;
  }

  const code = typeof error?.code === 'string' ? error.code : '';
  const axiosMsg = typeof error?.message === 'string' ? error.message.toLowerCase() : '';
  if (axios.isCancel(error) || error?.name === 'CanceledError' || axiosMsg === 'canceled') {
    return 'Request was canceled. Please try again.';
  }

  if (code === 'ECONNABORTED' || axiosMsg.includes('timeout')) {
    return 'The server is taking too long to respond. Wait a moment and try again.';
  }

  if (status === 401) {
    const url = String(error?.config?.url ?? '');
    if (url.includes('/login') || url.includes('/authenticate')) {
      return 'Invalid email or password';
    }
    return responseData?.message || responseData?.error?.message || 'Authentication required.';
  }

  if (status === 403) {
    return responseData?.error?.message?.trim() || 'Access denied';
  }

  if (!error?.response) {
    if (error instanceof Error && error.message && !error.message.toLowerCase().includes('network error')) {
      return error.message;
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return 'No internet connection. Please check your network and try again.';
    }
    return 'Unable to reach the server. Check your connection and try again.';
  }

  if (status === 404) {
    return 'Requested resource was not found.';
  }

  if (status === 422) {
    return responseData?.message || responseData?.error?.message || 'Validation failed';
  }

  if (status === 503) {
    const msg = responseData?.message?.trim();
    return msg || 'Service temporarily unavailable. Please try again shortly.';
  }

  if (status === 429) {
    const retryAfter = responseData?.retry_after;
    if (typeof retryAfter === 'number' && retryAfter > 0) {
      return `Too many attempts. Try again in ${retryAfter}s.`;
    }
    return responseData?.message || 'Too many requests. Please wait and try again.';
  }

  if (status === 502) {
    return 'Service temporarily unavailable. Please try again shortly.';
  }
  if (status === 504) {
    return 'The request timed out. Please try again shortly.';
  }

  if (status >= 500) {
    if (!isJsonApiPayload(responseData)) {
      return 'Unable to reach the API server. Make sure Laravel is running and try again.';
    }
    const requestId =
      responseData?.error?.request_id ?? responseData?.request_id ?? null;
    const base = 'A server error occurred. Please try again shortly.';
    return requestId ? `${base} (Request ID: ${requestId})` : base;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

// Key used historically for JWT in localStorage (cleared on boot; no longer written).
export { TOKEN_STORAGE_KEY, ACCESS_COOKIE_NAME } from '@/lib/auth-cookie';
export const REFRESH_TOKEN_STORAGE_KEY = 'EnthiosHub_refresh';

/** Dedicated storage for registration/verification tokens (isolated from user auth sessions). */
export const REGISTRATION_TOKEN_STORAGE_KEY = 'EnthiosHub_registration_token';
let memoryRegistrationToken: string | null = null;

export function setRegistrationToken(token: string | null): void {
  memoryRegistrationToken = token && typeof token === 'string' ? token.trim() : null;
  if (typeof window !== 'undefined') {
    try {
      if (memoryRegistrationToken) {
        sessionStorage.setItem(REGISTRATION_TOKEN_STORAGE_KEY, memoryRegistrationToken);
      } else {
        sessionStorage.removeItem(REGISTRATION_TOKEN_STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
  }
}

export function getRegistrationToken(): string | null {
  if (memoryRegistrationToken && memoryRegistrationToken.length > 20) {
    return memoryRegistrationToken;
  }
  if (typeof window !== 'undefined') {
    try {
      const stored = sessionStorage.getItem(REGISTRATION_TOKEN_STORAGE_KEY);
      if (stored && stored.trim().length > 20) {
        memoryRegistrationToken = stored.trim();
        return memoryRegistrationToken;
      }
    } catch {
      /* ignore */
    }
  }
  return null;
}

export function clearRegistrationToken(): void {
  memoryRegistrationToken = null;
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(REGISTRATION_TOKEN_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
}

/** In-memory access JWT only (AUTH-003). Never persist secrets in localStorage. */
import {
  clearMemoryAccessToken,
  getMemoryAccessToken,
  setMemoryAccessToken,
} from '@/lib/access-token-memory';

/** True after login/refresh when HttpOnly cookies are expected to carry the session. */
let cookieSessionActive = false;

function extractAuthToken(payload: any): string | null {
  if (!payload || typeof payload !== 'object') return null;

  // Try direct token first
  const direct = payload.token ?? payload.access_token ?? payload.jwt;
  if (typeof direct === 'string' && direct.trim()) {
    const trimmed = direct.trim();
    if (trimmed.length > 20) return trimmed; // Must be long enough to be a real token
  }

  // Try nested in data
  const nested = payload.data;
  if (nested && typeof nested === 'object') {
    const nestedToken = nested.token ?? nested.access_token ?? nested.jwt;
    if (typeof nestedToken === 'string' && nestedToken.trim()) {
      const trimmed = nestedToken.trim();
      if (trimmed.length > 20) return trimmed;
    }
  }

  return null;
}

function clearLegacyBrowserAuthSecrets(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem('EnthiosHub_jwt');
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem('EnthiosHub_jwt');
    sessionStorage.removeItem(REGISTRATION_TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  // Expire legacy JS-readable cookie.
  document.cookie = `${TOKEN_STORAGE_KEY}=; Path=/; Max-Age=0; SameSite=Lax`;
}

function syncMiddlewareAuthCookie(token: string | null): void {
  if (typeof window === 'undefined' || !token) return;
  try {
    document.cookie = buildAuthCookie(token);
  } catch {
    /* ignore */
  }
}

export function persistAuthToken(payload: any, _options?: { remember?: boolean }): void {
  if (typeof window === 'undefined') return;
  sessionExpiryHandled = false;
  const token = extractAuthToken(payload);
  userRefreshUnavailable = false;
  if (token) {
    setMemoryAccessToken(token);
    syncMiddlewareAuthCookie(token);
    try {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      localStorage.setItem('EnthiosHub_jwt', token);
      sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    } catch {
      /* ignore */
    }
  }
  // Refresh secrets must live in HttpOnly cookies only — never store from JSON body.
  cookieSessionActive = true;
  traceTokenSaved('api.persistAuthToken', payload);
}

function isUsableAccessToken(token: string | null | undefined): boolean {
  if (typeof token !== 'string') return false;
  const trimmed = token.trim();
  if (trimmed.length <= 20 || trimmed === 'undefined' || trimmed === 'null') {
    return false;
  }
  return !isJwtExpiredString(trimmed);
}

/**
 * Restore the access JWT from memory, JS middleware cookie, or storage fallback.
 * Guarantees tokens survive Next.js Fast Refresh and cross-origin dev proxying.
 */
export function restoreMemoryAccessTokenFromCookie(): string | null {
  if (typeof window === 'undefined') return null;
  const memory = getMemoryAccessToken();
  if (isUsableAccessToken(memory)) {
    cookieSessionActive = true;
    return memory!.trim();
  }
  const fromCookie = readAuthCookieFromDocument();
  if (isUsableAccessToken(fromCookie)) {
    const token = fromCookie!.trim();
    setMemoryAccessToken(token);
    cookieSessionActive = true;
    return token;
  }
  try {
    const fromStorage =
      localStorage.getItem(TOKEN_STORAGE_KEY) ||
      localStorage.getItem('EnthiosHub_jwt') ||
      sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (isUsableAccessToken(fromStorage)) {
      const token = fromStorage!.trim();
      setMemoryAccessToken(token);
      syncMiddlewareAuthCookie(token);
      cookieSessionActive = true;
      return token;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** Only invalid/expired auth may log the user out. Permission 403s stay signed in. */
export function isHardUnauthenticatedFailure(status?: number | null, code?: string | null): boolean {
  if (status === 401) return true;
  if (status !== 403) return false;
  const normalized = String(code ?? '').toUpperCase();
  return normalized === 'UNAUTHENTICATED' || normalized === 'SESSION_EXPIRED';
}

export function clearAuthToken(): void {
  if (typeof window === 'undefined') return;
  sessionExpiryHandled = false;
  clearMemoryAccessToken();
  clearRegistrationToken();
  cookieSessionActive = false;
  clearLegacyBrowserAuthSecrets();
  isRefreshingQueue = false;
  try {
    localStorage.removeItem('auth-storage');
    sessionStorage.removeItem('auth-storage');
  } catch {
    /* ignore */
  }
  processQueue(new Error('Authentication session cleared.'), null);
  window.dispatchEvent(new CustomEvent('auth:unauthenticated'));
}

export function isJwtExpiredString(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = typeof atob !== 'undefined'
      ? atob(base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '='))
      : Buffer.from(base64, 'base64').toString('utf8');
    const payload = JSON.parse(json);
    const exp = Number(payload.exp ?? 0);
    if (!Number.isFinite(exp) || exp <= 0) return false;
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
}

/** Returns true only if an authenticated user session exists in client state/storage. */
export function hasAuthenticatedUser(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem('auth-storage');
    if (raw) {
      const parsed = JSON.parse(raw);
      const user = parsed?.state?.user;
      const isAuthed = Boolean(parsed?.state?.isAuthenticated);
      if (isAuthed && user && (user.id || user.email)) {
        return true;
      }
    }
  } catch {
    /* ignore */
  }
  return false;
}

export function hasStoredAuthSession(): boolean {
  if (typeof window === 'undefined') return false;

  // Regular user session: requires authenticated user object in state/storage
  if (hasAuthenticatedUser()) {
    if (!userRefreshUnavailable) return true;
    const token = getMemoryAccessToken();
    if (typeof token === 'string' && token.trim().length > 20 && !isJwtExpiredString(token.trim())) {
      return true;
    }
    if (restoreMemoryAccessTokenFromCookie()) return true;
    if (cookieSessionActive) return true;
  }

  // Admin session: admin users authenticate via admin endpoints
  const token = getMemoryAccessToken() || restoreMemoryAccessTokenFromCookie();
  if (typeof token === 'string' && token.trim().length > 20 && !isJwtExpiredString(token.trim())) {
    try {
      const parts = token.trim().split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        const role = String(payload?.role ?? '').toLowerCase();
        const isAdmin = Boolean(payload?.is_admin || role === 'admin' || role === 'super_admin' || role === 'system_admin');
        if (isAdmin) {
          return true;
        }
      }
    } catch {
      /* ignore */
    }
  }

  return false;
}

export function hasStoredAuthToken(): boolean {
  if (typeof window === 'undefined') return false;
  if (hasAuthenticatedUser()) {
    if (restoreMemoryAccessTokenFromCookie()) return true;
    if (cookieSessionActive) return true;
    const token = getMemoryAccessToken();
    return typeof token === 'string' && token.trim().length > 20 && !isJwtExpiredString(token.trim());
  }
  const token = getMemoryAccessToken();
  return typeof token === 'string' && token.trim().length > 20 && !isJwtExpiredString(token.trim());
}

/**
 * Returns true if an active auth token or auth cookie exists in this browser context.
 * Used on public routes to avoid sending unneeded /auth/me or /admin/me verification calls
 * when the visitor is completely unauthenticated.
 */
export function hasAuthTokenOrCookie(): boolean {
  if (typeof window === 'undefined') return false;

  const memToken = getMemoryAccessToken();
  if (isUsableAccessToken(memToken)) {
    return true;
  }

  const cookieToken = readAuthCookieFromDocument();
  if (isUsableAccessToken(cookieToken)) {
    return true;
  }

  try {
    const fromStorage =
      localStorage.getItem(TOKEN_STORAGE_KEY) ||
      localStorage.getItem('EnthiosHub_jwt') ||
      sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (isUsableAccessToken(fromStorage)) {
      return true;
    }
  } catch {
    /* ignore */
  }

  try {
    const rawCookies = typeof document !== 'undefined' ? document.cookie : '';
    if (rawCookies) {
      const parts = rawCookies.split(';');
      const candidateKeys = [
        getJwtCookieName(),
        getAccessCookieName(),
        'EnthiosHub_jwt',
        'EnthiosHub_staging_jwt',
        'enthioshub_token',
        'enthioshub_staging_token',
      ];
      for (const part of parts) {
        const [rawName, ...rest] = part.trim().split('=');
        if (candidateKeys.includes(rawName)) {
          const val = decodeURIComponent(rest.join('=').trim());
          if (val.length > 20) {
            return true;
          }
        }
      }
    }
  } catch {
    /* ignore */
  }

  return false;
}

/** Mark session as cookie-backed before /me (HttpOnly access cookie is not JS-readable). */
export function assumeCookieSession(): void {
  if (typeof window === 'undefined') return;
  cookieSessionActive = true;
}

export { getMemoryAccessToken };

let authReadyWaiter: (() => Promise<void>) | null = null;

export function registerAuthReadyWaiter(waiter: () => Promise<void>): void {
  authReadyWaiter = waiter;
}

export async function waitForAuthReady(): Promise<void> {
  if (authReadyWaiter) {
    try {
      await authReadyWaiter();
    } catch {
      // ignore
    }
  }
}

let adminRefreshInFlight: Promise<boolean> | null = null;
let userRefreshInFlight: Promise<boolean> | null = null;
let userRefreshUnavailable = false;

export function isUserRefreshUnavailable(): boolean {
  return userRefreshUnavailable;
}

export async function tryUserTokenRefresh(): Promise<boolean> {
  if (userRefreshUnavailable) return false;
  // Skip refresh if no authenticated user exists
  if (!hasAuthenticatedUser() || !hasStoredAuthSession()) return false;
  if (userRefreshInFlight) return userRefreshInFlight;

  userRefreshInFlight = (async () => {
    const url = `${getApiBaseUrl().replace(/\/$/, '')}/auth/refresh`;
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-EnthiosHub-Client': 'spa',
    };
    const memory = getMemoryAccessToken();
    if (typeof memory === 'string' && memory.trim().length > 20) {
      headers.Authorization = `Bearer ${memory.trim()}`;
    }

    try {
      let res;
      try {
        res = await axios.post(url, {}, {
          withCredentials: true,
          headers,
          maxRedirects: 5,
        });
      } catch (error: any) {
        if (error?.response?.status === 405) {
          res = await axios.get(url, {
            withCredentials: true,
            headers,
            maxRedirects: 5,
          });
        } else {
          throw error;
        }
      }
      persistAuthToken(res.data);
      return hasStoredAuthToken();
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        userRefreshUnavailable = true;
        clearAuthToken();
      } else if (status === 429) {
        userRefreshUnavailable = true;
        window.setTimeout(() => {
          userRefreshUnavailable = false;
        }, 30000);
      }
      return false;
    }
  })().finally(() => {
    userRefreshInFlight = null;
  });

  return userRefreshInFlight;
}

export async function tryAdminTokenRefresh(): Promise<boolean> {
  if (!hasStoredAuthSession()) return false;
  if (adminRefreshInFlight) return adminRefreshInFlight;

  adminRefreshInFlight = (async () => {
    try {
      const headers: Record<string, string> = {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-EnthiosHub-Client': 'spa',
      };
      // Send the (possibly expired) in-memory access token so the backend can preserve
      // two_factor_verified when rotating the admin session.
      const existing = getMemoryAccessToken();
      if (typeof existing === 'string' && existing.trim().length > 20) {
        headers.Authorization = `Bearer ${existing.trim()}`;
      }
      const res = await axios.post(
        `${getApiBaseUrl()}/admin/refresh`,
        {},
        { withCredentials: true, headers },
      );
      persistAuthToken(res.data);
      return hasStoredAuthToken();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        clearAuthToken();
      }
      return false;
    }
  })().finally(() => {
    adminRefreshInFlight = null;
  });

  return adminRefreshInFlight;
}

export function isAdminTwoFactorLoginResponse(payload: any): boolean {
  return getAdminTwoFactorLoginChallenge(payload) !== null;
}

export function getAdminSetupLoginReason(payload: unknown): string | null {
  const data = (payload as { data?: { reason?: unknown } } | null)?.data ?? payload;
  const reason = (data as { reason?: unknown } | null)?.reason;
  return typeof reason === 'string' && reason.trim() ? reason.trim() : null;
}

export function isAdminSetupRequiredLoginResponse(payload: any): boolean {
  const data = payload?.data ?? payload;
  return Boolean(data?.setup_required && data?.access_token);
}

/**
 * Extract admin 2FA challenge from login payloads.
 * Accepts both `{ data: { two_factor_required, challenge_token, user } }` and
 * flat `{ two_factor_required, challenge_token, user }` shapes.
 */
export function getAdminTwoFactorLoginChallenge(payload: any): {
  challengeToken: string;
  user: Record<string, unknown> | null;
} | null {
  if (!payload || typeof payload !== 'object') return null;

  const root = payload as Record<string, unknown>;
  const nested =
    root.data && typeof root.data === 'object'
      ? (root.data as Record<string, unknown>)
      : null;
  const candidates = [nested, root].filter(Boolean) as Record<string, unknown>[];

  for (const data of candidates) {
    const rawToken = data.challenge_token;
    const challengeToken =
      typeof rawToken === 'string'
        ? rawToken.trim()
        : typeof rawToken === 'number'
          ? String(rawToken)
          : '';
    const requires2fa = Boolean(data.two_factor_required || data.requires_2fa);
    if (!requires2fa || challengeToken.length < 32) continue;

    const userCandidate =
      (data.user && typeof data.user === 'object'
        ? (data.user as Record<string, unknown>)
        : null) ??
      (nested?.user && typeof nested.user === 'object'
        ? (nested.user as Record<string, unknown>)
        : null);

    return { challengeToken, user: userCandidate };
  }

  return null;
}

// Request interceptor: attach JWT as Bearer token on every request.
// Needed because HttpOnly cookies are not forwarded cross-origin on localhost
// (different port = different origin; browsers block cross-origin cookies unless
//  SameSite=None;Secure which requires HTTPS).
api.interceptors.request.use(async (config) => {
  config.baseURL = getApiBaseUrl();
  if (authDebugEnabled()) {
    (config as { __requestStartedAt?: number }).__requestStartedAt = Date.now();
  }
  const requestUrl = String(config.url ?? '').replace(/^\/+/, '/');
  // Reject requests with empty path variables (e.g. /activities//conference or /events//...)
  // Consecutive slashes anywhere in the path (excluding protocol) indicate a missing parameter
  // which will cause server 301 redirects and break CORS preflight.
  const pathWithoutProtocol = requestUrl.replace(/^https?:\/\/[^/]+/i, '');
  if (/\/{2,}/.test(pathWithoutProtocol)) {
    return Promise.reject(new Error(`Malformed request URL: path "${requestUrl}" contains empty segment.`));
  }
  const isAuthEntrypoint =
    /^\/(login|auth\/login|auth\/firebase|admin\/login)(\/|$|\?)/.test(requestUrl);

  const isPublicReq = isPublicApiEndpoint(requestUrl) || isSessionRecoveryEndpoint(requestUrl);

  if (
    typeof AbortController !== 'undefined' &&
    !isPublicReq &&
    !isAnonymousSafeApiEndpoint(requestUrl) &&
    !isAuthEntrypoint
  ) {
    const existing = (config as any).__sessionAbortController as AbortController | undefined;
    const controller = existing && !existing.signal.aborted ? existing : new AbortController();
    (config as any).__sessionAbortController = controller;
    config.signal = controller.signal;
    pendingRequestControllers.add(controller);
  }

  if (requestUrl.startsWith('/organizer/') && !canAccessOrganizerApisFromStorage()) {
    return Promise.reject({
      config,
      response: {
        status: 403,
        data: {
          success: false,
          message: 'Organizer access required for this endpoint.',
        },
      },
      message: 'Organizer access required for this endpoint.',
    });
  }

  if (typeof window !== 'undefined') {
    const headers = AxiosHeaders.from(config.headers);
    const explicitAuth = headers.get('Authorization');
    // Prefer cookie session (HttpOnly) with optional in-memory Bearer for local/dev.
    const isPublicAdminEntrypoint =
      /^\/admin\/(login|refresh|master-admin-recovery)(\/|$|\?)/.test(requestUrl);
    const requiresAuthSession =
      requestUrl === '/registrations' ||
      requestUrl.startsWith('/refund-requests') ||
      requestUrl.startsWith('/user/') ||
      requestUrl.startsWith('/tickets') ||
      requestUrl.startsWith('/my/') ||
      requestUrl.startsWith('/organizer/') ||
      requestUrl.startsWith('/activities/') ||
      (requestUrl.startsWith('/admin/') && !isPublicAdminEntrypoint);

    const chatUnread = isChatUnreadPath(requestUrl);
    if (requiresAuthSession || chatUnread) {
      await waitForAuthReady();
    }
    if (explicitAuth === '') {
      headers.delete('Authorization');
      config.headers = headers;
    } else if (!isAuthEntrypoint) {
      const isRegistration = /^\/(register|auth\/register)(\/|$|\?)/.test(requestUrl);
      const isOrganizerAccess = requestUrl.startsWith('/organizer-access/');
      let token: string | null = null;
      if (isRegistration) {
        token = getRegistrationToken() || restoreMemoryAccessTokenFromCookie() || getMemoryAccessToken();
      } else if (
        hasAuthenticatedUser() ||
        chatUnread ||
        isPublicAdminEntrypoint ||
        requestUrl.startsWith('/admin/') ||
        isOrganizerAccess
      ) {
        token = restoreMemoryAccessTokenFromCookie();
        if (!token) {
          const memory = getMemoryAccessToken();
          if (typeof memory === 'string' && memory.trim().length > 20) {
            token = memory.trim();
          } else {
            try {
              const stored =
                localStorage.getItem(TOKEN_STORAGE_KEY) ||
                localStorage.getItem('EnthiosHub_jwt') ||
                sessionStorage.getItem(TOKEN_STORAGE_KEY) ||
                (isOrganizerAccess ? getRegistrationToken() : null);
              if (stored && stored.trim().length > 20) {
                token = stored.trim();
              }
            } catch {
              /* ignore */
            }
          }
        }
        if (!token && isOrganizerAccess) {
          token = getRegistrationToken();
        }
      }
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
        if (isRegistration) {
          headers.set('X-Registration-Token', token);
        }
        config.headers = headers;
        (config as { __requestAccessToken?: string }).__requestAccessToken = token;
      } else {
        (config as { __noAuthToken?: boolean }).__noAuthToken = true;
        // Cookie-authenticated sessions may have no JS-readable token; do not
        // short-circuit — let the backend validate HttpOnly cookies.
        if (
          (chatUnread && !cookieSessionActive) ||
          (requiresAuthSession && !cookieSessionActive && !hasStoredAuthSession())
        ) {
          return Promise.reject({
            config,
            response: {
              status: 401,
              data: {
                success: false,
                message: 'Authentication required.',
              },
            },
            message: 'Authentication required.',
          });
        }
      }
    }
  }

  // Default axios Content-Type is application/json; that breaks multipart file uploads.
  // Omit Content-Type so the runtime sets multipart/form-data with the correct boundary.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const headers = AxiosHeaders.from(config.headers);
    headers.delete('Content-Type');
    headers.delete('content-type');
    config.headers = headers;
  }

  return config;
});

function logTracedResponse(config: unknown, status: number, extra?: Record<string, unknown>): void {
  if (!authDebugEnabled() || !config || typeof config !== 'object') return;
  const cfg = config as { url?: string; method?: string; __requestStartedAt?: number; __retryCount?: number };
  const url = String(cfg.url ?? '');
  if (!shouldTraceRequest(url)) return;
  const startedAt = cfg.__requestStartedAt ?? Date.now();
  const duration = Date.now() - startedAt;
  const payload = {
    request: `${String(cfg.method ?? 'get').toUpperCase()} ${url}`,
    duration: `${duration}ms`,
    status,
    retryCount: cfg.__retryCount ?? 0,
    ...extra,
  };
  if (duration > 10000) {
    console.warn('SLOW_ENDPOINT', payload);
  } else {
    console.info('request', payload);
  }
}

// Response interceptor: only clear auth on 401 when the failing request was NOT to an auth endpoint
// (e.g. /auth/login 401 = wrong password, don't clear; /auth/me 401 = token invalid, clear).
api.interceptors.response.use(
  (response) => {
    releaseRequestController(response.config);
    logTracedResponse(response.config, response.status);
    const method = response.config?.method?.toLowerCase();
    if (method && ['post', 'put', 'patch', 'delete'].includes(method)) {
      clearApiResponseCache();
    }
    return response;
  },
  async (error) => {
    releaseRequestController(error?.config);
    if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }
    logTracedResponse(error?.config, error?.response?.status ?? 0, {
      error: error?.code ?? error?.message,
    });
    const config = error?.config as any;
    const status = error?.response?.status;
    const isMaintenance503 = status === 503 && Boolean(error?.response?.data?.maintenance);

    if (isMaintenance503) {
      if (typeof window !== 'undefined') {
        const pathname = window.location.pathname;
        const isAdmin = pathname.includes('/admin');
        const isAuth = pathname.includes('/login') || pathname.includes('/register');
        const isMaintenancePage = pathname === '/maintenance';
        if (!isAdmin && !isAuth && !isMaintenancePage) {
          window.location.href = '/maintenance';
        }
      }
      return Promise.reject(error);
    }

    if (status === 429) {
      return Promise.reject(error);
    }

    if (status === 422) {
      return Promise.reject(error);
    }

    const isIdempotent = String(config?.method ?? 'get').toLowerCase() === 'get';
    const isRetriableStatus = status === 502 || status === 503 || status === 504;
    if (config && isIdempotent && isRetriableStatus) {
      config.__retryCount = Number(config.__retryCount ?? 0);
      if (config.__retryCount < 1) {
        config.__retryCount += 1;
        await new Promise((resolve) => setTimeout(resolve, 500));
        return api.request(config);
      }
    }

    if (typeof window === 'undefined') return Promise.reject(error);
    const url = error.config?.url ?? '';
    const isSilentOrBackground = isSilentOrBackgroundApiRequest(url, config);
    const isLoginAttempt =
      url === AUTH_LOGIN_ENDPOINT ||
      url === ADMIN_LOGIN_ENDPOINT ||
      url === '/login';
    if (!error?.response && !isLoginAttempt && !isSilentOrBackground) {
      window.dispatchEvent(
        new CustomEvent('api:offline', {
          detail: {
            baseUrl: getApiBaseUrl(),
            message: getApiErrorMessage(error),
          },
        })
      );
    }
    if (status === 404) {
      const isBackgroundOrCheck =
        Boolean((error.config as any)?.silent404) ||
        Boolean((error.config as any)?.skipGlobalNotFound) ||
        Boolean((error.config as any)?.params?.silent) ||
        url.includes('/badges') ||
        url.includes('/unread') ||
        url.includes('/search') ||
        url.includes('/check') ||
        url.includes('/health');

      if (!isBackgroundOrCheck) {
        window.dispatchEvent(
          new CustomEvent('api:not-found', {
            detail: {
              url,
              message: getApiErrorMessage(error),
            },
          })
        );
      }
    }
    if (status === 403) {
      const responseData = error?.response?.data;
      const errorCode = getApiErrorCode(error);
      const skipGlobalForbidden = Boolean((error.config as { skipGlobalForbidden?: boolean } | undefined)?.skipGlobalForbidden);
      const isOrganizerProfileRequired =
        errorCode === 'ORGANIZER_PROFILE_REQUIRED' ||
        String(responseData?.message ?? '').toLowerCase().includes('organizer profile required');
      const isPlanUpgrade =
        responseData?.code === 'PLAN_UPGRADE_REQUIRED' ||
        errorCode === 'PLAN_UPGRADE_REQUIRED';

      if (skipGlobalForbidden || isOrganizerProfileRequired) {
        // Wizard/finance callers handle this locally — do not toast "Organizer profile required".
      } else if (isPlanUpgrade && typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('plan:upgrade-required', {
            detail: {
              code: 'PLAN_UPGRADE_REQUIRED',
              reason: responseData?.reason,
              currentPlan: responseData?.current_plan ?? responseData?.currentPlan,
              requiredPlan: responseData?.required_plan ?? responseData?.requiredPlan,
              feature: responseData?.feature,
              limits: responseData?.limits,
              message: responseData?.message,
            },
          })
        );
      } else if (typeof window !== 'undefined') {
        const CustomEvent = window.CustomEvent;
        window.dispatchEvent(
          new CustomEvent('api:forbidden', {
            detail: { message: getApiErrorMessage(error) || 'You do not have permission to perform this action.' }
          })
        );
      }
      if (errorCode === 'TWO_FACTOR_SETUP_REQUIRED' || errorCode === 'TWO_FACTOR_REQUIRED') {
        window.dispatchEvent(
          new CustomEvent('admin:two-factor-blocked', {
            detail: {
              code: errorCode,
              message: getApiErrorMessage(error),
              url,
            },
          })
        );
      } else if (isHardUnauthenticatedFailure(403, errorCode)) {
        const currentBrowserPath = typeof window !== 'undefined' ? window.location.pathname : '';
        const isPublicRoute = isPublicBrowserPath(currentBrowserPath) || isPublicAuthRoute(currentBrowserPath);
        if (!sessionExpiryHandled && hasStoredAuthSession()) {
          sessionExpiryHandled = true;
          localStorage.removeItem('auth-storage');
          clearAuthToken();
          if (!isPublicRoute) {
            window.dispatchEvent(new CustomEvent('auth:session-expired', {
              detail: { message: 'Your session expired. Please sign in again.' },
            }));
          }
        }
      }
    }

    if (status === 500 && typeof window !== 'undefined' && !(config as any)?.suppressServerErrorToast && !url.includes('/my/messages')) {
      window.dispatchEvent(
        new CustomEvent('api:server-error', {
          detail: { message: 'An unexpected server error occurred.' }
        })
      );
    }

    const isCredentialEndpoint = /\/auth\/(login|register|firebase|logout|refresh)/.test(url);
    const isAdminLoginEndpoint = /\/admin\/login/.test(url);
    const isAdminInviteEndpoint =
      url === '/admin-invite/accept' || /^\/admin-invite\/[^/]+$/.test(url);
    const failedUrl = String(config?.url ?? '');
    const failedPath = failedUrlNormalized(url) || failedUrlNormalized(failedUrl);
    const isPublic = isPublicApiEndpoint(url) || isPublicApiEndpoint(failedUrl) || isPublicApiEndpoint(failedPath) || isCredentialEndpoint || isAdminInviteEndpoint || isAdminLoginEndpoint;
    const isSessionProbe =
      failedPath === '/auth/me' ||
      failedPath === '/admin/me';
    const isRefreshCall = failedPath === '/auth/refresh' || failedPath === '/admin/refresh';

    if (status === 401) {
      // A failed organizer refresh must not redirect or expire the session
      // while the browser is on a public participant quiz route.
      if (isPublicParticipantBrowserPath()) {
        return Promise.reject(error);
      }

      // Badge polls such as chat/unread must not refresh or clear the session.
      if (isSilentOrBackground) {
        return Promise.reject(error);
      }

      const isAdminRoute = url.startsWith('/admin') || failedPath.startsWith('/admin');
      const isAuthOrRegEndpoint =
        /^\/(auth|api\/auth)\/(refresh|login|register|logout|otp|verify|email)/.test(failedPath) ||
        /^\/(register|api\/register)(\/|$|\?)/.test(failedPath) ||
        /^\/(signup|api\/signup)(\/|$|\?)/.test(failedPath) ||
        /^\/(login|api\/login)(\/|$|\?)/.test(failedPath);

      // Login, refresh, OTP, register, and other public calls must not start another refresh.
      // One failed refresh sets userRefreshUnavailable inside tryUserTokenRefresh.
      const mayRefresh = [url, failedUrl, failedPath].some((candidate) => shouldRefreshSessionAfter401(String(candidate || '')));
      if (isPublic || isSessionProbe || isRefreshCall || isAuthOrRegEndpoint || !mayRefresh || (!isAdminRoute && !hasAuthenticatedUser())) {
        return Promise.reject(error);
      }

      if (config && !config._retry && (config as { __noAuthToken?: boolean }).__noAuthToken) {
        await waitForAuthReady();
        const recovered = restoreMemoryAccessTokenFromCookie() || getMemoryAccessToken();
        if (typeof recovered === 'string' && recovered.trim().length > 20 && !isJwtExpiredString(recovered.trim())) {
          const headers = AxiosHeaders.from(config.headers);
          headers.set('Authorization', `Bearer ${recovered.trim()}`);
          config.headers = headers;
          config._retry = true;
          return api.request(config);
        }
      }

      // Unauthenticated visitor: not an expired session. Do not mark expired or abort requests.
      if (!hasStoredAuthSession()) {
        if (
          isAnonymousSafeApiEndpoint(failedUrl) ||
          isAnonymousSafeApiEndpoint(url) ||
          isPublicApiEndpoint(failedUrl) ||
          isPublicApiEndpoint(url) ||
          failedUrl === '/auth/me' ||
          failedUrl === '/admin/me' ||
          url === '/auth/me' ||
          url === '/admin/me'
        ) {
          return Promise.reject(error);
        }
        if (!isSilentOrBackground) {
          window.dispatchEvent(new CustomEvent('auth:unauthenticated'));
        }
        return Promise.reject(error);
      }

      // Prevent infinite refresh loops: if this request has already been retried once, do not refresh again.
      if (config?._retry) {
        const currentBrowserPath = typeof window !== 'undefined' ? window.location.pathname : '';
        const isPublicRoute = isPublicBrowserPath(currentBrowserPath) || isPublicAuthRoute(currentBrowserPath);
        if (!sessionExpiryHandled && hasStoredAuthSession()) {
          sessionExpiryHandled = true;
          localStorage.removeItem('auth-storage');
          clearAuthToken();
          if (!isPublicRoute) {
            window.dispatchEvent(
              new CustomEvent('auth:session-expired', {
                detail: { message: 'Your session expired. Please sign in again.' },
              })
            );
          }
          setTimeout(() => {
            sessionExpiryHandled = false;
          }, 3000);
        }
        return Promise.reject(error);
      }

      // If token refresh is already in-flight, queue this concurrent request
      if (isRefreshingQueue) {
        return new Promise<string | null>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (token && config.headers) {
              const headers = AxiosHeaders.from(config.headers);
              headers.set('Authorization', `Bearer ${token}`);
              config.headers = headers;
            }
            config._retry = true;
            return api.request(config);
          })
          .catch((err) => Promise.reject(err));
      }

      // First 401: initiate single-flight token refresh
      isRefreshingQueue = true;
      config._retry = true;
      restoreMemoryAccessTokenFromCookie();

      try {
        const refreshed = isAdminRoute
          ? await tryAdminTokenRefresh()
          : await tryUserTokenRefresh();

        if (refreshed) {
          const newToken = getMemoryAccessToken();
          processQueue(null, newToken);
          if (newToken && config.headers) {
            const headers = AxiosHeaders.from(config.headers);
            headers.set('Authorization', `Bearer ${newToken}`);
            config.headers = headers;
          }
          return api.request(config);
        } else {
          processQueue(error, null);
          const currentBrowserPath = typeof window !== 'undefined' ? window.location.pathname : '';
          const isPublicRoute = isPublicBrowserPath(currentBrowserPath) || isPublicAuthRoute(currentBrowserPath);
          if (!sessionExpiryHandled && hasStoredAuthSession()) {
            sessionExpiryHandled = true;
            localStorage.removeItem('auth-storage');
            clearAuthToken();
            if (!isPublicRoute) {
              window.dispatchEvent(
                new CustomEvent('auth:session-expired', {
                  detail: { message: 'Your session expired. Please sign in again.' },
                })
              );
            }
            setTimeout(() => {
              sessionExpiryHandled = false;
            }, 3000);
          }
          return Promise.reject(error);
        }
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        const currentBrowserPath = typeof window !== 'undefined' ? window.location.pathname : '';
        const isPublicRoute = isPublicBrowserPath(currentBrowserPath) || isPublicAuthRoute(currentBrowserPath);
        if (!sessionExpiryHandled && hasStoredAuthSession()) {
          sessionExpiryHandled = true;
          localStorage.removeItem('auth-storage');
          clearAuthToken();
          if (!isPublicRoute) {
            window.dispatchEvent(
              new CustomEvent('auth:session-expired', {
                detail: { message: 'Your session expired. Please sign in again.' },
              })
            );
          }
          setTimeout(() => {
            sessionExpiryHandled = false;
          }, 3000);
        }
        return Promise.reject(refreshErr);
      } finally {
        isRefreshingQueue = false;
      }
    }
    return Promise.reject(error);
  }
);

// Public CMS / pages API
export const pagesApi = {
  get: (slug: string) => api.get(`/pages/${slug}`),
};

export type PrivacyPolicySection = {
  id: number;
  title: string;
  slug: string;
  short_description?: string | null;
  full_content: string;
  meta_title?: string | null;
  meta_description?: string | null;
  display_order: number;
  section_number: number;
  show_in_snapshot: boolean;
  show_in_accordion: boolean;
  show_in_full_text: boolean;
  is_required_section: boolean;
  version: number;
  status: 'draft' | 'published' | 'archived';
  published_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type PrivacyPolicySettings = {
  id?: number;
  hero_title: string;
  hero_subtitle?: string | null;
  last_modified_date?: string | null;
  hero_badges?: string[];
  retention_text?: string | null;
  data_protection_text?: string | null;
  updated_at?: string | null;
};

export const privacyPolicyApi = {
  public: () =>
    api.get<{
      settings: PrivacyPolicySettings;
      snapshot_sections: PrivacyPolicySection[];
      accordion_sections: PrivacyPolicySection[];
      full_text_sections: PrivacyPolicySection[];
    }>('/privacy-policy'),
  settings: () => api.get<{ settings: PrivacyPolicySettings }>('/privacy-policy/settings'),
};

export type QaQuestion = {
  id: string;
  question: string;
  answer?: string | null;
  status: 'pending' | 'answered' | string;
  answered_at?: string | null;
  created_at?: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  responder?: {
    id: string;
    name: string;
    email: string;
  };
};

export const qaApi = {
  submitQuestion: (question: string) => api.post('/qa/questions', { question }),
  myQuestions: (params?: { page?: number }) => api.get('/qa/questions/my', { params }),
};

// Public: organizer reviews (testimonials) for homepage
export const organizerReviewsApi = {
  list: () => api.get<{ data: OrganizerReview[] }>('/organizer-reviews'),
};

export const organizerApi = {
  submitTestimonial: (data: { rating: number; review_text: string; event_name?: string }) =>
    api.post('/organizer-reviews/submit', data),
  kycList: (params?: Record<string, unknown>) => api.get('/organizer/kyc', { params }),
  kycShow: (eventId: string) => api.get(`/organizer/kyc/events/${eventId}`),
  kycStoreAadhaar: (eventId: string, payload: { aadhaar_number: string }) =>
    api.post(`/organizer/kyc/events/${eventId}/aadhaar`, payload),
  kycRequestOtp: (eventId: string) => api.post(`/organizer/kyc/events/${eventId}/aadhaar/request-otp`),
  kycVerifyOtp: (eventId: string, payload: { otp: string }) =>
    api.post(`/organizer/kyc/events/${eventId}/aadhaar/verify-otp`, payload),
  kycUploadDocument: (eventId: string, form: FormData) =>
    api.post(`/organizer/kyc/events/${eventId}/documents`, form),
  kycSubmit: (eventId: string) => api.post(`/organizer/kyc/events/${eventId}/submit`),
  identityVerificationShow: () => api.get('/organizer/identity-verification'),
  identityVerificationSubmit: (form: FormData) => api.post('/organizer/identity-verification', form),
  identityVerificationSaveDraft: (form: FormData) => api.post('/organizer/identity-verification/draft', form),
  settlementAccountsList: (params?: Record<string, unknown>) => api.get('/organizer/settlement-accounts', { params }),
  settlementAccountShow: (eventId: string) => api.get(`/organizer/settlement-accounts/events/${eventId}`),
  settlementAccountSubmit: (eventId: string, payload: Record<string, unknown>) =>
    api.post(`/organizer/settlement-accounts/events/${eventId}`, payload),
  settlementAccountUpdate: (eventId: string, payload: Record<string, unknown>) =>
    api.put(`/organizer/settlement-accounts/events/${eventId}`, payload),
  settlementAccountConfigurePayout: (eventId: string, payload: Record<string, unknown>) =>
    api.post(`/organizer/settlement-accounts/events/${eventId}/payout`, payload),
  settlementAccountReplacePayout: (eventId: string, payload: Record<string, unknown>) =>
    api.post(`/organizer/settlement-accounts/events/${eventId}/payout/replace`, payload),
  settlementAccountUploadDocument: (eventId: string, form: FormData) =>
    api.post(`/organizer/settlement-accounts/events/${eventId}/documents`, form),
  settlementAccountChangeRequest: (eventId: string, payload: Record<string, unknown> | FormData) =>
    api.post(`/organizer/settlement-accounts/events/${eventId}/change-requests`, payload),
  settlementAccountReadiness: (eventId: string) =>
    api.get(`/organizer/settlement-accounts/events/${eventId}/readiness`),
  payoutAccounts: () => api.get('/organizer/payout-accounts'),
  payoutAccountCreate: (payload: Record<string, unknown>) =>
    api.post('/organizer/payout-accounts', payload),
  payoutAccountSetDefault: (accountId: string) =>
    api.post(`/organizer/payout-accounts/${accountId}/default`),
  payoutAccountContinueKyc: (accountId: string, payload: Record<string, unknown>) =>
    api.post(`/organizer/payout-accounts/${accountId}/continue-kyc`, payload),
  payoutAccountArchive: (accountId: string) =>
    api.delete(`/organizer/payout-accounts/${accountId}`),
  settlementAccountAssignPayout: (eventId: string, payoutAccountId: string) =>
    api.put(`/organizer/settlement-accounts/events/${eventId}/payout-assignment`, {
      payout_account_id: payoutAccountId,
    }),
};

export const organizerGovernanceApi = {
  searchOrganizations: (params?: Record<string, unknown>) => api.get('/organizer-access/organizations', { params }),
  organizationRoles: (organizationId: string) => api.get(`/organizer-access/organizations/${organizationId}/roles`),
  myRequest: () => api.get('/organizer-access/requests/me'),
  submitRequest: (data: Record<string, unknown>) => api.post('/organizer-access/requests', data),
};

// Organization member invitation link (token accept flow)
export const organizationInvitationApi = {
  show: (token: string) => api.get(`/invitations/organization/${token}`),
  accept: (token: string) => api.post('/invitations/organization/accept', { token }),
};

export type EventSettlementFinancialRecord = {
  id: string;
  event_id: string;
  organizer_id: string;
  organization_id: string | null;
  gross_revenue: number;
  platform_fee: number;
  gst_amount: number;
  refund_hold_amount: number;
  net_organizer_balance: number;
  settled_amount: number;
  pending_settlement_amount: number;
  held_settlement_amount: number;
  available_balance: number;
  currency: string;
  last_synced_at: string | null;
  settlement_eligibility?: Record<string, unknown>;
  minimum_threshold?: number;
};

export type SettlementRequestRow = {
  id: string;
  event_id: string;
  organizer_id: string;
  organization_id: string | null;
  status: string;
  freeze_reason?: string | null;
  gross_revenue: number;
  platform_fee: number;
  gst_amount: number;
  refund_hold_amount: number;
  net_amount: number;
  requested_amount: number;
  currency: string;
  rejection_reason?: string | null;
  submitted_at?: string | null;
  approved_at?: string | null;
  paid_at?: string | null;
  frozen_at?: string | null;
  event?: { id: string; title: string; slug: string; status: string } | null;
};

export const organizerSettlementApi = {
  dashboard: (params?: Record<string, unknown>) => api.get('/organizer/settlements/dashboard', { params }),
  balances: (params?: Record<string, unknown>) => api.get('/organizer/settlements/balances', { params }),
  history: (params?: Record<string, unknown>) =>
    api.get<{
      success: boolean;
      data: { data: SettlementRequestRow[]; current_page: number; last_page: number; per_page: number; total: number };
    }>('/organizer/settlements/history', { params }),
  eventFinancials: (eventId: string) =>
    api.get<{ success: boolean; data: EventSettlementFinancialRecord }>(`/organizer/settlements/events/${eventId}/financials`),
  submitRequest: (payload: Record<string, unknown>) => api.post('/organizer/settlements/requests', payload),
  showRequest: (id: string) => api.get(`/organizer/settlements/requests/${id}`),
  cancelRequest: (id: string, payload?: { notes?: string }) => api.post(`/organizer/settlements/requests/${id}/cancel`, payload),
};

export type RefundRequestRow = {
  id: string;
  registration_id: string;
  event_id: string;
  organizer_id: string;
  status: string;
  refund_type: string;
  policy: string;
  amount: number;
  reason?: string | null;
  description?: string | null;
  rejection_reason?: string | null;
  submitted_at?: string | null;
  approved_at?: string | null;
  processed_at?: string | null;
  event?: { id: string; title: string; slug: string; status: string } | null;
  participant?: { id: string; name: string; email: string } | null;
  registration?: { id: string; registration_number: string; total_amount: number } | null;
};

export const organizerRefundApi = {
  dashboard: (params?: Record<string, unknown>) => api.get('/organizer/refunds/dashboard', { params }),
  history: (params?: Record<string, unknown>) =>
    api.get<{
      success: boolean;
      data: { data: RefundRequestRow[]; current_page: number; last_page: number; per_page: number; total: number };
    }>('/organizer/refunds/history', { params }),
  eligibility: (registrationId: string, params?: Record<string, unknown>) =>
    api.get(`/organizer/refunds/eligibility/${registrationId}`, { params }),
  submitRequest: (payload: Record<string, unknown>) => api.post('/organizer/refunds/requests', payload),
  showRequest: (id: string) => api.get(`/organizer/refunds/requests/${id}`),
  cancelRequest: (id: string, payload?: { notes?: string }) => api.post(`/organizer/refunds/requests/${id}/cancel`, payload),
};

export type OrganizerReview = {
  id: string;
  organizer_name: string;
  organizer_company?: string | null;
  organizer_avatar?: string | null;
  organizer_avatar_url?: string | null;
  rating: number;
  review_text: string;
  event_name?: string | null;
  featured?: boolean;
  status?: 'pending' | 'approved' | 'rejected' | string;
  created_at?: string;
};

export type PlatformResource = {
  id: string;
  type: 'success_story' | 'how_to' | 'article' | 'qa' | string;
  media_type: 'video' | 'article' | 'text' | string;
  title: string;
  slug: string;
  description?: string | null;
  content?: string | null;
  media_url?: string | null;
  thumbnail_url?: string | null;
  author?: string | null;
  is_published: boolean;
  display_order: number;
  metadata?: any;
  created_at?: string;
  updated_at?: string;
};

export type PublicSubscriptionPlan = {
  id: string;
  key?: string;
  name: string;
  slug?: string | null;
  description?: string | null;
  price_monthly?: number | string | null;
  price_yearly?: number | string | null;
  commission_percent?: number | string | null;
  gst_percent?: number | string | null;
  platform_fee_percent?: number | string | null;
  gateway_fee_percent?: number | string | null;
  is_active?: boolean;
  is_default?: boolean;
  is_enterprise?: boolean;
  is_popular?: boolean;
  is_recommended?: boolean;
  sort_order?: number | null;
  display_order?: number | null;
  features?: Array<{ id?: string; name?: string; slug?: string; key?: string; category?: string | null } | string>;
  payment_methods?: string[];
  fees?: {
    platform_fee_percent?: number;
    gateway_fee_percent?: number;
    gst_percent?: number;
    gst_applicable?: boolean;
  };
  limits?: {
    participants_max?: number | null;
    campaigns_max?: number | null;
    team_members_max?: number | null;
    tickets_max?: number | null;
    events_max?: number | null;
  };
};

// Pricing & plans (dynamic from admin)
export const pricingApi = {
  // Legacy pricing plans
  plans: (options?: { fresh?: boolean }) =>
    api.get('/plans', options?.fresh ? ({ params: { fresh: 1, _ts: Date.now() }, skipInFlightDedup: true } as ApiGetConfig) : undefined),
  // Detailed subscription plans (used for selection & checkout)
  subscriptionPlans: (options?: { fresh?: boolean }) =>
    api.get<{ data: PublicSubscriptionPlan[] }>(
      '/subscription-plans',
      options?.fresh ? ({ params: { fresh: 1, _ts: Date.now() }, skipInFlightDedup: true } as ApiGetConfig) : undefined
    ),
  // Public plans endpoint
  publicPlans: (options?: { fresh?: boolean }) =>
    api.get<{ data: PublicSubscriptionPlan[] }>(
      '/plans/public',
      options?.fresh ? ({ params: { fresh: 1, _ts: Date.now() }, skipInFlightDedup: true } as ApiGetConfig) : undefined
    ),
};

// Subscription (organizer: current, usage, subscribe, upgrade, cancel)
export const subscriptionApi = {
  current: () => api.get('/organizer/subscription/current', { cacheTtlMs: 60000, suppressServerErrorToast: true } as any),
  usage: () => api.get('/organizer/subscription/usage'),
  history: () => api.get('/organizer/subscription/history'),
  subscribe: (data: { plan_id: string; billing_cycle: 'monthly' | 'yearly'; payment_provider?: string; payment_reference?: string }) =>
    api.post('/organizer/subscription/subscribe', data),
  upgrade: (data: { plan_key?: string; plan_id?: string }) => api.post('/organizer/subscription/upgrade', data),
  downgrade: (data: { plan_key?: string; plan_id?: string }) => api.post('/organizer/subscription/downgrade', data),
  pause: () => api.post('/organizer/subscription/pause'),
  renew: () => api.post('/organizer/subscription/renew'),
  cancel: () => api.post('/organizer/subscription/cancel'),
  eventPlan: (eventId: string) =>
    api.get('/organizer/subscription/event/plan', { params: { event_id: eventId } }),
  selectEventPlan: (data: { event_id: string; plan_id: string }) =>
    api.post('/organizer/subscription/event/select-plan', data),
};

export const organizerAccessApi = {
  effective: () => api.get('/organizer/access/effective'),
  comparePlans: (params: { plan_id?: string; plan_key?: string }) =>
    api.get('/organizer/access/compare-plans', { params }),
  subscriptionHistory: () => api.get('/organizer/access/subscription-history'),
};

export const customPlanApi = {
  submitRequest: (data: {
    event_types: string[];
    expected_participants?: number | null;
    estimated_events_per_year?: number | null;
    requirements: string;
    contact_name: string;
    contact_email: string;
    contact_phone?: string;
    organization_name?: string;
    website_url?: string;
  }) => api.post('/custom-plan-requests', data),

  admin: {
    listRequests: (
      params?: { status?: string; search?: string; page?: number; per_page?: number },
      config?: import('axios').AxiosRequestConfig
    ) =>
      api.get('/admin/custom-plan-requests', { params, ...config }),
    getRequest: (id: string | number) =>
      api.get(`/admin/custom-plan-requests/${id}`),
    updateStatus: (id: string | number, data: { status: string; admin_notes?: string }) =>
      api.put(`/admin/custom-plan-requests/${id}`, data),
    applyPlan: (
      organizerId: string | number,
      data: {
        commission_rate?: number;
        max_participants_per_activity?: number | null;
        allowed_activity_types?: string[];
        features?: string[];
        notes?: string;
        custom_plan_request_id?: string | number;
      }
    ) => api.post(`/admin/organizers/${organizerId}/apply-custom-plan`, data),
    changePlan: (
      organizerId: string | number,
      data: {
        plan_key: string;
        reason?: string;
      }
    ) => api.post(`/admin/organizers/${organizerId}/change-plan`, data),
    getUsage: (organizerId: string | number) =>
      api.get(`/admin/organizers/${organizerId}/plan-usage`),
  },
};

export const eventInvitationApi = {
  preview: (token: string) => api.get(`/invitations/event/${token}`),
  accept: (token: string) => api.post('/invitations/event/accept', { token }),
  decline: (token: string) => api.post('/invitations/event/decline', { token }),
};

// Unified session-based auth API
export const authApi = {
  registerByRole: async (
    role: 'participant' | 'organizer' | 'organization_admin' | 'jury' | 'team',
    data: Record<string, unknown>
  ) => {
    const endpointMap: Record<string, string> = {
      participant: '/auth/register/participant',
      organizer: '/auth/register/organizer',
      organization_admin: '/auth/register/organization',
      jury: '/auth/register/jury',
      team: '/auth/register/team',
    };
    const res = await api.post(endpointMap[role], data);
    persistAuthToken(res.data);
    return res;
  },
  register: async (data: { name: string; email: string; password: string; phone?: string; otp?: string; firebase_token?: string }) => {
    const payload = { ...data, role: 'participant' };
    const res = await api.post('/auth/register/participant', payload);
    persistAuthToken(res.data);
    return res;
  },
  login: (data: { email: string; password: string }, options?: { adminLogin?: boolean }) =>
    loginApi.login(data, options),
  loginScanner: async (data: { event_code: string; pin?: string }) => {
    const res = await api.post('/auth/login/scanner', data);
    persistAuthToken(res.data);
    return res;
  },
  firebaseLogin: async (firebaseToken: string, profile?: { name?: string; email?: string }) => {
    const res = await api.post('/auth/firebase', {
      firebase_token: firebaseToken,
      name: profile?.name,
      email: profile?.email,
    });
    persistAuthToken(res.data);
    return res;
  },
  setupPassword: async (data: any) => {
    const res = await api.post('/auth/setup-password', data);
    persistAuthToken(res.data);
    return res;
  },
  forgotPassword: (email: string) =>
    api.post('/auth/password/forgot', { email: String(email || '').trim().toLowerCase() }),
  resetPassword: (data: {
    token: string;
    email: string;
    password: string;
    password_confirmation: string;
  }) => api.post('/auth/password/reset', data),
  resetTwoFactorRequest: (email: string) =>
    api.post('/auth/2fa/reset/request', { email: String(email || '').trim().toLowerCase() }),
  resetTwoFactorConfirm: (data: { token: string; password: string }) =>
    api.post('/auth/2fa/reset/confirm', data),
  getTwoFactorSetup: (setupToken: string) =>
    api.get('/auth/2fa/setup', {
      headers: { Authorization: `Bearer ${setupToken}` },
    }),
  verifyTwoFactorSetup: (code: string, setupToken: string) =>
    api.post(
      '/auth/2fa/setup/verify',
      { code: String(code || '').trim() },
      { headers: { Authorization: `Bearer ${setupToken}` } }
    ),
  appleLogin: async (data: any) => {
    const res = await api.post('/auth/apple', data);
    persistAuthToken(res.data);
    return res;
  },
  githubLogin: async (data: any) => {
    const res = await api.post('/auth/github', data);
    persistAuthToken(res.data);
    return res;
  },
  sendOtp: (email: string) => {
    const value = String(email || '').trim();
    return api.post('/auth/otp/send', {
      identifier: value,
      type: 'email',
      email: value,
    });
  },
  verifyOtp: (email: string, otp: string) => {
    const value = String(email || '').trim();
    return api.post('/auth/otp/verify', {
      identifier: value,
      type: 'email',
      email: value,
      otp,
    });
  },
  registerWithOtp: (email: string, name: string, password: string, otp: string, userType?: string) => {
    const emailValue = String(email || '').trim();
    return api.post('/auth/register-with-otp', {
      email: emailValue,
      name,
      password,
      otp,
      user_type: userType || 'participant',
    }).then((response) => {
      if (response.data) {
        persistAuthToken(response.data);
        const token = extractAuthToken(response.data);
        if (token) {
          setRegistrationToken(token);
        }
      }
      return response;
    });
  },
  me: (config?: AxiosRequestConfig) =>
    api.get('/auth/me', { timeout: AUTH_ME_TIMEOUT_MS, ...config }).then((res) => {
      traceMeResponse('/auth/me', res.status, res.data);
      return res;
    }),
  profile: () => api.get('/auth/profile'),
  updateProfile: (data: any) => api.put('/auth/profile', data),
  changePassword: (data: {
    current_password?: string;
    password: string;
    password_confirmation: string;
  }) => api.put('/user/change-password', data),
  deleteAccount: () => api.delete('/user/account'),
  verifyEmail: (token: string) => api.post('/auth/email/verify', { token }),
  logout: async () => {
    try {
      return await api.post('/auth/logout');
    } finally {
      if (typeof window !== 'undefined') {
        clearAuthToken();
      }
    }
  },
  becomeOrganizer: async (data: any) => {
    try {
      return await api.post('/user/become-organizer', data);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return api.post('/auth/become-organizer', data);
      }
      throw error;
    }
  },
  getRole: () => api.get('/auth/me'),
};

export type UserWorkspace = {
  type: 'organization' | 'personal_organizer';
  id: string;
  label: string;
  organization_id?: string | null;
};

export const workspacesApi = {
  list: () =>
    api.get<{
      success: boolean;
      data: { workspaces: UserWorkspace[]; can_create_events: boolean };
    }>('/user/workspaces'),
};

export const registrationApi = {
  registerOrganization: (formData: FormData) => {
    const regToken = getRegistrationToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (regToken) {
      headers['Authorization'] = `Bearer ${regToken}`;
      headers['X-Registration-Token'] = regToken;
    }
    return api.post('/register/organization', formData, {
      headers,
      timeout: 120000,
      suppressServerErrorToast: true,
    });
  },
  registerIndividual: (formData: FormData) => {
    const regToken = getRegistrationToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (regToken) {
      headers['Authorization'] = `Bearer ${regToken}`;
      headers['X-Registration-Token'] = regToken;
    }
    return api.post('/register/individual', formData, {
      headers,
      timeout: 120000,
      suppressServerErrorToast: true,
    });
  },
};

export type EventPayoutReadinessData = {
  is_paid_event: boolean;
  payout_required: boolean;
  payout_ready: boolean;
  destination_type: 'temporary_route' | 'organizer_payout' | 'none';
  status: 'not_required' | 'configured' | 'missing';
  account_id?: string | null;
  account_id_masked?: string | null;
  message?: string;
};

/**
 * Event-scoped finance settings: fee bearers, payment methods, tax display fields, and the
 * organizer's payout bank details. Distinct from `eventFinancialSettingsApi` (GST registration,
 * refund policy, settlement preference, platform-controlled fee percentages).
 */
export type EventFinanceSettingsData = {
  event_id?: string;
  bank: {
    bank_name: string | null;
    bank_account_name: string | null;
    bank_account_number: string | null;
    bank_account_type: string | null;
    bank_ifsc: string | null;
    bank_address: string | null;
  };
  tax: {
    tax_enabled: boolean;
    tax_name: string;
    tax_id: string | null;
    tax_percent: number;
    tax_inclusive: boolean;
    show_tax_on_invoice?: boolean;
    business_details?: unknown;
    source?: string;
  };
  processing_fee_bearer: 'organizer' | 'attendee';
  platform_fee_bearer: 'organizer' | 'attendee';
  platform_commission_percent: number;
  payment_methods: string[];
  available_payment_methods: Array<{ id: string; label: string; description?: string | null }>;
  payment_method_options: Array<{
    id: string;
    label: string;
    fee_percent: number;
    is_active: boolean;
    sort_order: number;
    description: string | null;
  }>;
  payment_method_fees: Record<string, number>;
  payout_readiness?: EventPayoutReadinessData;
};

// Events API
export const eventsApi = {
  list: (params?: any) => api.get('/events', { params }),
  // CONNECTED TO BACKEND: POST /events/{slug}/view — track a page view for analytics
  recordView: (slug: string) => api.post(`/events/${slug}/view`),
  search: (query: string) => api.get('/events/search', { params: { q: query } }),
  get: (slug: string, params?: { access?: string; verification?: string; email?: string }) =>
    api.get(`/events/${slug}`, { params }),
  getRecommended: (params?: { location?: string }) =>
    api.get<{ success: boolean; data: any[] }>('/events/recommended', { params }),
  getTickets: (slug: string) => api.get(`/events/${slug}/tickets`),
  getTimeline: (slug: string) => api.get(`/events/${slug}/timeline`),
  getSchedule: (slug: string) => api.get(`/events/${slug}/schedule`),
  getSponsors: (slug: string) => api.get(`/events/${slug}/sponsors`),
  getJudges: (slug: string) => api.get(`/events/${slug}/judges`),
  getFeedback: (idOrSlug: string) => api.get(`/events/${idOrSlug}/feedback`),
  getAnnouncements: (slug: string) => api.get<{ success: boolean; data: { id: string; title: string; message: string; created_at: string; is_pinned?: boolean }[] }>(`/events/${slug}/announcements`),
  getChat: (idOrSlug: string, params?: { channel?: string; team_id?: string; q?: string }) =>
    api.get<{ success: boolean; data: WorkspaceChatMessage[]; abilities?: WorkspaceChatAbilities; unread_count?: number }>(`/events/${idOrSlug}/chat`, { params, skipInFlightDedup: true }),
  getChatUnread: (idOrSlug: string) =>
    api.get<{ success: boolean; data: { unread_count: number } }>(`/events/${idOrSlug}/chat/unread`, { silent: true }),
  markChatRead: (idOrSlug: string, data?: { message_id?: string; channel?: string }) =>
    api.post(`/events/${idOrSlug}/chat/read`, data ?? {}),
  searchChat: (idOrSlug: string, q: string) =>
    api.get<{ success: boolean; data: WorkspaceChatMessage[] }>(`/events/${idOrSlug}/chat/search`, { params: { q } }),
  sendChat: (idOrSlug: string, data: WorkspaceChatSendPayload) =>
    api.post(`/events/${idOrSlug}/chat`, data),
  pinChatMessage: (idOrSlug: string, messageId: string) =>
    api.post(`/events/${idOrSlug}/chat/${messageId}/pin`),
  unpinChatMessage: (idOrSlug: string, messageId: string) =>
    api.delete(`/events/${idOrSlug}/chat/${messageId}/pin`),
  uploadEventChatAttachment: (idOrSlug: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ success: boolean; data: WorkspaceChatAttachment }>(`/events/${idOrSlug}/chat/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getOrganizerConversation: (idOrSlug: string) =>
    api.get(`/events/${idOrSlug}/organizer-conversation`, { skipInFlightDedup: true }),
  getOrganizerConversationMessages: (idOrSlug: string) =>
    api.get<{ success: boolean; data: OrganizerDirectMessage[]; unread_count?: number }>(
      `/events/${idOrSlug}/organizer-conversation/messages`,
      { skipInFlightDedup: true },
    ),
  getOrganizerConversationUnread: (idOrSlug: string) =>
    api.get<{ success: boolean; data: { unread_count: number } }>(
      `/events/${idOrSlug}/organizer-conversation/unread`,
    ),
  markOrganizerConversationRead: (idOrSlug: string, data?: { message_id?: string }) =>
    api.post(`/events/${idOrSlug}/organizer-conversation/read`, data ?? {}),
  sendOrganizerMessage: (idOrSlug: string, payload: string | { message?: string; attachments?: any[]; poll?: any }) =>
    api.post(
      `/events/${idOrSlug}/organizer-conversation/messages`,
      typeof payload === 'string' ? { message: payload } : payload,
    ),
  getWaitlistStatus: (idOrSlug: string) =>
    api.get<{
      success: boolean;
      data: {
        on_waitlist: boolean;
        sold_out: boolean;
        waitlist_status?: string | null;
        offer_expires_at?: string | null;
      };
    }>(`/events/${idOrSlug}/waitlist/status`),
  getWaitlistOffer: (token: string) =>
    api.get<{
      success: boolean;
      data: {
        event?: { id: string; title: string; slug: string };
        ticket?: { id: string; name: string; price: number; currency: string };
        offer_expires_at?: string;
        checkout_url?: string;
      };
    }>(`/waitlist/offers/${encodeURIComponent(token)}`),
  joinWaitlist: (idOrSlug: string) => api.post(`/events/${idOrSlug}/waitlist`),
  submitFeedback: (idOrSlug: string, data: { rating: number; comment?: string }) => api.post(`/events/${idOrSlug}/feedback`, data),
  getEvaluationLeaderboard: (slug: string) => api.get(`/events/${slug}/evaluation-leaderboard`),

  // Organizer Dashboard
  getDashboardStats: () => api.get('/organizer/dashboard'),

  // Organizer Events
  myEvents: (params?: any) => api.get('/organizer/events', { params }),
  showEvent: (id: string) => api.get(`/organizer/events/${id}`),
  create: (data: any) => api.post('/organizer/events', data),
  createWizard: (payload: {
    step_1: Record<string, any>;
    step_2?: Array<Record<string, any>>;
    step_3?: Array<Record<string, any>>;
    step_4?: Record<string, boolean>;
    step_5?: Array<Record<string, any>>;
    step_6?: Record<string, any>;
    step_7?: Record<string, any>;
    step_8?: boolean;
    publish?: boolean;
  }) => api.post('/organizer/events/wizard', payload),
  update: (id: string, data: any) => api.put(`/organizer/events/${id}`, data),
  delete: (id: string) => api.delete(`/organizer/events/${id}`),
  /** @deprecated Prefer eventWorkflowApi.publish — both target POST /events/{id}/publish */
  publish: (id: string) => api.post(`/events/${id}/publish`),
  createTicket: (eventId: string, data: any) => api.post(`/organizer/events/${eventId}/tickets`, data),

        // Registrations
  getRegistrations: (id: string, params?: any) => api.get(`/organizer/events/${id}/registrations`, { params }),
  exportRegistrations: (id: string) => api.get(`/organizer/events/${id}/registrations/export`),
  // Participants & approval
  getParticipants: (id: string, params?: any, config?: Record<string, unknown>) =>
    api.get(`/organizer/events/${id}/participants`, { params, ...config }),
  exportParticipants: (id: string, params?: Record<string, string>) =>
    api.get(`/organizer/events/${id}/participants/export`, { params, responseType: 'blob' }),
  getCampaignAudienceCounts: (id: string) =>
    api.get(`/organizer/events/${id}/campaign-audience-counts`),
  sendFilteredMessage: (id: string, data: Record<string, unknown>) =>
    api.post(`/organizer/events/${id}/communications/send-filtered`, data),
  sendSessionUpdate: (id: string, data: Record<string, unknown>) =>
    api.post(`/organizer/events/${id}/communications/session-update`, data),
  getEventFinanceSettings: (id: string) =>
    api.get<{ success: boolean; data: EventFinanceSettingsData }>(`/organizer/events/${id}/finance-settings`, {
      skipGlobalForbidden: true,
    } as Record<string, unknown>),
  updateEventFinanceSettings: (id: string, data: Record<string, unknown>) =>
    api.put<{ success: boolean; data: EventFinanceSettingsData }>(`/organizer/events/${id}/finance-settings`, data, {
      skipGlobalForbidden: true,
    } as Record<string, unknown>),
  getEventPayoutReadiness: (id: string) =>
    api.get<{ success: boolean; data: EventPayoutReadinessData }>(`/organizer/events/${id}/payout-readiness`, {
      skipGlobalForbidden: true,
    } as Record<string, unknown>),
  getTemporaryRouteAccount: (id: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/organizer/events/${id}/temporary-route-account`, {
      skipGlobalForbidden: true,
    } as Record<string, unknown>),
  verifyTemporaryRouteAccount: (id: string, linkedAccountId: string) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>(
      `/organizer/events/${id}/temporary-route-account/verify`,
      { linked_account_id: linkedAccountId },
      { skipGlobalForbidden: true } as Record<string, unknown>,
    ),
  saveTemporaryRouteAccount: (id: string, linkedAccountId: string) =>
    api.put<{ success: boolean; data: Record<string, unknown> }>(
      `/organizer/events/${id}/temporary-route-account`,
      { linked_account_id: linkedAccountId },
      { skipGlobalForbidden: true } as Record<string, unknown>,
    ),
  getAttendanceSessions: (eventId: string) => api.get(`/organizer/events/${eventId}/attendance-sessions`),
  createAttendanceSession: (eventId: string, data: { name: string; notes?: string; expires_at?: string }) =>
    api.post(`/organizer/events/${eventId}/attendance-sessions`, data),
  updateAttendanceSession: (eventId: string, sessionId: string, data: Record<string, unknown>) =>
    api.put(`/organizer/events/${eventId}/attendance-sessions/${sessionId}`, data),
  closeAttendanceSession: (eventId: string, sessionId: string) =>
    api.post(`/organizer/events/${eventId}/attendance-sessions/${sessionId}/close`, {}),
  getWaitingList: (id: string) => api.get(`/organizer/events/${id}/waiting-list`),
  getApprovalRequests: (id: string) => api.get(`/organizer/events/${id}/approval-requests`),
  getParticipant: (eventId: string, registrationId: string) =>
    api.get(`/organizer/events/${eventId}/participants/${registrationId}`),
  // Event teams (participant teams per event)
  getEventTeams: (eventId: string, params?: any, config?: Record<string, unknown>) =>
    api.get(`/organizer/events/${eventId}/teams`, { params, ...config }),
  exportTeams: (eventId: string, params?: Record<string, string>) =>
    api.get(`/organizer/events/${eventId}/teams/export`, { params, responseType: 'blob' }),
  exportTeamMembers: (eventId: string, params?: Record<string, string>) =>
    api.get(`/organizer/events/${eventId}/teams/export-members`, { params, responseType: 'blob' }),
  approveRegistration: (eventId: string, registrationId: string) => api.put(`/organizer/events/${eventId}/registrations/${registrationId}/approve`),
  rejectRegistration: (eventId: string, registrationId: string) => api.put(`/organizer/events/${eventId}/registrations/${registrationId}/reject`),
  bulkApproveRegistrations: (eventId: string, registrationIds: string[]) =>
    api.post(`/organizer/events/${eventId}/registrations/bulk-approve`, { registration_ids: registrationIds }),
  bulkRejectRegistrations: (eventId: string, registrationIds: string[]) =>
    api.post(`/organizer/events/${eventId}/registrations/bulk-reject`, { registration_ids: registrationIds }),
  // Analytics & Unified Stats
  getEventStats: (eventId: string) => api.get(`/organizer/events/${eventId}/stats`),
  getEventAnalytics: (eventId: string) => api.get(`/organizer/events/${eventId}/analytics`),
  getEventAnalyticsFunnel: (eventId: string) => api.get(`/organizer/events/${eventId}/analytics/funnel`),
  getEventAnalyticsEngagement: (eventId: string) => api.get(`/organizer/events/${eventId}/analytics/engagement`),
  getEventRevenue: (eventId: string) => api.get(`/organizer/events/${eventId}/revenue`),
  // Collaborators
  getCollaborators: (eventId: string) => api.get(`/organizer/events/${eventId}/collaborators`),
  addCollaborator: (eventId: string, data: { user_id: string; permissions: string[] }) => api.post(`/organizer/events/${eventId}/collaborators`, data),
  removeCollaborator: (eventId: string, userId: string) => api.delete(`/organizer/events/${eventId}/collaborators/${userId}`),
  // Hidden ticket URL
  getHiddenTicketUrl: (eventId: string, ticketId: string) => api.get(`/organizer/events/${eventId}/tickets/${ticketId}/hidden-url`),
    liveMetrics: (eventId: string) =>
    api.get<{ success: boolean; data: { registrations_count: number; checked_in_count: number; teams_count: number; submissions_count: number } }>(
      `/organizer/events/${eventId}/live-metrics`
    ),
};

export const organizerInboxApi = {
  list: (eventId: string, params?: { search?: string; type?: string }) =>
    api.get<{ success: boolean; data: OrganizerInboxConversation[]; meta?: { unread_total?: number } }>(
      `/organizer/events/${eventId}/inbox`,
      { params, skipInFlightDedup: true },
    ),
  unread: (eventId: string) =>
    api.get<{ success: boolean; data: { unread_count: number } }>(
      `/organizer/events/${eventId}/inbox/unread`,
      { skipInFlightDedup: true },
    ),
  contactables: (eventId: string, params?: { search?: string }) =>
    api.get<{ success: boolean; data: OrganizerInboxContactables }>(
      `/organizer/events/${eventId}/inbox/contactables`,
      { params, skipInFlightDedup: true },
    ),
  start: (
    eventId: string,
    data: { type: string; user_id?: string; team_id?: string },
  ) =>
    api.post<{ success: boolean; data: OrganizerInboxConversation }>(
      `/organizer/events/${eventId}/inbox/start`,
      data,
    ),
  messages: (eventId: string, conversationId: string, params?: { before_id?: string; limit?: number }) =>
    api.get<{
      success: boolean;
      data: OrganizerDirectMessage[];
      unread_count?: number;
      meta?: { has_more?: boolean };
    }>(`/organizer/events/${eventId}/inbox/${conversationId}/messages`, {
      params,
      skipInFlightDedup: true,
    }),
  markRead: (eventId: string, conversationId: string, data?: { message_id?: string }) =>
    api.post(`/organizer/events/${eventId}/inbox/${conversationId}/read`, data ?? {}),
  send: (
    eventId: string,
    conversationId: string,
    payload: string | { message?: string; attachments?: any[]; poll?: any },
  ) =>
    api.post(
      `/organizer/events/${eventId}/inbox/${conversationId}/messages`,
      typeof payload === 'string' ? { message: payload } : payload,
    ),
  rename: (eventId: string, conversationId: string, name: string) =>
    api.put<{ success: boolean; data: OrganizerInboxConversation }>(
      `/organizer/events/${eventId}/inbox/${conversationId}`,
      { name },
    ),
  delete: (eventId: string, conversationId: string) =>
    api.delete<{ success: boolean }>(`/organizer/events/${eventId}/inbox/${conversationId}`),
};

export type OrganizerDirectMessage = {
  id: string;
  message: string;
  created_at: string;
  sender?: { id: string; name: string; avatar?: string | null };
  sender_role?: 'organizer' | 'participant' | 'jury' | 'volunteer' | 'organization' | 'team';
  sender_label?: string;
};

export type OrganizerInboxConversation = {
  id: string;
  name: string;
  type?: string;
  account_type?: 'participant' | 'team' | 'jury' | 'volunteer' | 'organization' | string;
  team_id?: string | null;
  event_id?: string;
  updated_at?: string;
  unread_count?: number;
  participant?: { id?: string; name?: string | null; avatar?: string | null } | null;
  event?: { id?: string; title?: string; slug?: string } | null;
  team?: { id?: string; name?: string; code?: string } | null;
  latest_message?: OrganizerDirectMessage | null;
  members?: Array<{ role?: string; user?: { id: string; name: string; avatar?: string | null } }>;
};

export type OrganizerInboxContactables = {
  participants: Array<{ id: string; name?: string | null; avatar?: string | null; email?: string | null; account_type: string }>;
  teams: Array<{ id: string; name?: string | null; code?: string | null; members_count?: number; account_type: string }>;
  jury: Array<{ id: string; name?: string | null; avatar?: string | null; email?: string | null; account_type: string }>;
  volunteers: Array<{ id: string; name?: string | null; avatar?: string | null; email?: string | null; account_type: string }>;
  organization: {
    id: string;
    organization_id?: string;
    name?: string | null;
    avatar?: string | null;
    email?: string | null;
    account_type: string;
  } | null;
};

export const mediaUploadApi = {
  upload: (file: File, options?: { context?: string; parent_id?: string; max_size_mb?: number; allowed_types?: string[] }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.context) formData.append('context', options.context);
    if (options?.parent_id) formData.append('parent_id', options.parent_id);
    if (options?.max_size_mb) formData.append('max_size_mb', String(options.max_size_mb));
    if (options?.allowed_types) formData.append('allowed_types', options.allowed_types.join(','));

    return api.post<{
      success: boolean;
      data: {
        storage_key: string;
        original_filename: string;
        content_type: string;
        size_bytes: number;
        url: string;
        thumbnail_url?: string;
      };
    }>('/uploads/process', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const myMessagesApi = {
  list: (params?: { search?: string; type?: string; event_id?: string }, config?: any) =>
    api.get<{
      success: boolean;
      data: OrganizerInboxConversation[];
      meta?: { unread_total?: number };
    }>('/my/messages', { params, suppressServerErrorToast: true, skipAuthRedirect: true, ...config } as any),
  get: (conversationId: string, params?: { before_id?: string; limit?: number }, config?: any) =>
    api.get<{
      success: boolean;
      data: {
        conversation: OrganizerInboxConversation;
        messages: OrganizerDirectMessage[];
      };
      meta?: { has_more?: boolean };
    }>(`/my/messages/${conversationId}`, { params, skipInFlightDedup: true, suppressServerErrorToast: true, skipAuthRedirect: true, ...config }),
  send: (conversationId: string, payload: string | { message?: string; attachments?: any[]; poll?: any }) =>
    api.post(`/my/messages/${conversationId}`, typeof payload === 'string' ? { message: payload } : payload),
  sendPayload: (conversationId: string, payload: { message?: string; attachments?: any[]; poll?: any }) =>
    api.post(`/my/messages/${conversationId}`, payload),
  markRead: (conversationId: string, data?: { message_id?: string }) =>
    api.post(`/my/messages/${conversationId}/read`, data ?? {}),
  votePoll: (pollId: string, optionId: string) =>
    api.post(`/polls/${pollId}/vote`, { option_id: optionId }),
  closePoll: (pollId: string) =>
    api.post(`/polls/${pollId}/close`, {}),
};

export const organizerOrgChatApi = {
  getOrganizations: () =>
    api.get<{
      success: boolean;
      data: Array<{
        id: string;
        name: string;
        logo_url?: string | null;
        description?: string | null;
        admin_user_id?: string | null;
        conversation_id?: string | null;
        unread_count?: number;
      }>;
    }>('/organizer/my-organizations', { skipInFlightDedup: true, suppressServerErrorToast: true, skipAuthRedirect: true } as any),
  startChat: (organization_id: string) =>
    api.post<{
      success: boolean;
      data: OrganizerInboxConversation;
    }>('/organizer/org-chat/start', { organization_id }, { suppressServerErrorToast: true, skipAuthRedirect: true } as any),
  getOrganizers: () =>
    api.get<{
      success: boolean;
      data: Array<{
        id: string;
        name: string;
        email?: string | null;
        avatar?: string | null;
        role?: string;
        conversation_id?: string | null;
        unread_count?: number;
      }>;
    }>('/organization/my-organizers', { skipInFlightDedup: true, suppressServerErrorToast: true, skipAuthRedirect: true } as any),
  startOrganizerChat: (organizer_user_id: string) =>
    api.post<{
      success: boolean;
      data: OrganizerInboxConversation;
    }>('/organization/organizer-chat/start', { organizer_user_id }, { suppressServerErrorToast: true, skipAuthRedirect: true } as any),
};

/**
 * Production event workflow: draft lifecycle (POST/PATCH /events/...), tickets (/events/{id}/tickets), publish.
 */
export type WorkspaceChatMessage = {
  id: string;
  message: string | null;
  created_at: string;
  message_type?: string;
  channel_type?: string;
  is_pinned?: boolean;
  attachment_type?: string | null;
  attachment_url?: string | null;
  attachment_name?: string | null;
  reply_to_id?: string | null;
  sender_context?: Record<string, unknown> | null;
  user?: { id: string; name: string } | null;
  reply_to?: { id: string; message: string | null; user?: { id: string; name: string } | null } | null;
};

export type WorkspaceChatAbilities = {
  is_organizer?: boolean;
  is_participant?: boolean;
  can_announce?: boolean;
  can_moderate?: boolean;
  can_post?: boolean;
  can_view?: boolean;
  is_staff?: boolean;
};

export type WorkspaceChatAttachment = {
  attachment_type: string;
  attachment_url: string;
  attachment_name: string;
};

export type WorkspaceChatSendPayload = {
  message?: string;
  channel?: string;
  team_id?: string;
  reply_to_id?: string;
  message_type?: string;
  attachment_type?: string;
  attachment_url?: string;
  attachment_name?: string;
};

export type ActivityTeamMemberRecord = {
  id: string;
  user_id?: string | null;
  email: string;
  name?: string | null;
  role_slug: string;
  role_label?: string;
  status: string;
  permissions?: string[];
  joined_at?: string | null;
  invited_at?: string | null;
  accepted_at?: string | null;
  expires_at?: string | null;
};

export type ActivityFrameworkCatalogItem = {
  key: string;
  label: string;
  icon: string;
  template_code: string | null;
  framework_code: string | null;
  default_registration_mode: string;
};

export type EventActivityRecord = {
  id: string;
  event_id: string;
  framework_key: string;
  framework_label?: string;
  name: string;
  slug: string;
  status: string;
  visibility: string;
  registration_mode: string;
  registrations_count?: number;
  workspace_path?: string;
  is_default?: boolean;
};

const ACTIVITY_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isValidActivityId(id: unknown): boolean {
  if (!id || typeof id !== 'string') return false;
  const trimmed = id.trim().toLowerCase();
  if (trimmed === '__default' || trimmed === '_default' || trimmed === 'default' || trimmed === 'null' || trimmed === 'undefined' || trimmed === '0') {
    return false;
  }
  return ACTIVITY_UUID_PATTERN.test(trimmed);
}

export const activityApi = {
  catalog: async () => {
    try {
      const res = await api.get<{ success: boolean; data: ActivityFrameworkCatalogItem[] }>('/activity-frameworks/catalog');
      if (res.data?.success && Array.isArray(res.data?.data)) {
        res.data.data = res.data.data.filter((item) => item.key !== 'coding_contest');
      }
      return res;
    } catch {
      return { data: { success: true, data: [] } } as any;
    }
  },
  list: (eventId: string) =>
    api.get<{
      success: boolean;
      data: { event_id: string; is_multi_activity: boolean; activities: EventActivityRecord[] };
    }>(`/events/${eventId}/activities`),
  create: (eventId: string, data: Record<string, unknown>) =>
    api.post<{ success: boolean; data: EventActivityRecord }>(`/events/${eventId}/activities`, data),
  update: (activityId: string, data: Record<string, unknown>) =>
    api.put<{ success: boolean; data: EventActivityRecord }>(`/activities/${activityId}`, data),
  delete: (activityId: string) => api.delete(`/activities/${activityId}`),
  workspace: (activityId: string) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot load activity workspace: invalid activity ID "${activityId}"`));
    }
    return api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/workspace`);
  },
  // Phase 20B — workspace module endpoints
  dashboard: (activityId: string) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot load activity dashboard: invalid activity ID "${activityId}"`));
    }
    return api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/dashboard`);
  },
  participants: (activityId: string, params?: { search?: string; status?: string; page?: number; pageSize?: number }) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/participants`, { params }),
  syncParticipants: (activityId: string) =>
    api.post<{ success: boolean; data: Record<string, unknown>; message?: string }>(`/activities/${activityId}/participants/sync`),
  eligibleParticipants: (activityId: string, params?: { search?: string }) =>
    api.get<{ success: boolean; data: any[] }>(`/activities/${activityId}/participants/eligible`, { params }),
  addParticipant: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/participants`, data),
  removeParticipant: (activityId: string, registrationId: string) =>
    api.delete(`/activities/${activityId}/participants/${registrationId}`),
  teams: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/teams`),
  createTeam: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/teams`, data),
  getTeam: (activityId: string, teamId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/teams/${teamId}`),
  updateTeam: (activityId: string, teamId: string, data: Record<string, unknown>) =>
    api.put(`/activities/${activityId}/teams/${teamId}`, data),
  deleteTeam: (activityId: string, teamId: string) =>
    api.delete(`/activities/${activityId}/teams/${teamId}`),
  addTeamMember: (activityId: string, teamId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/teams/${teamId}/members`, data),
  removeTeamMember: (activityId: string, teamId: string, userId: string) =>
    api.delete(`/activities/${activityId}/teams/${teamId}/members/${userId}`),
  setTeamCaptain: (activityId: string, teamId: string, userId: string) =>
    api.put(`/activities/${activityId}/teams/${teamId}/captain`, { user_id: userId }),
  assignTeamMentor: (activityId: string, teamId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/teams/${teamId}/mentors`, data),
  unassignTeamMentor: (activityId: string, teamId: string, assignmentId: string) =>
    api.delete(`/activities/${activityId}/teams/${teamId}/mentors/${assignmentId}`),
  projects: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/projects`),
  createProject: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/projects`, data),
  updateProject: (activityId: string, projectId: string, data: Record<string, unknown>) =>
    api.put(`/activities/${activityId}/projects/${projectId}`, data),
  submitProject: (activityId: string, projectId: string) =>
    api.post(`/activities/${activityId}/projects/${projectId}/submit`, {}),
  deleteProject: (activityId: string, projectId: string) =>
    api.delete(`/activities/${activityId}/projects/${projectId}`),
  leaderboard: (activityId: string, params?: Record<string, unknown>, options?: AxiosRequestConfig) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/leaderboard`, { params, ...options }),
  exportLeaderboard: (activityId: string, params?: Record<string, unknown>) =>
    api.get(`/activities/${activityId}/leaderboard/export`, { params, responseType: 'blob' }),
  domainContext: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/domain-context`),
  conferenceSpeakers: (activityId: string) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot load conference speakers: invalid activity ID "${activityId}"`));
    }
    return api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/conference/speakers`);
  },
  createConferenceSpeaker: (activityId: string, data: Record<string, unknown>) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot create conference speaker: invalid activity ID "${activityId}"`));
    }
    return api.post(`/activities/${activityId}/conference/speakers`, data);
  },
  updateConferenceSpeaker: (activityId: string, speakerId: string, data: Record<string, unknown>) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot update conference speaker: invalid activity ID "${activityId}"`));
    }
    return api.put(`/activities/${activityId}/conference/speakers/${speakerId}`, data);
  },
  deleteConferenceSpeaker: (activityId: string, speakerId: string) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot delete conference speaker: invalid activity ID "${activityId}"`));
    }
    return api.delete(`/activities/${activityId}/conference/speakers/${speakerId}`);
  },
  conferenceTracks: (activityId: string) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot load conference tracks: invalid activity ID "${activityId}"`));
    }
    return api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/conference/tracks`);
  },
  createConferenceTrack: (activityId: string, data: Record<string, unknown>) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot create conference track: invalid activity ID "${activityId}"`));
    }
    return api.post(`/activities/${activityId}/conference/tracks`, data);
  },
  deleteConferenceTrack: (activityId: string, trackId: string) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot delete conference track: invalid activity ID "${activityId}"`));
    }
    return api.delete(`/activities/${activityId}/conference/tracks/${trackId}`);
  },
  conferenceSessions: (activityId: string) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot load conference sessions: invalid activity ID "${activityId}"`));
    }
    return api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/conference/sessions`);
  },
  createConferenceSession: (activityId: string, data: Record<string, unknown>) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot create conference session: invalid activity ID "${activityId}"`));
    }
    return api.post(`/activities/${activityId}/conference/sessions`, data);
  },
  updateConferenceSession: (activityId: string, sessionId: string, data: Record<string, unknown>) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot update conference session: invalid activity ID "${activityId}"`));
    }
    return api.put(`/activities/${activityId}/conference/sessions/${sessionId}`, data);
  },
  deleteConferenceSession: (activityId: string, sessionId: string) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot delete conference session: invalid activity ID "${activityId}"`));
    }
    return api.delete(`/activities/${activityId}/conference/sessions/${sessionId}`);
  },
  conferenceAgenda: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/conference/agenda`),
  munCountriesCatalog: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/mun/countries-catalog`),
  attendance: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/attendance`),
  checkIn: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/attendance`, data),
  results: (activityId: string, round?: number | AxiosRequestConfig, options?: AxiosRequestConfig) => {
    const config = typeof round === 'object' ? round : options;
    const roundNum = typeof round === 'number' ? round : undefined;
    return api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/results`, { params: roundNum ? { round: roundNum } : {}, ...config });
  },
  addResult: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/results`, data),
  removeResult: (activityId: string, resultId: string) =>
    api.delete(`/activities/${activityId}/results/${resultId}`),
  certificates: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/certificates`),
  certificateTemplates: (activityId: string) =>
    api.get<{ success: boolean; data: { templates: any[] } }>(`/activities/${activityId}/certificates/templates`),
  uploadCertificateTemplate: (activityId: string, formData: FormData) =>
    api.post<{ success: boolean; message: string; data: { template: any } }>(
      `/activities/${activityId}/certificates/template`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ),
  deleteCertificateTemplate: (activityId: string, templateId: string) =>
    api.delete<{ success: boolean; message: string }>(`/activities/${activityId}/certificates/template/${templateId}`),
  previewCertificateTemplateBlob: (activityId: string, templateId: string, sampleName?: string) =>
    api.get(`/activities/${activityId}/certificates/template/${templateId}/preview`, {
      params: sampleName ? { name: sampleName } : undefined,
      responseType: 'blob',
    }),
  bulkIssueCertificates: (activityId: string, type: string) =>
    api.post(`/activities/${activityId}/certificates/bulk`, { type, auto_create_template: true }),
  getQa: (activityId: string) =>
    api.get<{ success: boolean; data: { questions: any[]; can_manage?: boolean } }>(`/activities/${activityId}/qa`),
  askQaQuestion: (activityId: string, data: { question: string; is_anonymous?: boolean; session_id?: string }) =>
    api.post<{ success: boolean; message: string; data: any }>(`/activities/${activityId}/qa`, data),
  upvoteQaQuestion: (activityId: string, questionId: string) =>
    api.post<{ success: boolean; data: { question_id: string; upvotes: number; has_upvoted: boolean } }>(
      `/activities/${activityId}/qa/${questionId}/upvote`
    ),
  answerQaQuestion: (activityId: string, questionId: string, answer: string) =>
    api.post<{ success: boolean; message: string }>(`/activities/${activityId}/qa/${questionId}/answer`, { answer }),
  reports: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/reports`),
  analytics: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/analytics`),
  communications: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/communications`),
  postCommunication: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/communications`, data),
  deleteCommunication: (activityId: string, announcementId: string) =>
    api.delete(`/activities/${activityId}/communications/${announcementId}`),
  permissions: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/permissions`),
  grantPermission: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/permissions`, data),
  revokePermission: (activityId: string, permissionId: string) =>
    api.delete(`/activities/${activityId}/permissions/${permissionId}`),
  // Phase B — framework entity modules
  frameworkEntities: (activityId: string, moduleKey: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(
      `/activities/${activityId}/framework-modules/${moduleKey}`,
    ),
  storeFrameworkEntity: (activityId: string, moduleKey: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/framework-modules/${moduleKey}`, data),
  destroyFrameworkEntity: (activityId: string, moduleKey: string, entityId: string) =>
    api.delete(`/activities/${activityId}/framework-modules/${moduleKey}/${entityId}`),
  // Activity chat (private per-activity channel)
  getChat: (activityId: string, params?: { q?: string }) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot fetch activity chat: invalid activity ID "${activityId}"`));
    }
    return api.get<{ success: boolean; data: WorkspaceChatMessage[]; abilities?: WorkspaceChatAbilities }>(`/activities/${activityId}/chat`, { params });
  },
  getChatUnread: (activityId: string) => {
    if (!isValidActivityId(activityId)) {
      return Promise.resolve({ data: { success: false, data: { unread_count: 0 } } } as any);
    }
    return api.get<{ success: boolean; data: { unread_count: number } }>(`/activities/${activityId}/chat/unread`, { silent: true });
  },
  markChatRead: (activityId: string, data?: { message_id?: string }) => {
    if (!isValidActivityId(activityId)) {
      return Promise.resolve({ data: { success: false } } as any);
    }
    return api.post(`/activities/${activityId}/chat/read`, data ?? {});
  },
  sendChat: (activityId: string, data: WorkspaceChatSendPayload) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot send activity chat: invalid activity ID "${activityId}"`));
    }
    return api.post(`/activities/${activityId}/chat`, data);
  },
  pinChatMessage: (activityId: string, messageId: string) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot pin activity chat: invalid activity ID "${activityId}"`));
    }
    return api.post(`/activities/${activityId}/chat/${messageId}/pin`);
  },
  unpinChatMessage: (activityId: string, messageId: string) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot unpin activity chat: invalid activity ID "${activityId}"`));
    }
    return api.delete(`/activities/${activityId}/chat/${messageId}/pin`);
  },
  uploadChatAttachment: (activityId: string, file: File) => {
    if (!isValidActivityId(activityId)) {
      return Promise.reject(new Error(`Cannot upload activity chat attachment: invalid activity ID "${activityId}"`));
    }
    const form = new FormData();
    form.append('file', file);
    return api.post<{ success: boolean; data: WorkspaceChatAttachment }>(`/activities/${activityId}/chat/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Activity staff team (organizers assigned to this activity)
  staffTeam: (activityId: string) =>
    api.get<{ success: boolean; data: ActivityTeamMemberRecord[]; roles?: string[] }>(`/activities/${activityId}/staff-team`),
  addStaffTeamMember: (activityId: string, data: { email: string; name?: string; role_slug: string; message?: string; user_id?: string; permissions?: string[] }) =>
    api.post(`/activities/${activityId}/staff-team`, data),
  resendStaffInvite: (activityId: string, memberId: string) =>
    api.post(`/activities/${activityId}/staff-team/${memberId}/resend`),
  revokeStaffInvite: (activityId: string, memberId: string) =>
    api.post(`/activities/${activityId}/staff-team/${memberId}/revoke`),
  updateStaffTeamMember: (activityId: string, memberId: string, data: Record<string, unknown>) =>
    api.put(`/activities/${activityId}/staff-team/${memberId}`, data),
  removeStaffTeamMember: (activityId: string, memberId: string) =>
    api.delete(`/activities/${activityId}/staff-team/${memberId}`),
  staffTeamRoleCatalog: (activityId: string) =>
    api.get<{ success: boolean; data: Array<{ slug: string; name: string; description: string; permissions: string[] }> }>(
      `/activities/${activityId}/staff-team/roles`,
    ),
  // Phase D/E — organizer experience & productivity
  organizerExperience: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/organizer/experience`),
  organizerCommandCenter: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/organizer/command-center`),
  organizerBulk: (activityId: string, domain: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/organizer/bulk/${domain}`, { ...data, confirm: true }),
  organizerSmartDefaults: (activityId: string) =>
    api.post(`/activities/${activityId}/organizer/smart-defaults`, {}),
  organizerScheduleTask: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/organizer/schedule-task`, data),
  organizerAudit: (activityId: string, limit = 50) =>
    api.get<{ success: boolean; data: { items: Array<Record<string, unknown>> } }>(
      `/activities/${activityId}/organizer/audit`,
      { params: { limit } },
    ),
  organizerUndo: (activityId: string, auditId: string) =>
    api.post(`/activities/${activityId}/organizer/audit/${auditId}/undo`, {}),
  organizerApproveCertificates: (activityId: string) =>
    api.post(`/activities/${activityId}/organizer/approve-certificates`, {}),
  // Phase 22A — evaluation engine
  evalDashboard: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/evaluation/dashboard`),
  evalTemplates: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/evaluation/templates`),
  createEvalTemplate: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/evaluation/templates`, data),
  updateEvalTemplate: (activityId: string, templateId: string, data: Record<string, unknown>) =>
    api.put(`/activities/${activityId}/evaluation/templates/${templateId}`, data),
  deleteEvalTemplate: (activityId: string, templateId: string) =>
    api.delete(`/activities/${activityId}/evaluation/templates/${templateId}`),
  // Evaluation Builder — structure + template lifecycle
  evalTemplateStructure: (activityId: string, templateId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/evaluation/templates/${templateId}/structure`),
  duplicateEvalTemplate: (activityId: string, templateId: string) =>
    api.post(`/activities/${activityId}/evaluation/templates/${templateId}/duplicate`, {}),
  archiveEvalTemplate: (activityId: string, templateId: string, restore = false) =>
    api.post(`/activities/${activityId}/evaluation/templates/${templateId}/archive`, { restore }),
  versionEvalTemplate: (activityId: string, templateId: string) =>
    api.post(`/activities/${activityId}/evaluation/templates/${templateId}/version`, {}),
  // Evaluation Builder — categories
  evalCategories: (activityId: string, templateId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/evaluation/templates/${templateId}/categories`),
  addEvalCategory: (activityId: string, templateId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/evaluation/templates/${templateId}/categories`, data),
  updateEvalCategory: (activityId: string, templateId: string, categoryId: string, data: Record<string, unknown>) =>
    api.put(`/activities/${activityId}/evaluation/templates/${templateId}/categories/${categoryId}`, data),
  deleteEvalCategory: (activityId: string, templateId: string, categoryId: string) =>
    api.delete(`/activities/${activityId}/evaluation/templates/${templateId}/categories/${categoryId}`),
  evalCriteria: (activityId: string, templateId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/evaluation/templates/${templateId}/criteria`),
  addEvalCriterion: (activityId: string, templateId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/evaluation/templates/${templateId}/criteria`, data),
  updateEvalCriterion: (activityId: string, templateId: string, criterionId: string, data: Record<string, unknown>) =>
    api.put(`/activities/${activityId}/evaluation/templates/${templateId}/criteria/${criterionId}`, data),
  deleteEvalCriterion: (activityId: string, templateId: string, criterionId: string) =>
    api.delete(`/activities/${activityId}/evaluation/templates/${templateId}/criteria/${criterionId}`),
  evalJudges: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/evaluation/judges`),
  assignEvalJudge: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/evaluation/judges`, data),
  inviteEvalJury: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/evaluation/jury/invite`, data),
  assignEvalJudgeSubjects: (activityId: string, assignmentId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/evaluation/judges/${assignmentId}/subjects`, data),
  updateEvalJudge: (activityId: string, assignmentId: string, data: Record<string, unknown>) =>
    api.put(`/activities/${activityId}/evaluation/judges/${assignmentId}`, data),
  unassignEvalJudge: (activityId: string, assignmentId: string) =>
    api.delete(`/activities/${activityId}/evaluation/judges/${assignmentId}`),
  evalEvaluations: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/evaluation/evaluations`),
  createEvaluation: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/evaluation/evaluations`, data),
  updateEvaluation: (activityId: string, evalId: string, data: Record<string, unknown>) =>
    api.put(`/activities/${activityId}/evaluation/evaluations/${evalId}`, data),
  submitEvaluation: (activityId: string, evalId: string) =>
    api.post(`/activities/${activityId}/evaluation/evaluations/${evalId}/submit`, {}),
  lockEvaluation: (activityId: string, evalId: string) =>
    api.post(`/activities/${activityId}/evaluation/evaluations/${evalId}/lock`, {}),
  unlockEvaluation: (activityId: string, evalId: string) =>
    api.post(`/activities/${activityId}/evaluation/evaluations/${evalId}/unlock`, {}),
  evalWorkspace: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/evaluation/workspace`),
  evalResults: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/evaluation/results`),
  evalPublish: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/evaluation/publish`, data),
  evalAudit: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/evaluation/audit`),
  // Phase 22B — live judge workflow
  evalScanQr: (activityId: string, data: Record<string, unknown>) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/evaluation/scan-qr`, data),
  evalMyQueue: (activityId: string, round?: number) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/evaluation/my-queue${round ? `?round=${round}` : ''}`),
  evalSearchSubjects: (activityId: string, query: string) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/evaluation/search-subjects`, { query }),
  // Phase 21A — settings + resources
  getSettings: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/settings`),
  updateSettings: (activityId: string, data: Record<string, unknown>) =>
    api.put<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/settings`, data),
  resources: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/resources`),
  addResource: (activityId: string, data: Record<string, unknown>) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/resources`, data),
  removeResource: (activityId: string, resourceId: string) =>
    api.delete(`/activities/${activityId}/resources/${resourceId}`),
  // Specialized Activity Modules
  munSessions: (activityId: string) =>
    api.get<{ success: boolean; data: { sessions: any[]; committees: any[]; can_manage: boolean } }>(`/activities/${activityId}/mun/sessions`),
  storeMunSession: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/mun/sessions`, data),
  updateMunSessionStatus: (activityId: string, sessionId: string, data: { status: string; current_speaker?: string }) =>
    api.post(`/activities/${activityId}/mun/sessions/${sessionId}/status`, data),
  munCouncilVotes: (activityId: string, projector = false, committeeId?: string) => {
    const params = new URLSearchParams();
    if (projector) params.set('projector', '1');
    if (committeeId) params.set('committee_id', committeeId);
    const qs = params.toString();
    return api.get(`/activities/${activityId}/mun/council-votes${qs ? `?${qs}` : ''}`);
  },
  openMunCouncilVote: (activityId: string, sessionId: string, durationMinutes?: number) =>
    api.post(`/activities/${activityId}/mun/council-votes/${sessionId}/open`, durationMinutes ? { duration_minutes: durationMinutes } : {}),
  closeMunCouncilVote: (activityId: string, sessionId: string) =>
    api.post(`/activities/${activityId}/mun/council-votes/${sessionId}/close`),
  publishMunCouncilVote: (activityId: string, sessionId: string) =>
    api.post(`/activities/${activityId}/mun/council-votes/${sessionId}/publish`),
  castMunCouncilVote: (activityId: string, sessionId: string, voteChoice: string, countryId?: string) =>
    api.post(`/activities/${activityId}/mun/council-votes/${sessionId}/ballots`, {
      vote_choice: voteChoice,
      ...(countryId ? { country_id: countryId } : {}),
    }),
  createMunCouncilVote: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/mun/council-votes`, data),
  munMotions: (activityId: string) =>
    api.get<{ success: boolean; data: any[] }>(`/activities/${activityId}/mun/motions`),
  createMunMotion: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/mun/motions`, data),
  updateMunMotion: (activityId: string, motionId: string, data: Record<string, unknown>) =>
    api.put(`/activities/${activityId}/mun/motions/${motionId}`, data),
  deleteMunMotion: (activityId: string, motionId: string) =>
    api.delete(`/activities/${activityId}/mun/motions/${motionId}`),
  startMotionVote: (activityId: string, motionId: string, data?: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/mun/motions/${motionId}/start-vote`, data ?? {}),
  munVoting: (activityId: string) =>
    api.get<{ success: boolean; data: { sessions: any[]; committees: any[]; can_manage: boolean } }>(`/activities/${activityId}/mun/voting`),
  storeMunVoting: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/mun/voting`, data),
  recordMunBallot: (activityId: string, sessionId: string, data: { delegate_name: string; country: string; vote: string }) =>
    api.post(`/activities/${activityId}/mun/voting/${sessionId}/ballots`, data),
  transitionMunVoting: (activityId: string, sessionId: string, action: 'open' | 'close' | 'reopen' | 'publish') =>
    api.post(`/activities/${activityId}/mun/voting/${sessionId}/transition`, { action }),
  munPositionPapers: (activityId: string) =>
    api.get<{ success: boolean; data: { papers: any[]; can_manage: boolean } }>(`/activities/${activityId}/mun/position-papers`),
  storeMunPositionPaper: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/mun/position-papers`, data),
  gradeMunPositionPaper: (activityId: string, paperId: string, data: { status: string; score?: number; feedback?: string }) =>
    api.put(`/activities/${activityId}/mun/position-papers/${paperId}/grade`, data),
  munResolutions: (activityId: string) =>
    api.get<{ success: boolean; data: { resolutions: any[]; can_manage: boolean } }>(`/activities/${activityId}/mun/resolutions`),
  storeMunResolution: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/mun/resolutions`, data),
  storeMunResolutionAmendment: (activityId: string, resolutionId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/mun/resolutions/${resolutionId}/amendments`, data),
  munDelegates: (activityId: string) =>
    api.get<{ success: boolean; data: { allocations: any[]; candidates: any[]; committees: any[]; can_manage: boolean } }>(`/activities/${activityId}/mun/delegates`),
  munCountryOptions: (activityId: string) =>
    api.get<{ success: boolean; data: { councils: any[] } }>(`/activities/${activityId}/mun/country-options`),
  munCouncilTypes: (activityId: string) =>
    api.get<{ success: boolean; data: Array<Record<string, unknown>> }>(`/activities/${activityId}/mun/council-types`),
  addMunCouncil: (activityId: string, data: { council_type_id: string; capacity?: number }) =>
    api.post(`/activities/${activityId}/mun/councils`, data),
  munCouncilCountries: (activityId: string, committeeId: string, params?: { q?: string; catalog?: boolean }) =>
    api.get(`/activities/${activityId}/mun/councils/${committeeId}/countries`, { params }),
  updateMunCouncilEligibility: (activityId: string, committeeId: string, countryIds: string[]) =>
    api.put(`/activities/${activityId}/mun/councils/${committeeId}/eligibility`, { country_ids: countryIds }),
  allocateMunDelegate: (activityId: string, data: {
    delegate_name: string;
    delegate_email?: string;
    country?: string;
    country_id?: string;
    committee_name?: string;
    committee_id?: string;
    allocation_id?: string;
    status?: string;
  }) =>
    api.post(`/activities/${activityId}/mun/delegates/allocate`, data),
  mentorship: (activityId: string) =>
    api.get<{ success: boolean; data: { teams: any[]; mentors: any[]; assignments: any[]; can_manage: boolean } }>(`/activities/${activityId}/mentors`),
  createMentor: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/mentors`, data),
  updateMentor: (activityId: string, mentorId: string, data: Record<string, unknown>) =>
    api.put(`/activities/${activityId}/mentors/${mentorId}`, data),
  deleteMentor: (activityId: string, mentorId: string) =>
    api.delete(`/activities/${activityId}/mentors/${mentorId}`),
  assignMentorship: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/mentorship-assignments`, data),
  logMentorshipCheckIn: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/mentors/check-in`, data),
  checkIns: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/check-ins`),
  createCheckIn: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/check-ins`, data),
  updateCheckIn: (activityId: string, checkInId: string, data: Record<string, unknown>) =>
    api.put(`/activities/${activityId}/check-ins/${checkInId}`, data),
  activityAwards: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/activities/${activityId}/awards`),
  createActivityAward: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/awards`, data),
  updateActivityAward: (activityId: string, prizeId: string, data: Record<string, unknown>) =>
    api.put(`/activities/${activityId}/awards/${prizeId}`, data),
  deleteActivityAward: (activityId: string, prizeId: string) =>
    api.delete(`/activities/${activityId}/awards/${prizeId}`),
  assignActivityAward: (activityId: string, prizeId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/awards/${prizeId}/assign`, data),
  setActivityAwardPublication: (activityId: string, prizeId: string, status: 'draft' | 'finalized' | 'published') =>
    api.post(`/activities/${activityId}/awards/${prizeId}/publication`, { status }),
  sponsors: (activityId: string) =>
    api.get<{ success: boolean; data: { sponsors: any[]; can_manage: boolean } }>(`/activities/${activityId}/sponsors`),
  storeSponsor: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/sponsors`, data),
  destroySponsor: (activityId: string, sponsorId: string) =>
    api.delete(`/activities/${activityId}/sponsors/${sponsorId}`),
  debateRounds: (activityId: string) =>
    api.get<{ success: boolean; data: { rounds: any[]; can_manage: boolean } }>(`/activities/${activityId}/debate/rounds`),
  storeDebateRound: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/debate/rounds`, data),
  debateMotions: (activityId: string) =>
    api.get<{ success: boolean; data: { motions: any[]; can_manage: boolean } }>(`/activities/${activityId}/debate/motions`),
  storeDebateMotion: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/debate/motions`, data),
  debatePairings: (activityId: string) =>
    api.get<{ success: boolean; data: { pairings: any[]; can_manage: boolean } }>(`/activities/${activityId}/debate/pairings`),
  storeDebatePairing: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/debate/pairings`, data),
  problems: (activityId: string) =>
    api.get<{ success: boolean; data: { problems: any[]; can_manage: boolean } }>(`/activities/${activityId}/problems`),
  storeProblem: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/problems`, data),
  destroyProblem: (activityId: string, problemId: string) =>
    api.delete(`/activities/${activityId}/problems/${problemId}`),
  paperSubmissions: (activityId: string) =>
    api.get<{ success: boolean; data: { papers: any[]; can_manage: boolean } }>(`/activities/${activityId}/paper-submissions`),
  storePaperSubmission: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/paper-submissions`, data),
  plagiarismReports: (activityId: string) =>
    api.get<{ success: boolean; data: { reports: any[]; can_manage: boolean } }>(`/activities/${activityId}/plagiarism-reports`),
};

// Public Conference Pages API
export const publicConferenceApi = {
  show: (slug: string) => api.get(`/conference/${slug}`),
  speakers: (slug: string) => api.get(`/conference/${slug}/speakers`),
  speaker: (slug: string, speakerId: string) => api.get(`/conference/${slug}/speakers/${speakerId}`),
  session: (slug: string, sessionId: string) => api.get(`/conference/${slug}/sessions/${sessionId}`),
  downloadAgenda: (slug: string) => api.get(`/conference/${slug}/agenda/download`, { responseType: 'blob' }),
  cfp: (slug: string) => api.get(`/conference/${slug}/cfp`),
  register: (slug: string, data: Record<string, unknown>) => api.post(`/conference/${slug}/register`, data),
};

export const eventWorkflowApi = {
  createDraft: (data?: {
    step?: string;
    title?: string;
    description?: string;
    contact_email?: string;
    slug?: string;
    type?: string;
    event_framework_id?: string;
    mode?: string;
    start_date?: string;
    end_date?: string;
    organization_id?: string;
    owner_type?: string;
    owner_id?: string;
  }) => api.post('/events', data ?? {}),
  /**
   * Load the event draft. Pass `{ fresh: true }` after a mutation or on version conflict
   * so in-flight GET dedup cannot return a pre-save version.
   */
  getDraft: (eventId: string, options?: { fresh?: boolean }) =>
    api.get(`/events/${eventId}`, {
      skipInFlightDedup: Boolean(options?.fresh),
      ...(options?.fresh ? { params: { _ts: Date.now() } } : {}),
    } as ApiGetConfig),
  updateSettings: (eventId: string, data: Record<string, unknown>) =>
    api.patch(`/events/${eventId}/settings`, data),
  saveStep: (eventId: string, step: string, body: { version: number; payload: Record<string, unknown> }) => {
    invalidateInFlightGets(`/events/${eventId}`);
    return api.patch(`/events/${eventId}/${step}`, body);
  },
  uploadCoverImage: (
    eventIdOrFile: string | File,
    fileOrProgress?: File | ((pct: number) => void),
    onProgress?: (pct: number) => void,
  ) => {
    if (typeof eventIdOrFile === 'string') {
      const eventId = eventIdOrFile;
      const file = fileOrProgress as File;
      const form = new FormData();
      form.append('cover_image', file);
      return api.post(`/events/${eventId}/upload-cover-image`, form, {
        onUploadProgress: (e) => {
          if (!onProgress || !e.total) return;
          onProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
    }

    const file = eventIdOrFile;
    const progress = fileOrProgress as ((pct: number) => void) | undefined;
    const form = new FormData();
    form.append('cover_image', file);
    return api.post<{
      success: boolean;
      message?: string;
      data?: { cover_image?: string; cover_image_url?: string; cover_image_path?: string };
    }>('/events/media/upload-cover', form, {
      onUploadProgress: (e) => {
        if (!progress || !e.total) return;
        progress(Math.round((e.loaded / e.total) * 100));
      },
    });
  },
  uploadLogoImage: (file: File, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append('logo_image', file);
    return api.post<{
      success: boolean;
      message?: string;
      data?: { logo?: string; logo_url?: string; logo_path?: string };
    }>('/events/media/upload-logo', form, {
      onUploadProgress: (e) => {
        if (!onProgress || !e.total) return;
        onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
  },
  uploadLogo: (eventId: string, file: File, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append('logo', file);
    return api.post(`/events/${eventId}/upload-logo`, form, {
      onUploadProgress: (e) => {
        if (!onProgress || !e.total) return;
        onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
  },
  uploadBannerImage: (file: File, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append('banner_image', file);
    return api.post<{
      success: boolean;
      message?: string;
      data?: { banner?: string; banner_url?: string; banner_path?: string };
    }>('/events/media/upload-banner', form, {
      onUploadProgress: (e) => {
        if (!onProgress || !e.total) return;
        onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
  },
  uploadBanner: (eventId: string, file: File, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append('banner', file);
    return api.post(`/events/${eventId}/upload-banner`, form, {
      onUploadProgress: (e) => {
        if (!onProgress || !e.total) return;
        onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
  },
  publishCheck: (eventId: string) =>
    api.get<{ success: boolean; data: { can_publish: boolean; errors: string[]; payout_readiness?: EventPayoutReadinessData } }>(`/events/${eventId}/publish-check`),
  getPayoutReadiness: (eventId: string) =>
    api.get<{ success: boolean; data: EventPayoutReadinessData }>(`/events/${eventId}/payout-readiness`, {
      skipGlobalForbidden: true,
    } as Record<string, unknown>),
  publish: (eventId: string) => api.post(`/events/${eventId}/publish`),
  createTicket: (eventId: string, data: Record<string, unknown>) => {
    if (!eventId) {
      throw new Error('Save the draft before adding tickets.');
    }
    invalidateInFlightGets(`/events/${eventId}`);
    return api.post(`/events/${eventId}/tickets`, data);
  },
  updateTicket: (ticketId: string, data: Record<string, unknown>) => {
    invalidateInFlightGets('/events/');
    return api.patch(`/tickets/${ticketId}`, data);
  },
  deleteTicket: async (ticketId: string) => {
    const id = String(ticketId ?? '').trim();
    if (!id || id.startsWith('temp') || id.startsWith('mock') || id.startsWith('new-')) {
      return { data: { success: true, message: 'Unsaved ticket skipped' } } as any;
    }
    invalidateInFlightGets('/events/');
    try {
      return await api.delete(`/tickets/${id}`);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        return { data: { success: true, message: 'Ticket not found or already deleted' } } as any;
      }
      throw err;
    }
  },
  reorderTickets: (eventId: string, ticketIds: string[]) => {
    invalidateInFlightGets(`/events/${eventId}`);
    return api.patch(`/events/${eventId}/tickets/reorder`, { ticket_ids: ticketIds });
  },
  getTicketFormFields: (ticketId: string) =>
    api.get<{
      success: boolean;
      data: {
        ticket_id: string;
        event_activity_id: string | null;
        form_fields: Array<{
          form_field_id: string;
          required: boolean;
          sort_order: number;
          field?: Record<string, unknown>;
        }>;
        event_form?: { id: string; name?: string; fields?: Array<Record<string, unknown>> } | null;
      };
    }>(`/tickets/${ticketId}/form-fields`),
  syncTicketFormFields: (
    ticketId: string,
    data: {
      event_activity_id?: string | null;
      apply_recommendations?: boolean;
      form_fields?: Array<{ form_field_id: string; required?: boolean; sort_order?: number }>;
    },
  ) => api.put(`/tickets/${ticketId}/form-fields`, data),
  applyTicketFormRecommendations: (ticketId: string) =>
    api.post(`/tickets/${ticketId}/form-fields/apply-recommendations`),
  listActivityRecommendedFields: (activityId: string) =>
    api.get<{ success: boolean; data: Array<Record<string, unknown>> }>(
      `/activities/${activityId}/recommended-fields`,
    ),
  syncActivityRecommendedFields: (
    activityId: string,
    data: {
      fields?: Array<{ form_field_id: string; required?: boolean; sort_order?: number }>;
      reseeds_from_framework?: boolean;
    },
  ) => api.put(`/activities/${activityId}/recommended-fields`, data),
  getEventRegistrationForm: (eventId: string) =>
    api.get<{
      success: boolean;
      data: {
        form: { id: string; name?: string; type?: string } | null;
        fields: Array<{
          id: string;
          label: string;
          slug?: string | null;
          type?: string;
          required?: boolean;
          options?: unknown;
          order?: number;
          placeholder?: string | null;
          help_text?: string | null;
          is_system?: boolean;
        }>;
      };
    }>(`/events/${eventId}/registration-form`),
  checkoutRegistrationForm: (eventId: string, ticketIds: string[]) =>
    api.get<{
      success: boolean;
      data: {
        form_id: string | null;
        fields: Array<Record<string, unknown>>;
        ticket_ids: string[];
        source: string;
      };
    }>(`/events/${eventId}/checkout/registration-form`, { params: { ticket_ids: ticketIds } }),
};

export const eventFrameworksApi = {
  list: () => api.get<{ success: boolean; data: EventFrameworkRecord[] }>('/event-frameworks', { cacheTtlMs: 300000 } as any),
  show: (id: string) => api.get<{ success: boolean; data: EventFrameworkRecord }>(`/event-frameworks/${id}`),
};

export type OrganizerExperienceNavSection = {
  id: string;
  label: string;
  route: string;
  route_slug: string;
  icon?: string | null;
  type: string;
  visible: boolean;
};

export type OrganizerExperienceNavGroup = {
  id: string;
  label: string;
  parent_section: string;
  items: Array<{
    id: string;
    label: string;
    route: string;
    route_slug: string;
    module_key?: string;
    visible: boolean;
  }>;
  visible: boolean;
};

export type OrganizerExperienceProgress = {
  stage_key: string;
  organizer_label: string;
  status: 'complete' | 'in_progress' | 'pending' | 'skipped';
  required: boolean;
};

export type OrganizerSmartAction = {
  key: string;
  label: string;
  description?: string;
  route_slug: string;
  priority: number;
  count?: number;
};

export type OrganizerExperiencePayload = {
  role?: string;
  event: { id: string; title?: string; status?: string; format_label?: string };
  workspace_mode?: 'mun' | 'standard' | 'conference';
  navigation: { sections: OrganizerExperienceNavSection[]; groups: OrganizerExperienceNavGroup[] };
  progress: OrganizerExperienceProgress[];
  smart_actions: OrganizerSmartAction[];
  empty_states: Record<string, { title: string; description?: string; guidance?: string; primary_action?: { label: string; route_slug: string } }>;
  setup_checklist?: Array<{ key: string; label: string; route?: string; route_slug?: string; complete: boolean }>;
  universal_capabilities: Array<Record<string, unknown>>;
  permissions: Record<string, boolean>;
  workflow_health?: number;
  workflow_progress?: {
    progress_percent: number;
    stages: OrganizerExperienceProgress[];
    missing_requirements?: unknown[];
    blocked_actions?: string[];
    current_stage?: { stage_key: string; organizer_label: string; status: string } | null;
  };
  alerts?: Array<{ key: string; severity: string; message: string; route_slug?: string }>;
  blocked_stages?: unknown[];
  recommendations?: { next_action?: OrganizerSmartAction | null; recommendations: OrganizerSmartAction[] };
  automation_status?: { triggered: unknown[]; pending: unknown[] };
  dashboard?: Record<string, unknown> & {
    labels?: Record<string, string>;
    metric_status?: Record<string, string>;
    pending_applications?: number;
  };
  navigation_groups?: OrganizerExperienceNavGroup[];
  workflow_stages?: OrganizerExperienceProgress[];
  role_catalog?: { roles: Array<{ role_key: string; role_name: string; permissions?: string[] }> };
  role_navigation?: Record<string, Array<{ id: string; label: string; route_slug: string }>>;
};

export type OrganizerEventFormatCategory = {
  id: string;
  label: string;
  formats: Array<{ id: string; label: string; description?: string; icon?: string; color?: string }>;
};

export const organizerExperienceApi = {
  getExperience: (eventId: string) =>
    api.get<{ success: boolean; data: OrganizerExperiencePayload }>(`/organizer/events/${eventId}/experience`),
  eventFormats: (category?: string) =>
    api.get<{ success: boolean; data: OrganizerEventFormatCategory[] }>('/organizer/event-formats', {
      params: category ? { category } : undefined,
    }),
};

/** MUN workspace APIs — Phase 16 engines */
export const munWorkspaceApi = {
  applications: (eventId: string, params?: Record<string, unknown>) =>
    api.get<{ success: boolean; data: unknown[]; meta?: Record<string, unknown> }>(
      `/events/${eventId}/applications`,
      { params },
    ),
  reviewApplication: (applicationId: string, data: Record<string, unknown>) =>
    api.post(`/applications/${applicationId}/review`, data),
  decideApplication: (applicationId: string, data: Record<string, unknown>) =>
    api.post(`/applications/${applicationId}/decision`, data),
  assignments: (eventId: string, params?: Record<string, unknown>) =>
    api.get<{ success: boolean; data: unknown[]; meta?: Record<string, unknown> }>(
      `/events/${eventId}/assignments`,
      { params },
    ),
  createAssignment: (eventId: string, data: Record<string, unknown>) =>
    api.post(`/events/${eventId}/assignments`, data),
  assignmentRecommendations: (eventId: string, data?: Record<string, unknown>) =>
    api.post(`/events/${eventId}/assignments/recommendations`, data ?? {}),
  committees: (eventId: string, activityId?: string) =>
    api.get<{ success: boolean; data: { committees?: unknown[]; meta?: { count?: number } } }>(
      `/events/${eventId}/committees`,
      { params: activityId ? { event_activity_id: activityId } : undefined },
    ),
  createCommittee: (eventId: string, data: Record<string, unknown>) =>
    api.post(`/events/${eventId}/committees`, data),
  ensureDefaultCouncils: (activityId: string) =>
    api.post<{ success: boolean; data: { created: number; kept: number } }>(`/activities/${activityId}/councils/defaults`),
  updateCommittee: (committeeId: string, data: Record<string, unknown>) =>
    api.put(`/committees/${committeeId}`, data),
  speakerQueue: (committeeId: string) =>
    api.get(`/committees/${committeeId}/speaker-queue`),
  speakerQueueAction: (committeeId: string, data: Record<string, unknown>) =>
    api.post(`/committees/${committeeId}/speaker-queue`, data),
  committeeAttendance: (committeeId: string, params?: Record<string, unknown>) =>
    api.get(`/committees/${committeeId}/attendance`, { params }),
  markCommitteeAttendance: (committeeId: string, data: Record<string, unknown>) =>
    api.post(`/committees/${committeeId}/attendance`, data),
  correctCommitteeAttendance: (committeeId: string, logId: string, data: Record<string, unknown>) =>
    api.put(`/committees/${committeeId}/attendance/${logId}`, data),
  committeeOfficers: (committeeId: string, data: Record<string, unknown>) =>
    api.post(`/committees/${committeeId}/officers`, data),
  secretariat: (eventId: string) => api.get(`/events/${eventId}/secretariat`),
  saveSecretariatOfficer: (eventId: string, data: Record<string, unknown>) =>
    api.post(`/events/${eventId}/secretariat`, data),
  activityDocuments: (activityId: string) => api.get(`/activities/${activityId}/documents`),
  createActivityDocumentType: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/document-types`, data),
  createActivityDocument: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/documents`, data),
  transitionActivityDocument: (activityId: string, documentId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/documents/${documentId}/transition`, data),
  collaborationPeers: (activityId: string) => api.get(`/activities/${activityId}/collaboration/peers`),
  collaborationAlliances: (activityId: string) => api.get(`/activities/${activityId}/collaboration/alliances`),
  createAlliance: (activityId: string, data: Record<string, unknown>) =>
    api.post(`/activities/${activityId}/collaboration/alliances`, data),
  inviteAlliance: (activityId: string, allianceId: string, userId: string) =>
    api.post(`/activities/${activityId}/collaboration/alliances/${allianceId}/invite`, { user_id: userId }),
  respondAlliance: (activityId: string, allianceId: string, status: 'accepted' | 'rejected') =>
    api.post(`/activities/${activityId}/collaboration/alliances/${allianceId}/respond`, { status }),
  leaveAlliance: (activityId: string, allianceId: string) =>
    api.post(`/activities/${activityId}/collaboration/alliances/${allianceId}/leave`, {}),
  openDirectConversation: (activityId: string, userId: string) =>
    api.post(`/activities/${activityId}/collaboration/direct`, { user_id: userId }),
  collaborationMessages: (activityId: string, conversationId: string) =>
    api.get(`/activities/${activityId}/collaboration/conversations/${conversationId}/messages`),
  sendCollaborationMessage: (activityId: string, conversationId: string, body: string) =>
    api.post(`/activities/${activityId}/collaboration/conversations/${conversationId}/messages`, { body }),
  blockDelegate: (activityId: string, userId: string) =>
    api.post(`/activities/${activityId}/collaboration/blocks`, { user_id: userId }),
  reportDelegateMessage: (activityId: string, messageId: string, reason: string) =>
    api.post(`/activities/${activityId}/collaboration/messages/${messageId}/report`, { reason }),
  committee: (committeeId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/committees/${committeeId}`),
  committeeReports: (committeeId: string, params?: Record<string, unknown>) =>
    api.get(`/committees/${committeeId}/reports`, { params }),
  resolutions: (committeeId: string) =>
    api.get<{ success: boolean; data: unknown[] }>(`/committees/${committeeId}/resolutions`),
  votingSessions: (committeeId: string) =>
    api.get<{ success: boolean; data: unknown[] }>(`/committees/${committeeId}/voting-sessions`),
  awards: (committeeId: string) =>
    api.get<{ success: boolean; data: unknown[] }>(`/committees/${committeeId}/awards`),
  certificateEligibility: (eventId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/events/${eventId}/certificate-eligibility`),
  generateCertificates: (eventId: string, data?: Record<string, unknown>) =>
    api.post(`/events/${eventId}/certificates/generate`, data ?? {}),
  publishCertificates: (eventId: string) =>
    api.post(`/events/${eventId}/certificates/publish`, {}),
  certificateReports: (eventId: string, type?: string) =>
    api.get(`/events/${eventId}/certificate-reports`, { params: type ? { type } : undefined }),
};

export const eventTelemetryApi = {
  ingest: (eventId: string, events: Record<string, unknown>[]) =>
    api.post(`/events/${eventId}/telemetry`, { events }),
  workflowAnalytics: (eventId: string, days?: number) =>
    api.get(`/events/${eventId}/workflow-analytics`, { params: days ? { days } : undefined }),
};

export const munCertificationApi = {
  dashboard: (eventId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/events/${eventId}/mun-certification`),
  run: (eventId: string) =>
    api.post<{ success: boolean; data: Record<string, unknown> }>(`/events/${eventId}/mun-certification/run`, {}),
  history: (eventId: string) =>
    api.get<{ success: boolean; data: unknown[] }>(`/events/${eventId}/mun-certification/history`),
  reports: (eventId: string, type?: string) =>
    api.get(`/events/${eventId}/mun-certification/reports`, { params: type ? { type } : undefined }),
  pilotAudit: (eventId: string) =>
    api.get<{ success: boolean; data: Record<string, unknown> }>(`/events/${eventId}/mun-pilot-audit`),
};

export type EventFrameworkRecord = {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  sort_order?: number;
  is_visible?: boolean;
  is_active?: boolean;
  default_registration_mode?: string;
  default_attendance_mode?: string;
  default_submission_mode?: string;
  default_evaluation_mode?: string;
  default_result_mode?: string;
  features?: Record<string, boolean>;
  wizard_steps?: Array<{ step_key: string; sort_order: number; required: boolean; visible: boolean }>;
};

/** Phase 11 Wave 2 — Event Operations Engine (registration, submissions, evaluation, jury, attendance, results). */
export const eventOperationsApi = {
  registration: {
    get: (eventId: string) => api.get(`/events/${eventId}/operations/registration`),
    saveConfig: (eventId: string, data: Record<string, unknown>) =>
      api.put(`/events/${eventId}/operations/registration/config`, data),
    saveForm: (eventId: string, data: Record<string, unknown>) =>
      api.put(`/events/${eventId}/operations/registration/form`, data),
  },
  submissions: {
    get: (eventId: string) => api.get(`/events/${eventId}/operations/submissions`),
    saveConfig: (eventId: string, data: Record<string, unknown>) =>
      api.put(`/events/${eventId}/operations/submissions/config`, data),
    list: (eventId: string, round?: number) =>
      api.get(`/events/${eventId}/operations/submissions/list`, { params: round ? { round } : {} }),
    submit: (eventId: string, data: Record<string, unknown>) =>
      api.post(`/events/${eventId}/operations/submissions/submit`, data),
  },
  evaluation: {
    templates: (eventId: string) => api.get(`/events/${eventId}/operations/evaluation/templates`),
    dashboard: (eventId: string) => api.get(`/events/${eventId}/operations/evaluation/dashboard`),
    saveTemplate: (eventId: string, data: Record<string, unknown>) =>
      api.post(`/events/${eventId}/operations/evaluation/templates`, data),
  },
  jury: {
    assignments: (eventId: string) => api.get(`/events/${eventId}/operations/jury/assignments`),
    assign: (eventId: string, data: Record<string, unknown>) =>
      api.post(`/events/${eventId}/operations/jury/assignments`, data),
    unassign: (eventId: string, assignmentId: string) =>
      api.delete(`/events/${eventId}/operations/jury/assignments/${assignmentId}`),
    updateAssignment: (eventId: string, assignmentId: string, data: Record<string, unknown>) =>
      api.put(`/events/${eventId}/operations/jury/assignments/${assignmentId}`, data),
    members: (eventId: string, params?: { search?: string; status?: string }) =>
      api.get(`/events/${eventId}/operations/jury/members`, { params }),
    inviteOptions: (eventId: string) => api.get(`/events/${eventId}/operations/jury/invite-options`),
    invite: (eventId: string, data: Record<string, unknown>) =>
      api.post(`/events/${eventId}/operations/jury/members/invite`, data),
    updateMember: (eventId: string, judgeId: string, data: Record<string, unknown>) =>
      api.put(`/events/${eventId}/operations/jury/members/${judgeId}`, data),
    resend: (eventId: string, judgeId: string) =>
      api.post(`/events/${eventId}/operations/jury/members/${judgeId}/resend`),
    disable: (eventId: string, judgeId: string) =>
      api.post(`/events/${eventId}/operations/jury/members/${judgeId}/disable`),
    enable: (eventId: string, judgeId: string) =>
      api.post(`/events/${eventId}/operations/jury/members/${judgeId}/enable`),
    revoke: (eventId: string, judgeId: string) =>
      api.post(`/events/${eventId}/operations/jury/members/${judgeId}/revoke`),
    remove: (eventId: string, judgeId: string) =>
      api.delete(`/events/${eventId}/operations/jury/members/${judgeId}`),
  },
  attendance: {
    get: (eventId: string) => api.get(`/events/${eventId}/operations/attendance`),
    saveModel: (eventId: string, data: Record<string, unknown>) =>
      api.post(`/events/${eventId}/operations/attendance/models`, data),
    analytics: (eventId: string) => api.get(`/events/${eventId}/operations/attendance/analytics`),
    manual: (eventId: string, registrationId: string) =>
      api.post(`/events/${eventId}/operations/attendance/manual`, { registration_id: registrationId }),
    otpGenerate: (eventId: string, registrationId: string) =>
      api.post(`/events/${eventId}/operations/attendance/otp/generate`, { registration_id: registrationId }),
    otpVerify: (eventId: string, code: string) =>
      api.post(`/events/${eventId}/operations/attendance/otp/verify`, { code }),
    bulk: (eventId: string, registrationIds: string[]) =>
      api.post(`/events/${eventId}/operations/attendance/bulk`, { registration_ids: registrationIds }),
  },
  results: {
    get: (eventId: string) => api.get(`/events/${eventId}/operations/results`),
    setDisposition: (eventId: string, data: Record<string, unknown>) =>
      api.post(`/events/${eventId}/operations/results/dispositions`, data),
    generate: (eventId: string, mode?: string) =>
      api.post(`/events/${eventId}/operations/results/generate`, { mode: mode ?? 'weighted' }),
    publish: (eventId: string) => api.post(`/events/${eventId}/operations/results/publish`),
    lock: (eventId: string) => api.post(`/events/${eventId}/operations/results/lock`),
  },
};

/** Phase 11 Wave 3 — Ecosystem (reports, feedback, certificates, sponsors, volunteers, support, intelligence). */
export const eventEcosystemApi = {
  reports: {
    dashboard: (eventId: string, params?: { activityId?: string; compareEventId?: string }) =>
      api.get(`/events/${eventId}/ecosystem/reports`, {
        params: {
          ...(params?.activityId ? { activity_id: params.activityId } : {}),
          ...(params?.compareEventId ? { compare_event_id: params.compareEventId } : {}),
        },
      }),
    snapshot: (eventId: string) => api.post(`/events/${eventId}/ecosystem/reports/snapshots`),
    snapshots: (eventId: string) => api.get(`/events/${eventId}/ecosystem/reports/snapshots`),
    exportCsv: (eventId: string, compareEventId?: string) =>
      api.get(`/events/${eventId}/ecosystem/reports/export/csv`, {
        responseType: 'blob',
        params: compareEventId ? { compare_event_id: compareEventId } : undefined,
      }),
    exportPdf: (eventId: string, compareEventId?: string) =>
      api.get(`/events/${eventId}/ecosystem/reports/export/pdf`, {
        responseType: 'blob',
        params: compareEventId ? { compare_event_id: compareEventId } : undefined,
      }),
    exportExcel: (eventId: string, compareEventId?: string) =>
      api.get(`/events/${eventId}/ecosystem/reports/export/excel`, {
        responseType: 'blob',
        params: compareEventId ? { compare_event_id: compareEventId } : undefined,
      }),
  },
  feedback: {
    get: (eventId: string) => api.get(`/events/${eventId}/ecosystem/feedback`),
    save: (eventId: string, data: Record<string, unknown>) => api.put(`/events/${eventId}/ecosystem/feedback`, data),
    analytics: (eventId: string) => api.get(`/events/${eventId}/ecosystem/feedback/analytics`),
    submit: (eventId: string, data: Record<string, unknown>) => api.post(`/events/${eventId}/ecosystem/feedback/submit`, data),
  },
  certificates: {
    registry: (eventId: string) => api.get(`/events/${eventId}/ecosystem/certificates/registry`),
    verify: (code: string) => api.get(`/certificates/${code}/verify`),
  },
  sponsors: {
    list: (eventId: string) => api.get(`/events/${eventId}/ecosystem/sponsors`),
    saveCategory: (eventId: string, data: Record<string, unknown>) =>
      api.post(`/events/${eventId}/ecosystem/sponsors/categories`, data),
    save: (eventId: string, data: Record<string, unknown>) => api.post(`/events/${eventId}/ecosystem/sponsors`, data),
    delete: (eventId: string, sponsorId: string) => api.delete(`/events/${eventId}/ecosystem/sponsors/${sponsorId}`),
    analytics: (eventId: string) => api.get(`/events/${eventId}/ecosystem/sponsors/analytics`),
  },
  volunteers: {
    list: (eventId: string) => api.get(`/events/${eventId}/ecosystem/volunteers`),
    saveRole: (eventId: string, data: Record<string, unknown>) =>
      api.post(`/events/${eventId}/ecosystem/volunteers/roles`, data),
    save: (eventId: string, data: Record<string, unknown>) => api.post(`/events/${eventId}/ecosystem/volunteers`, data),
    assign: (eventId: string, data: Record<string, unknown>) =>
      api.post(`/events/${eventId}/ecosystem/volunteers/assignments`, data),
    delete: (eventId: string, volunteerId: string) => api.delete(`/events/${eventId}/ecosystem/volunteers/${volunteerId}`),
    checkIn: (eventId: string, assignmentId: string, notes?: string) =>
      api.post(`/events/${eventId}/ecosystem/volunteers/assignments/${assignmentId}/check-in`, { notes }),
    analytics: (eventId: string) => api.get(`/events/${eventId}/ecosystem/volunteers/analytics`),
  },
  support: {
    list: (eventId: string) => api.get(`/events/${eventId}/ecosystem/support`),
    create: (eventId: string, data: Record<string, unknown>) => api.post(`/events/${eventId}/ecosystem/support`, data),
  },
  intelligence: {
    get: (eventId: string) => api.get(`/events/${eventId}/ecosystem/intelligence`),
    timeline: (eventId: string, page?: number) =>
      api.get(`/events/${eventId}/ecosystem/intelligence/timeline`, { params: { page } }),
  },
};

const requireRegistrationFieldsEventId = (eventId: string) => {
  if (!eventId) {
    throw new Error('Save the draft before editing registration fields.');
  }

  return eventId;
};

/** Organizer registration form builder (CRUD fields). */
export const registrationFieldsApi = {
  list: (eventId: string) =>
    api.get(`/organizer/events/${requireRegistrationFieldsEventId(eventId)}/registration-fields`),
  create: (eventId: string, data: Record<string, unknown>) =>
    api.post(`/organizer/events/${requireRegistrationFieldsEventId(eventId)}/registration-fields`, data),
  update: (eventId: string, fieldId: string, data: Record<string, unknown>) =>
    api.put(
      `/organizer/events/${requireRegistrationFieldsEventId(eventId)}/registration-fields/${fieldId}`,
      data,
    ),
  destroy: (eventId: string, fieldId: string) =>
    api.delete(`/organizer/events/${requireRegistrationFieldsEventId(eventId)}/registration-fields/${fieldId}`),
  reorder: (eventId: string, fieldIds: string[]) =>
    api.put(`/organizer/events/${requireRegistrationFieldsEventId(eventId)}/registration-fields/reorder`, {
      field_ids: fieldIds,
    }),
};

export const collegesApi = {
  list: (params?: any) => api.get('/colleges', { params }),
  search: (query: string) => api.get('/colleges/search', { params: { q: query } }),
  get: (slug: string) => api.get(`/colleges/${slug}`),
  follow: (collegeId: string) => api.post(`/colleges/${collegeId}/follow`),
  unfollow: (collegeId: string) => api.post(`/colleges/${collegeId}/unfollow`),
  announcements: (collegeId: string) => api.get(`/colleges/${collegeId}/announcements`),
  createAnnouncement: (collegeId: string, payload: { content: string }) => api.post(`/colleges/${collegeId}/announcements`, payload),
  updateProfile: (collegeId: string, payload: any) => api.put(`/colleges/${collegeId}/profile`, payload),
};

// Event team: members, roles, committees, invitations, activity (organizer event-scoped)
export const eventTeamApi = {
  members: (eventId: string) => api.get(`/organizer/events/${eventId}/team/members`),
  inviteMember: (
    eventId: string,
    data: {
      email: string;
      name?: string;
      role_id?: string;
      committee_id?: string;
      profile_picture?: string;
      university_id?: string;
      phone?: string;
      designation?: string;
      department?: string;
      linkedin?: string;
    }
  ) => api.post(`/organizer/events/${eventId}/team/members`, data),
  updateMember: (eventId: string, memberId: string, data: { role_id?: string; committee_id?: string; status?: string }) =>
    api.put(`/organizer/events/${eventId}/team/members/${memberId}`, data),
  removeMember: (eventId: string, memberId: string) => api.delete(`/organizer/events/${eventId}/team/members/${memberId}`),
  roles: (eventId: string) => api.get(`/organizer/events/${eventId}/team/roles`),
  createRole: (eventId: string, data: { role_name: string; permissions?: string[] }) =>
    api.post(`/organizer/events/${eventId}/team/roles`, data),
  updateRole: (eventId: string, roleId: string, data: { role_name?: string; permissions?: string[] }) =>
    api.put(`/organizer/events/${eventId}/team/roles/${roleId}`, data),
  deleteRole: (eventId: string, roleId: string) => api.delete(`/organizer/events/${eventId}/team/roles/${roleId}`),
  committees: (eventId: string) => api.get(`/organizer/events/${eventId}/team/committees`),
  getCommittee: (eventId: string, committeeId: string) => api.get(`/organizer/events/${eventId}/team/committees/${committeeId}`),
  createCommittee: (eventId: string, data: { committee_name: string; description?: string }) =>
    api.post(`/organizer/events/${eventId}/team/committees`, data),
  updateCommittee: (eventId: string, committeeId: string, data: { committee_name?: string; description?: string }) =>
    api.put(`/organizer/events/${eventId}/team/committees/${committeeId}`, data),
  deleteCommittee: (eventId: string, committeeId: string) => api.delete(`/organizer/events/${eventId}/team/committees/${committeeId}`),
  addCommitteeMember: (eventId: string, committeeId: string, data: { user_id: string; role_id?: string }) =>
    api.post(`/organizer/events/${eventId}/team/committees/${committeeId}/members`, data),
  removeCommitteeMember: (eventId: string, committeeId: string, userId: string) =>
    api.delete(`/organizer/events/${eventId}/team/committees/${committeeId}/members/${userId}`),
  committeeMessages: (eventId: string, committeeId: string, params?: { page?: number }) =>
    api.get(`/organizer/events/${eventId}/team/committees/${committeeId}/messages`, { params }),
  sendCommitteeMessage: (eventId: string, committeeId: string, data: { message: string; attachment?: string }) =>
    api.post(`/organizer/events/${eventId}/team/committees/${committeeId}/messages`, data),
  invitations: (eventId: string) => api.get(`/organizer/events/${eventId}/team/invitations`),
  activity: (eventId: string, params?: { page?: number }) => api.get(`/organizer/events/${eventId}/team/activity`, { params }),
};

/** Phase 3 — event-scoped team roles, permissions, invitations */
export const eventTeamAccessApi = {
  permissionCatalog: (eventId: string) =>
    api.get(`/organizer/events/${eventId}/team-access/permissions`),
  roles: (eventId: string) => api.get(`/organizer/events/${eventId}/roles`),
  createRole: (eventId: string, data: { role_name: string; description?: string; permissions?: string[]; permission_ids?: string[] }) =>
    api.post(`/organizer/events/${eventId}/roles`, data),
  updateRole: (eventId: string, roleId: string, data: { role_name?: string; description?: string; permissions?: string[]; permission_ids?: string[] }) =>
    api.put(`/organizer/events/${eventId}/roles/${roleId}`, data),
  deleteRole: (eventId: string, roleId: string) => api.delete(`/organizer/events/${eventId}/roles/${roleId}`),
  team: (eventId: string) => api.get(`/organizer/events/${eventId}/team`),
  invite: (eventId: string, data: { email: string; role_id: string; name?: string }) =>
    api.post(`/organizer/events/${eventId}/team/invite`, data),
  updateMember: (eventId: string, memberId: string, data: { role_id?: string; status?: string }) =>
    api.put(`/organizer/events/${eventId}/team/${memberId}`, data),
  removeMember: (eventId: string, memberId: string) => api.delete(`/organizer/events/${eventId}/team/${memberId}`),
  syncMemberPermissions: (eventId: string, memberId: string, data: { grants?: string[]; denies?: string[] }) =>
    api.put(`/organizer/events/${eventId}/team/${memberId}/permissions`, data),
  accessPreview: (eventId: string, memberId: string) =>
    api.get(`/organizer/events/${eventId}/team/${memberId}/access-preview`),
  invitations: (eventId: string) => api.get(`/organizer/events/${eventId}/team/invitations/v2`),
  resendInvitation: (eventId: string, inviteId: string) =>
    api.post(`/organizer/events/${eventId}/team/invitations/${inviteId}/resend`),
  revokeInvitation: (eventId: string, inviteId: string) =>
    api.post(`/organizer/events/${eventId}/team/invitations/${inviteId}/revoke`),
  activityLogs: (eventId: string, params?: { page?: number; per_page?: number }) =>
    api.get(`/organizer/events/${eventId}/team/activity/v2`, { params }),
  acceptInvitation: (token: string) => api.post('/invitations/event/accept', { token }),
  declineInvitation: (token: string) => api.post('/invitations/event/decline', { token }),
  showInvitation: (token: string) => api.get(`/invitations/event/${token}`),
};

/** Phase 4 — permission requests & approval workflow */
export const accessRequestsApi = {
  list: (params?: { mine?: boolean; queue?: boolean; request_type?: string; target_scope_id?: string; status?: string; page?: number }) =>
    api.get('/access/requests', { params }),
  create: (data: Record<string, unknown>) => api.post('/access/requests', data),
  show: (id: string) => api.get(`/access/requests/${id}`),
  update: (id: string, data: Record<string, unknown>) => api.put(`/access/requests/${id}`, data),
  cancel: (id: string) => api.delete(`/access/requests/${id}`),
  approve: (id: string, data?: { note?: string }) => api.post(`/access/requests/${id}/approve`, data),
  reject: (id: string, data?: { reason?: string }) => api.post(`/access/requests/${id}/reject`, data),
  comment: (id: string, data: { message: string; request_changes?: boolean }) =>
    api.post(`/access/requests/${id}/comment`, data),
  assignReviewer: (id: string, reviewerId: string) =>
    api.post(`/access/requests/${id}/assign-reviewer`, { reviewer_id: reviewerId }),
  auditLogs: (params?: { page?: number }) => api.get('/access/audit-logs', { params }),
  listTempAccess: (params?: { mine?: boolean; page?: number }) => api.get('/access/temp-access', { params }),
  createTempAccess: (data: {
    user_id?: string;
    scope_type: string;
    scope_id?: string;
    permission_key: string;
    duration_days?: number;
    duration_preset?: '1d' | '7d' | '30d';
    reason?: string;
  }) => api.post('/access/temp-access', data),
  revokeTempAccess: (id: string) => api.delete(`/access/temp-access/${id}`),
};

/** Phase 3 — admin portal governance (hierarchy-driven requests & temporary grants) */
export const adminGovernanceApi = {
  listPermissionRequests: (params?: { mine?: boolean; queue?: boolean; status?: string; page?: number }) =>
    api.get('/admin/permission-requests', { params }),
  createPermissionRequest: (data: {
    permission_key: string;
    target_user_id?: string;
    reason?: string;
    requested_until?: string;
    duration_days?: number;
  }) => api.post('/admin/permission-requests', data),
  approvePermissionRequest: (id: string, data?: { note?: string; requested_until?: string }) =>
    api.post(`/admin/permission-requests/${id}/approve`, data),
  rejectPermissionRequest: (id: string, data?: { reason?: string }) =>
    api.post(`/admin/permission-requests/${id}/reject`, data),
  listTemporaryGrants: (params?: { mine?: boolean; active_only?: boolean; page?: number }) =>
    api.get('/admin/temporary-grants', { params }),
  revokeTemporaryGrant: (id: string) => api.post(`/admin/temporary-grants/${id}/revoke`),
  getHierarchy: () => api.get('/admin/hierarchy'),
  updateHierarchy: (data: { user_id: string; hierarchy_level?: number; master_admin?: boolean }) =>
    api.patch('/admin/hierarchy', data),
};

/** Phase 4–6 — admin security, notifications, operations, audit */
export const adminProfileApi = {
  get: (userId?: string) =>
    api.get(userId ? `/admin/profile/${userId}` : '/admin/profile'),
  update: (data: { name?: string; timezone?: string | null; avatar?: string | null }, userId?: string) =>
    api.put(userId ? `/admin/profile/${userId}` : '/admin/profile', data),
  security: () => api.get('/admin/profile/security'),
  access: () => api.get('/admin/profile/access'),
  activity: () => api.get('/admin/profile/activity'),
};

export const adminSecurityApi = {
  twoFactorStatus: () => api.get('/admin/security/2fa/status'),
  twoFactorEnable: (force = false) => api.post('/admin/security/2fa/enable', force ? { force: true } : {}),
  twoFactorVerify: (code: string, setupToken?: string | null) => api.post('/admin/security/2fa/verify', { code, setup_token: setupToken }),
  twoFactorDisable: (code: string) => api.post('/admin/security/2fa/disable', { code }),
  twoFactorRecoveryCodes: (code: string) => api.post('/admin/security/2fa/recovery-codes', { code }),
  listSessions: () => api.get('/admin/security/sessions'),
  revokeSession: (id: string) => api.delete(`/admin/security/sessions/${id}`),
  revokeAllSessions: () => api.post('/admin/security/sessions/revoke-all'),
  listDevices: () => api.get('/admin/security/devices'),
  revokeDevice: (id: string) => api.delete(`/admin/security/devices/${id}`),
  listIpAllowlist: () => api.get('/admin/security/ip-allowlist'),
  storeIpAllowlist: (data: { cidr: string; label?: string; enabled?: boolean }) =>
    api.post('/admin/security/ip-allowlist', data),
  destroyIpAllowlist: (id: string) => api.delete(`/admin/security/ip-allowlist/${id}`),
  ipRestrictionStatus: () => api.get('/admin/security/ip-status'),
  listSecurityEvents: (params?: { severity?: string; event_type?: string; from?: string; to?: string; page?: number }) =>
    api.get('/admin/security/events', { params }),
};

export const adminNotificationsApi = {
  list: (params?: { page?: number }) =>
    api.get('/admin/notifications', { params, silent: true }),
  unreadCount: () =>
    api.get('/admin/notifications/unread-count', { silent: true }),
  markRead: (id: string) => api.post(`/admin/notifications/${id}/read`),
  markAllRead: () => api.post('/admin/notifications/read-all'),
};

export const adminOperationsApi = {
  dashboard: () => api.get('/admin/operations/dashboard'),
  observability: () => api.get('/admin/operations/observability'),
  scheduler: () => api.get('/admin/operations/scheduler'),
  mailHealth: () => api.get('/admin/operations/mail-health'),
  notificationDelivery: () => api.get('/admin/operations/notifications'),
};

// Phase 25 — Enterprise Platform Operations API clients
export const adminQueueApi = {
  summary: () => api.get('/admin/queues/summary'),
  failedJobs: (params?: { search?: string; page?: number; per_page?: number }) =>
    api.get('/admin/queues/failed', { params }),
  showFailed: (id: number) => api.get(`/admin/queues/failed/${id}`),
  retryFailed: (id: number) => api.post(`/admin/queues/failed/${id}/retry`),
  destroyFailed: (id: number) => api.delete(`/admin/queues/failed/${id}`),
  retryAll: () => api.post('/admin/queues/failed/retry-all'),
  flush: () => api.post('/admin/queues/failed/flush'),
};

export const adminLogApi = {
  files: () => api.get('/admin/logs/files'),
  read: (params?: { file?: string; lines?: number; search?: string; severity?: string }) =>
    api.get('/admin/logs/read', { params }),
  recentErrors: (params?: { limit?: number }) =>
    api.get('/admin/logs/recent-errors', { params }),
};

export const adminMailApi = {
  listProviders: () => api.get('/admin/communications/mail-providers'),
  reorderProviders: (order: string[]) =>
    api.put('/admin/communications/mail-providers/reorder', { order }),
  updateProvider: (code: string, data: { enabled?: boolean; priority?: number }) =>
    api.put(`/admin/communications/mail-providers/${code}`, data),
  testProvider: (code: string, recipient?: string) =>
    api.post(`/admin/communications/mail-providers/${code}/test`, recipient ? { recipient } : {}),
  providerHealth: (code: string) => api.get(`/admin/communications/mail-providers/${code}/health`),
  healthDashboard: () => api.get('/admin/communications/mail-health'),
  providerSettings: (code: string) => api.get(`/admin/communications/mail-providers/${code}/settings`),
  updateProviderSettings: (code: string, data: Record<string, unknown>) =>
    api.put(`/admin/communications/mail-providers/${code}/settings`, data),
};

export const adminPlatformAuditApi = {
  scan: () => api.get('/admin/platform-audit'),
};

export const adminMonitoringApi = {
  pilotCertification: () => api.get('/admin/pilot-certification'),
  runPilotCertification: (type: string, eventId?: string) =>
    api.post(`/admin/pilot-certification/${type}/run`, eventId ? { event_id: eventId } : {}),
  pilotCertificationReport: (type: string) => api.get(`/admin/pilot-certification/${type}/report`),
  auditCoverage: () => api.get('/admin/audit-coverage'),
  navigationHealth: () => api.get('/admin/navigation-health'),
  crudCertification: () => api.get('/admin/crud-certification'),
  workflowCertification: () => api.get('/admin/workflow-certification'),
  runWorkflowCertification: () => api.post('/admin/workflow-certification/run'),
  launchReadiness: (strict = false) => api.get('/admin/launch-readiness', { params: { strict: strict ? 1 : 0 } }),
  workflowAnalytics: (params?: { days?: number; event_id?: string }) =>
    api.get('/admin/workflow-analytics', { params }),
};

export const adminPlatformOpsApi = {
  platformHealth: () => api.get('/admin/platform-health'),
  productionMonitoring: () => api.get('/admin/production-monitoring'),
  readiness: (strict = false) => api.get('/admin/platform-health/readiness', { params: { strict: strict ? 1 : 0 } }),
  audit: (params?: Record<string, string | number | undefined>) => api.get('/admin/audit', { params }),
  auditExport: (params?: Record<string, string | undefined>) => api.get('/admin/audit/export', { params }),
  securityEvents: (params?: Record<string, string | number | undefined>) =>
    api.get('/admin/security-events', { params }),
  metrics: (params?: Record<string, string | number | boolean | undefined>) =>
    api.get('/admin/metrics', { params }),
  listFeatureFlags: () => api.get('/admin/feature-flags'),
  upsertFeatureFlag: (data: Record<string, unknown>) => api.post('/admin/feature-flags', data),
  governanceOverview: () => api.get('/admin/governance/overview'),
  organizationRisk: (id: string) => api.get(`/admin/governance/organizations/${id}/risk`),
  suspendOrganization: (id: string, reason: string) =>
    api.post(`/admin/governance/organizations/${id}/suspend`, { reason }),
  complianceExport: (params?: Record<string, string | undefined>) =>
    api.get('/admin/governance/compliance-export', { params }),
  reportFrontendError: (data: { message: string; stack?: string; name?: string; request_id?: string; url?: string }) =>
    api.post('/platform/errors', data),
};

export const adminPlatformConfigApi = {
  showSmtp: () => api.get('/admin/platform-config/smtp'),
  updateSmtp: (data: Record<string, unknown>) => api.put('/admin/platform-config/smtp', data),
  testSmtp: (data: Record<string, unknown>) => api.post('/admin/platform-config/smtp/test', data),
  showPayment: () => api.get('/admin/platform-config/payment'),
  updatePayment: (data: Record<string, unknown>) => api.put('/admin/platform-config/payment', data),
  showStorage: () => api.get('/admin/platform-config/storage'),
  updateStorage: (data: Record<string, unknown>) => api.put('/admin/platform-config/storage', data),
  testStorage: (data: Record<string, unknown>) => api.post('/admin/platform-config/storage/test', data),
};

export const checkoutPaymentMethodsApi = {
  list: () => api.get('/checkout-payment-methods'),
};

export const adminCheckoutPaymentMethodsApi = {
  list: () => api.get('/admin/checkout-payment-methods'),
  update: (methods: Record<string, unknown>[]) => api.put('/admin/checkout-payment-methods', { methods }),
};

export const adminPlatformTaxApi = {
  show: () => api.get('/admin/platform-tax-settings'),
  update: (data: Record<string, unknown>) => api.put('/admin/platform-tax-settings', data),
};

// Platform scaling & performance (Phase 7A) — super admin
export const adminPlatformScalingApi = {
  dashboard: () => api.get('/admin/scaling/dashboard'),
  search: (params?: Record<string, string | number | undefined>) => api.get('/admin/scaling/search', { params }),
  reindex: (data: { type: string; limit?: number }) => api.post('/admin/scaling/search/reindex', data),
  regions: () => api.get('/admin/scaling/regions'),
  upsertRegion: (data: Record<string, unknown>) => api.post('/admin/scaling/regions', data),
  costs: () => api.get('/admin/scaling/costs'),
  loadTests: () => api.get('/admin/scaling/load-tests'),
  recordLoadTest: (data: Record<string, unknown>) => api.post('/admin/scaling/load-tests', data),
  queues: () => api.get('/admin/scaling/queues'),
};

export const platformSearchApi = {
  search: (params?: { q?: string; type?: string; page?: number; per_page?: number; organization_id?: string }) =>
    api.get('/search', { params }),
};

// Enterprise security & compliance (Phase 7B) — super admin
export const adminEnterpriseApi = {
  security: () => api.get('/admin/security'),
  compliance: (params?: Record<string, string | number | boolean | undefined>) =>
    api.get('/admin/compliance', { params }),
  createChallenge: (data: { action: string; organization_id?: string }) =>
    api.post('/admin/security/challenge', data),
  verifyChallenge: (data: { challenge_id: string; token: string; action: string }) =>
    api.post('/admin/security/challenge/verify', data),
  tenantRisk: (params?: Record<string, string | number | undefined>) =>
    api.get('/admin/tenants/risk', { params }),
  privacyExport: (data: { organization_id: string; subject_user_id?: string; format?: 'json' | 'csv' }) =>
    api.post('/admin/compliance/privacy-export', data),
  disasterRecovery: () => api.get('/admin/disaster-recovery'),
};

// Marketplace & growth (Phase 8A)
export const marketplaceApi = {
  listApps: () => api.get('/marketplace/apps'),
  install: (data: { app_id: string; permissions?: string[] }) => api.post('/marketplace/install', data),
  uninstall: (data: { install_id: string }) => api.post('/marketplace/uninstall', data),
  enablePlugin: (installId: string) => api.post(`/marketplace/installs/${installId}/enable`),
  disablePlugin: (installId: string) => api.post(`/marketplace/installs/${installId}/disable`),
  registerHook: (installId: string, hook: string) =>
    api.post(`/marketplace/installs/${installId}/hooks`, { hook }),
  listPartners: () => api.get('/partners'),
  invitePartner: (data: Record<string, unknown>) => api.post('/partners/invite', data),
  listTemplates: (params?: { type?: string }) => api.get('/templates', { params }),
  saveTemplate: (data: Record<string, unknown>) => api.post('/templates', data),
  duplicateTemplate: (id: string) => api.post(`/templates/${id}/duplicate`),
  growthMetrics: () => api.get('/growth/metrics'),
  createReferral: (data?: { email?: string }) => api.post('/growth/referrals', data ?? {}),
  branding: () => api.get('/growth/branding'),
  upsertBranding: (data: Record<string, unknown>) => api.post('/growth/branding', data),
};

export const adminMarketplaceApi = {
  overview: () => api.get('/admin/marketplace/overview'),
  pendingApps: () => api.get('/admin/marketplace/apps/pending'),
  approveApp: (id: string) => api.post(`/admin/marketplace/apps/${id}/approve`),
  rejectApp: (id: string, reason?: string) => api.post(`/admin/marketplace/apps/${id}/reject`, { reason }),
  pendingPartners: () => api.get('/admin/marketplace/partners/pending'),
  approvePartner: (id: string) => api.post(`/admin/marketplace/partners/${id}/approve`),
  organizationMetrics: (organizationId: string) =>
    api.get('/admin/marketplace/organizations/metrics', { params: { organization_id: organizationId } }),
  seedCatalog: () => api.post('/admin/marketplace/catalog/seed'),
};

// Release engineering & production hardening (Phase 8B) — super admin
export const adminReleaseOpsApi = {
  dashboard: () => api.get('/admin/release-ops/dashboard'),
  createRelease: (data: Record<string, unknown>) => api.post('/admin/release-ops/releases', data),
  validateRelease: (id: string) => api.post(`/admin/release-ops/releases/${id}/validate`),
  deployRelease: (id: string) => api.post(`/admin/release-ops/releases/${id}/deploy`),
  rollbackRelease: (id: string, targetVersion: string) =>
    api.post(`/admin/release-ops/releases/${id}/rollback`, { target_version: targetVersion }),
  runQualityGates: () => api.post('/admin/release-ops/quality-gates'),
  runSmokeTests: (phase?: string) => api.post('/admin/release-ops/smoke-tests', null, { params: { phase } }),
  listIncidents: () => api.get('/admin/release-ops/incidents'),
  openIncident: (data: Record<string, unknown>) => api.post('/admin/release-ops/incidents', data),
  resolveIncident: (id: string, resolution: string) =>
    api.post(`/admin/release-ops/incidents/${id}/resolve`, { resolution }),
  migrationStatus: () => api.get('/admin/release-ops/migrations/status'),
  killSwitch: (flagKey: string) => api.post('/admin/release-ops/feature-flags/kill-switch', { flag_key: flagKey }),
  operationalDocs: () => api.get('/admin/release-ops/docs'),
};

export const adminAuditApi = {
  list: (params?: Record<string, string | number | undefined>) => api.get('/admin/audit/logs', { params }),
  export: (params?: { format?: 'json' | 'csv' } & Record<string, string | undefined>) =>
    api.get('/admin/audit/export', { params }),
  verify: () => api.get('/admin/audit/verify'),
  retentionPolicies: () => api.get('/admin/audit/retention'),
  listSavedFilters: () => api.get('/admin/audit/saved-filters'),
  storeSavedFilter: (data: { name: string; filters: Record<string, unknown> }) =>
    api.post('/admin/audit/saved-filters', data),
  destroySavedFilter: (id: string) => api.delete(`/admin/audit/saved-filters/${id}`),
};

/** Emergency master admin recovery (public, no auth) */
export const masterAdminRecoveryApi = {
  validate: (data: { token: string; exp: number; sig: string }) =>
    api.post('/admin/master-admin-recovery/validate', data),
  confirm: (data: { token: string; exp: number; sig: string }) =>
    api.post('/admin/master-admin-recovery/confirm', data),
};

// Event promo codes (organizer: list/create; auth: validate at checkout)
export const eventPromoCodesApi = {
  list: (eventId: string) => api.get<{ success: boolean; data: EventPromoCode[] }>(`/organizer/events/${eventId}/promo-codes`),
  create: (eventId: string, data: {
    code: string;
    discount_percentage: number;
    referral_code?: string;
    max_uses?: number;
    valid_from?: string;
    valid_until?: string;
  }) => api.post(`/organizer/events/${eventId}/promo-codes`, data),
  validate: (eventIdOrSlug: string, data: { code: string; amount: number; ticket_id?: string }) =>
    api.post<{ success: boolean; data?: { discount_percentage: number; original_amount: number; discounted_amount: number; discount_amount: number } }>(
      `/events/${eventIdOrSlug}/promo-codes/validate`,
      data
    ),
};
export type EventPromoCode = {
  id: string;
  event_id: string;
  code: string;
  discount_percentage: number;
  referral_code?: string | null;
  usage_count: number;
  max_uses?: number | null;
  valid_from?: string | null;
  valid_until?: string | null;
  created_at: string;
  updated_at: string;
};

// Event announcements (organizer event-scoped)
export const eventAnnouncementsApi = {
  list: (eventId: string, params?: { pinned_only?: boolean; sent_only?: boolean }) =>
    api.get(`/organizer/events/${eventId}/announcements`, { params }),
  create: (eventId: string, data: {
    title: string;
    message: string;
    audience_type: string;
    is_pinned?: boolean;
    scheduled_at?: string;
    post_to_event_chat?: boolean;
    target_ids?: string[];
  }) => api.post(`/organizer/events/${eventId}/announcements`, data),
};

// Pitch competition (organizer event-scoped + public leaderboard)
export const pitchApi = {
  submissions: (eventId: string) => api.get(`/organizer/events/${eventId}/pitch/submissions`),
  createSubmission: (eventId: string, data: any) => api.post(`/organizer/events/${eventId}/pitch/submissions`, data),
  updateSubmission: (eventId: string, submissionId: string, data: { status?: string; judge_notes?: string }) =>
    api.put(`/organizer/events/${eventId}/pitch/submissions/${submissionId}`, data),
  judges: (eventId: string) => api.get(`/organizer/events/${eventId}/pitch/judges`),
  addJudge: (eventId: string, data: { user_id: string; role?: string }) =>
    api.post(`/organizer/events/${eventId}/pitch/judges`, data),
  inviteJudge: (eventId: string, data: { email: string; name?: string; role?: string }) =>
    api.post(`/organizer/events/${eventId}/pitch/judges/invite`, data),
  removeJudge: (eventId: string, judgeId: string) => api.delete(`/organizer/events/${eventId}/pitch/judges/${judgeId}`),
  submitScore: (eventId: string, submissionId: string, data: {
    innovation_score: number;
    market_score: number;
    technology_score: number;
    presentation_score: number;
    scalability_score: number;
    comments?: string;
  }) => api.post(`/organizer/events/${eventId}/pitch/submissions/${submissionId}/score`, data),
  leaderboard: (eventIdOrSlug: string) => api.get(`/events/${eventIdOrSlug}/pitch-leaderboard`),
};

// Event sessions (organizer: CRUD; public: list by slug)
export const eventSessionsApi = {
  list: (eventId: string) => api.get(`/organizer/events/${eventId}/sessions`),
  create: (eventId: string, data: any) => api.post(`/organizer/events/${eventId}/sessions`, data),
  update: (eventId: string, sessionId: string, data: any) =>
    api.put(`/organizer/events/${eventId}/sessions/${sessionId}`, data),
  delete: (eventId: string, sessionId: string) => api.delete(`/organizer/events/${eventId}/sessions/${sessionId}`),
  publicList: (eventSlug: string) => api.get(`/events/${eventSlug}/sessions`),
  show: (eventIdOrSlug: string, sessionId: string) => api.get(`/events/${eventIdOrSlug}/sessions/${sessionId}`),
};

// Event mentors / guest speakers (organizer event-scoped)
export const eventMentorsApi = {
  list: (eventId: string) => api.get(`/organizer/events/${eventId}/mentors`),
  create: (eventId: string, data: Record<string, unknown>) => api.post(`/organizer/events/${eventId}/mentors`, data),
  update: (eventId: string, mentorId: string, data: Record<string, unknown>) =>
    api.put(`/organizer/events/${eventId}/mentors/${mentorId}`, data),
  delete: (eventId: string, mentorId: string) => api.delete(`/organizer/events/${eventId}/mentors/${mentorId}`),
};

// Session live Q&A & polls (public read; auth for submit/upvote/vote; organizer for moderate)
export const sessionLiveApi = {
  questions: (eventIdOrSlug: string, sessionId: string, params?: { status?: string }) =>
    api.get(`/events/${eventIdOrSlug}/sessions/${sessionId}/questions`, { params }),
  submitQuestion: (eventIdOrSlug: string, sessionId: string, question: string) =>
    api.post(`/events/${eventIdOrSlug}/sessions/${sessionId}/questions`, { question }),
  upvote: (eventIdOrSlug: string, sessionId: string, questionId: string) =>
    api.post(`/events/${eventIdOrSlug}/sessions/${sessionId}/questions/${questionId}/upvote`),
  updateQuestion: (eventId: string, sessionId: string, questionId: string, data: { status?: string; answer?: string }) =>
    api.put(`/organizer/events/${eventId}/sessions/${sessionId}/questions/${questionId}`, data),
  polls: (eventIdOrSlug: string, sessionId: string) =>
    api.get(`/events/${eventIdOrSlug}/sessions/${sessionId}/polls`),
  pollResults: (eventIdOrSlug: string, sessionId: string, pollId: string) =>
    api.get(`/events/${eventIdOrSlug}/sessions/${sessionId}/polls/${pollId}/results`),
  createPoll: (eventId: string, sessionId: string, data: { question: string; options: string[] }) =>
    api.post(`/organizer/events/${eventId}/sessions/${sessionId}/polls`, data),
  closePoll: (eventId: string, sessionId: string, pollId: string) =>
    api.put(`/organizer/events/${eventId}/sessions/${sessionId}/polls/${pollId}/close`),
  votePoll: (eventIdOrSlug: string, sessionId: string, pollId: string, optionIndex: number) =>
    api.post(`/events/${eventIdOrSlug}/sessions/${sessionId}/polls/${pollId}/vote`, { option_index: optionIndex }),
};

// Multi-event passes (organizer: CRUD; auth: purchase, my orders)
export const eventPassesApi = {
  list: () => api.get('/organizer/passes'),
  create: (data: { pass_name: string; description?: string; price: number; currency?: string; valid_event_ids: string[] }) =>
    api.post('/organizer/passes', data),
  update: (id: string, data: any) => api.put(`/organizer/passes/${id}`, data),
  delete: (id: string) => api.delete(`/organizer/passes/${id}`),
  listForEvent: (eventSlug: string) => api.get(`/events/${eventSlug}/passes`),
  purchase: (passId: string) => api.post(`/passes/${passId}/purchase`),
  myOrders: () => api.get('/passes/my-orders'),
};

// Timeline API (organizer event-scoped)
export const timelineApi = {
  get: (eventId: string) => api.get(`/organizer/events/${eventId}/timeline`),
  update: (eventId: string, data: { name?: string; description?: string }) => api.put(`/organizer/events/${eventId}/timeline`, data),
  reorderStages: (eventId: string, stageIds: string[]) => api.put(`/organizer/events/${eventId}/timeline/reorder`, { stage_ids: stageIds }),
  currentStage: (eventId: string) => api.get(`/organizer/events/${eventId}/timeline/current-stage`),
  createStage: (eventId: string, data: any) => api.post(`/organizer/events/${eventId}/timeline/stages`, data),
  updateStage: (eventId: string, stageId: string, data: any) => api.put(`/organizer/events/${eventId}/timeline/stages/${stageId}`, data),
  deleteStage: (eventId: string, stageId: string) => api.delete(`/organizer/events/${eventId}/timeline/stages/${stageId}`),
  createAction: (eventId: string, stageId: string, data: any) => api.post(`/organizer/events/${eventId}/timeline/stages/${stageId}/actions`, data),
  updateAction: (eventId: string, stageId: string, actionId: string, data: any) => api.put(`/organizer/events/${eventId}/timeline/stages/${stageId}/actions/${actionId}`, data),
  deleteAction: (eventId: string, stageId: string, actionId: string) => api.delete(`/organizer/events/${eventId}/timeline/stages/${stageId}/actions/${actionId}`),
  createReminder: (eventId: string, stageId: string, data: { minutes_before: number; message: string }) => api.post(`/organizer/events/${eventId}/timeline/stages/${stageId}/reminders`, data),
  updateReminder: (eventId: string, stageId: string, reminderId: string, data: any) => api.put(`/organizer/events/${eventId}/timeline/stages/${stageId}/reminders/${reminderId}`, data),
  deleteReminder: (eventId: string, stageId: string, reminderId: string) => api.delete(`/organizer/events/${eventId}/timeline/stages/${stageId}/reminders/${reminderId}`),
  stageLogs: (eventId: string, stageId: string, params?: any) => api.get(`/organizer/events/${eventId}/timeline/stages/${stageId}/logs`, { params }),
};

// Public event schedule (participants)
export const scheduleApi = {
  get: (eventSlug: string) => api.get(`/events/${eventSlug}/schedule`),
};

// Settlements API (organizer)
export const settlementsApi = {
  // Backend routes are mounted under /organizer/settlements
  list: (params?: any) => api.get('/organizer/settlements', { params }),
  summary: () => api.get('/organizer/settlements/summary'),
  request: (data: { type: string; amount: number }) => api.post('/organizer/settlements', data),
};

// Organizer wallet & payouts (Laravel: /organizer/wallet, /organizer/payout-requests)
export const organizerWalletApi = {
  getWallet: () => api.get('/organizer/wallet'),
  getPayoutRequests: (params?: { limit?: number; offset?: number }) =>
    api.get('/organizer/payout-requests', { params }),
  requestPayout: (data: { amount: number; payment_method?: string }) =>
    api.post('/organizer/payout-requests', data),
};

// Email templates API (organizer)
export const emailTemplatesApi = {
  list: () => api.get('/organizer/email-templates'),
  get: (slug: string) => api.get(`/organizer/email-templates/${slug}`),
  save: (data: { slug: string; name: string; subject: string; body_html?: string; body_text?: string }) => api.post('/organizer/email-templates', data),
  update: (id: string, data: any) => api.put(`/organizer/email-templates/${id}`, data),
};

// Registrations API
export const registrationsApi = {
  list: (params?: { page?: number; per_page?: number }) =>
    api.get('/registrations', { params, cacheTtlMs: 30000 }),
  get: (id: string) => api.get(`/registrations/${id}`),
  getCheckoutForm: (eventId: string, ticketIds: string[], activityIds?: string[]) =>
    api.get<{
      success: boolean;
      data: {
        form_id?: string;
        form?: { id: string; name: string; type: string };
        fields: Array<{
          id: string;
          form_id?: string;
          label: string;
          slug: string;
          type: string;
          required: boolean;
          options?: any;
          placeholder?: string;
          help_text?: string;
          is_system?: boolean;
          sort_order?: number;
        }>;
        ticket_ids: string[];
        source: string;
      };
    }>(`/events/${eventId}/checkout/registration-form`, {
      params: {
        ticket_ids: ticketIds,
        ticket_id: ticketIds[0],
        activity_ids: activityIds && activityIds.length > 0 ? activityIds : undefined,
      },
    }),
  validate: (eventId: string, data: Record<string, unknown>) =>
    api.post<{
      success: boolean;
      message?: string;
      code?: string;
      details?: Record<string, unknown>;
    }>(`/events/${eventId}/register/validate`, { ...data, soft_validate: true }),
  register: async (eventId: string, data: any, options?: { idempotencyKey?: string }) => {
    const payload = data ?? {};
    const idempotencyKey =
      options?.idempotencyKey ||
      (typeof payload.idempotency_key === 'string' && payload.idempotency_key ? payload.idempotency_key : undefined);
    const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined;

    const attempts = [
      `/registrations/events/${eventId}/register`,
      `/events/${eventId}/register`,
      `/events/${eventId}/registrations`,
      `/registrations/${eventId}/register`,
    ];

    let lastError: any = null;
    for (const endpoint of attempts) {
      try {
        return await api.post(endpoint, payload, headers ? { headers } : undefined);
      } catch (error: any) {
        lastError = error;
        const status = error?.response?.status;
        // Try next endpoint only for route-mismatch style errors.
        if (status !== 404 && status !== 405) {
          throw error;
        }
      }
    }

    throw lastError ?? new Error('Registration request failed');
  },
  cancel: (id: string) => api.delete(`/registrations/${id}`),
};

// Refund requests (participant)
export const refundRequestsApi = {
  list: () => api.get('/refund-requests', { cacheTtlMs: 30000 }),
  create: (data: { registration_id: string; reason: string; description?: string }) => api.post('/refund-requests', data),
};

// Tickets API
export const ticketsApi = {
  get: (id: string) => api.get(`/tickets/${id}`),
  getQr: (id: string) => api.get(`/tickets/${id}/qr`, { responseType: 'blob' }),
  downloadPdf: (id: string) => api.get(`/tickets/${id}/pdf`, { responseType: 'blob' }),
  verify: (token: string) => api.get('/tickets/verify', { params: { token } }),
};

// Ticket templates API (organizer)
export const ticketTemplatesApi = {
  getForEvent: (eventId: string) =>
    api.get(`/organizer/events/${eventId}/ticket-templates`),
  save: (payload: {
    id?: string;
    event_id: string;
    organizer_id: string;
    name: string;
    layout: string;
    colors: Record<string, string>;
    fonts: Record<string, string>;
    background_type: string;
    background_image_url?: string | null;
    banner_image_url?: string | null;
    organizer_logo_url?: string | null;
    sections: Record<string, boolean>;
    custom_fields: Array<{ key: string; label: string; required: boolean }>;
    terms_text?: string | null;
  }) => api.post(`/organizer/events/${payload.event_id}/ticket-templates`, payload),
};

// Invoices API
export const invoicesApi = {
  get: (id: string) => api.get(`/invoices/${id}`),
  downloadPdf: (id: string) =>
    api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  email: (id: string) => api.post(`/invoices/${id}/email`),
};

export const workshopApi = {
  // Legacy stubs
  listSessions: (eventId: string) => api.get('/workshop/sessions', { params: { event_id: eventId } }),
  checkin: (sessionId: string) => api.post('/workshop/checkin', { session_id: sessionId }),
  checkout: (sessionId: string) => api.post('/workshop/checkout', { session_id: sessionId }),

  // Full Workshop Workspace APIs (Participant)
  participantWorkspace: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, any> }>(`/activities/${activityId}/workshop/participant`),
  submitAssignment: (activityId: string, formData: FormData) =>
    api.post<{ success: boolean; message?: string; data: Record<string, any> }>(
      `/activities/${activityId}/workshop/assignment-submit`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ),
  checkInSession: (activityId: string, sessionId: string) =>
    api.post<{ success: boolean; message?: string; already_checked_in?: boolean; record?: any }>(
      `/activities/${activityId}/workshop/check-in`,
      { session_id: sessionId }
    ),
  askQuestion: (activityId: string, data: { question: string; session_id?: string }) =>
    api.post<{ success: boolean; message?: string; data: any }>(
      `/activities/${activityId}/workshop/questions`,
      data
    ),
  upvoteQuestion: (activityId: string, questionId: string) =>
    api.post<{ success: boolean; question_id: string }>(
      `/activities/${activityId}/workshop/questions/${questionId}/upvote`
    ),
  submitFeedback: (activityId: string, data: { rating: number; comment?: string; nps_score?: number }) =>
    api.post<{ success: boolean; message?: string; data: any }>(
      `/activities/${activityId}/workshop/feedback`,
      data
    ),

  // Organizer Workshop APIs
  organizerWorkspace: (activityId: string) =>
    api.get<{ success: boolean; data: Record<string, any> }>(`/activities/${activityId}/workshop/organizer`),
  createSession: (activityId: string, data: Record<string, any>) =>
    api.post<{ success: boolean; data: any }>(`/activities/${activityId}/workshop/sessions`, data),
  updateSession: (activityId: string, sessionId: string, data: Record<string, any>) =>
    api.put<{ success: boolean; data: any }>(`/activities/${activityId}/workshop/sessions/${sessionId}`, data),
  deleteSession: (activityId: string, sessionId: string) =>
    api.delete<{ success: boolean; message?: string }>(`/activities/${activityId}/workshop/sessions/${sessionId}`),
  createAssignment: (activityId: string, data: Record<string, any>) =>
    api.post<{ success: boolean; data: any }>(`/activities/${activityId}/workshop/assignments`, data),
  gradeSubmission: (activityId: string, submissionId: string, data: { score: number; feedback?: string; status?: string; resubmission_allowed?: boolean }) =>
    api.post<{ success: boolean; data: any }>(`/activities/${activityId}/workshop/submissions/${submissionId}/grade`, data),
  answerQuestion: (activityId: string, questionId: string, data: { answer: string }) =>
    api.post<{ success: boolean; data: any }>(`/activities/${activityId}/workshop/questions/${questionId}/answer`, data),
  updateSettings: (activityId: string, data: Record<string, any>) =>
    api.put<{ success: boolean; data: any }>(`/activities/${activityId}/workshop/settings`, data),
};

// Scanner API
export const scannerApi = {
  getEvents: () => api.get('/scanner/events'),
  getGates: (eventId: string) => api.get(`/organizer/events/${eventId}/gates`),
  sync: (data: any) => api.post('/scanner/sync', data),
  checkin: (data: { event_id: string; attendance_session_id?: string; token?: string; qr_data?: string; ticket_number?: string }) =>
    api.post('/scanner/checkin', data),
  getSessions: (eventId: string) => api.get('/scanner/sessions', { params: { event_id: eventId } }),
  createSession: (data: { event_id: string; name: string; notes?: string }) => api.post('/scanner/sessions', data),
  closeSession: (eventId: string, sessionId: string) =>
    api.post(`/scanner/sessions/${sessionId}/close`, { event_id: eventId }),
  getStats: (eventId: string) => api.get('/scanner/stats', { params: { event_id: eventId } }),
  getRecent: (eventId: string) => api.get('/scanner/recent', { params: { event_id: eventId } }),
};

// Participant dashboard (my submissions, my teams, submission management)
export const participantApi = {
  dashboard: () => api.get('/participant/dashboard', { cacheTtlMs: 30000 }),
  mySubmissions: () => api.get('/my-submissions', { cacheTtlMs: 30000 }),
  myTeams: () => api.get('/my-teams', { cacheTtlMs: 30000 }),
  allSubmissions: () => api.get('/participant/submissions'),
  eventSubmissions: (eventId: string) => api.get(`/events/${eventId}/participant/submissions`),
  getSubmission: (eventId: string, submissionId: string) => api.get(`/events/${eventId}/participant/submissions/${submissionId}`),
  createSubmission: (eventId: string, data: any) => api.post(`/events/${eventId}/participant/submissions`, data),
  updateSubmission: (eventId: string, submissionId: string, data: any) => api.put(`/events/${eventId}/participant/submissions/${submissionId}`, data),
  finalSubmit: (eventId: string, submissionId: string) => api.post(`/events/${eventId}/participant/submissions/${submissionId}/final-submit`),
  uploadSubmissionFile: (eventId: string, submissionId: string, formData: FormData) => api.post(`/events/${eventId}/participant/submissions/${submissionId}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteSubmissionFile: (eventId: string, submissionId: string, fileId: string) => api.delete(`/events/${eventId}/participant/submissions/${submissionId}/files/${fileId}`),
  getSubmissionDownloadUrl: (eventId: string, submissionId: string, fileId: string) => `/api/events/${eventId}/participant/submissions/${submissionId}/files/${fileId}/download`,
};

// Jury API (judge)
export const juryApi = {
  dashboard: () => api.get('/jury/dashboard', { cacheTtlMs: 30000 }),
  events: () => api.get('/jury/events', { cacheTtlMs: 30000 }),
  submissions: (eventId: string) => api.get(`/jury/events/${eventId}/submissions`),
  eventSummary: (eventId: string) => api.get(`/jury/events/${eventId}/summary`),
  myScores: (eventId: string) => api.get(`/jury/events/${eventId}/my-scores`),
  submitScore: (eventId: string, submissionId: string, data: any) =>
    api.post(`/jury/events/${eventId}/submissions/${submissionId}/score`, data),
  acceptInvitation: (data: { token: string }) => api.post('/jury/invitations/accept', data),
  rejectInvitation: (data: { token: string }) => api.post('/jury/invitations/reject', data),
};

// Public organization profile
export const organizersPublicApi = {
  getBySlug: (slug: string) => api.get(`/organizers/${slug}`),
};

export const organizationsApi = {
  getBySlug: (slug: string) => api.get(`/organizations/${slug}`),
};

// Event visibility & secure access (Phase 3C)
export const eventAccessApi = {
  generateLink: (eventId: string, data?: { expires_at?: string; max_uses?: number }) =>
    api.post(`/events/${eventId}/generate-link`, data),
  accessLinks: (eventId: string) => api.get(`/events/${eventId}/access-links`),
  disableLink: (eventId: string, tokenOrLinkId: string) =>
    api.delete(`/events/${eventId}/link/${tokenOrLinkId}`),
  regenerateLink: (eventId: string, linkId: string, data?: { expires_at?: string; max_uses?: number }) =>
    api.post(`/events/${eventId}/link/${linkId}/regenerate`, data),
  verifyAccess: (eventId: string, data: { email?: string; access_token?: string; verification_token?: string }) =>
    api.post(`/events/${eventId}/verify-access`, data),
  accessByToken: (token: string) => api.get(`/events/access/${token}`),
  updateDomains: (
    eventId: string,
    data: {
      allowed_email_domains: string[];
      strict_domain_mode?: boolean;
      event_visibility?: 'public' | 'unlisted' | 'private' | 'organization_only';
    }
  ) => api.post(`/events/${eventId}/domains`, data),
  accessAttempts: (eventId: string, params?: { page?: number; per_page?: number }) =>
    api.get(`/events/${eventId}/access-attempts`, { params }),
};

// Event lifecycle operations (Phase 3B)
export const eventLifecycleApi = {
  publish: (eventId: string) => api.post(`/events/${eventId}/publish`),
  archive: (eventId: string) => api.post(`/events/${eventId}/archive`),
  cancel: (eventId: string, reason?: string) => api.post(`/events/${eventId}/cancel`, { reason }),
  duplicate: (eventId: string) => api.post(`/events/${eventId}/duplicate`),
  transfer: (
    eventId: string,
    data: { owner_type: string; owner_id: string; organizer_id?: string }
  ) => api.post(`/events/${eventId}/transfer`, data),
  approve: (eventId: string, comment?: string) => api.post(`/events/${eventId}/approve`, { comment }),
  reject: (eventId: string, comment?: string) => api.post(`/events/${eventId}/reject`, { comment }),
  suspend: (eventId: string) => api.post(`/events/${eventId}/suspend`),
  restore: (eventId: string) => api.post(`/events/${eventId}/restore`),
  history: (eventId: string, params?: { page?: number; per_page?: number }) =>
    api.get(`/events/${eventId}/history`, { params }),
  notifications: (eventId: string, params?: { page?: number; per_page?: number }) =>
    api.get(`/events/${eventId}/notifications`, { params }),
};

// Analytics & reporting (Phase 5B)
export const analyticsApi = {
  organization: (params?: {
    from?: string;
    to?: string;
    organizer_id?: string;
    department_id?: string;
    event_type?: string;
    status?: string;
    currency?: string;
  }) => api.get('/analytics/organization', { params }),
  event: (eventId: string, params?: { from?: string; to?: string }) =>
    api.get(`/analytics/events/${eventId}`, { params }),
  organizers: (params?: { from?: string; to?: string; organizer_id?: string }) =>
    api.get('/analytics/organizers', { params }),
  funnels: (eventId: string, params?: { from?: string; to?: string }) =>
    api.get('/analytics/funnels', { params: { event_id: eventId, ...params } }),
  finance: (params?: { from?: string; to?: string; currency?: string }) =>
    api.get('/analytics/finance', { params }),
  export: (data: {
    type?: 'analytics' | 'finance' | 'communications' | 'attendance' | 'operations';
    format?: 'csv' | 'xlsx' | 'pdf' | 'json';
    filters?: Record<string, unknown>;
    async?: boolean;
  }) => api.post('/analytics/export', data, { responseType: data.format === 'json' ? 'json' : 'blob' }),
  reports: (data?: {
    name?: string;
    type?: string;
    format?: string;
    metrics?: string[];
    filters?: Record<string, unknown>;
    save_template?: boolean;
    schedule?: string;
    run_now?: boolean;
    preview?: boolean;
  }) => (data ? api.post('/analytics/reports', data) : api.get('/analytics/reports')),
};

// Developer portal & integrations (Phase 6A)
export const developerApi = {
  dashboard: () => api.get('/developer/dashboard'),
  permissions: () => api.get('/developer/permissions'),
  listApiKeys: () => api.get('/developer/api-keys'),
  createApiKey: (data: { name: string; permissions: string[]; expires_at?: string }) =>
    api.post('/developer/api-keys', data),
  revokeApiKey: (id: string) => api.delete(`/developer/api-keys/${id}`),
  rotateApiKey: (id: string) => api.post(`/developer/api-keys/${id}/rotate`),
  listWebhooks: () => api.get('/developer/webhooks'),
  createWebhook: (data: { url: string; events: string[]; max_retries?: number }) =>
    api.post('/developer/webhooks', data),
  deleteWebhook: (id: string) => api.delete(`/developer/webhooks/${id}`),
  usageLogs: (params?: { page?: number; per_page?: number }) =>
    api.get('/developer/usage-logs', { params }),
  docs: () => api.get('/v1/docs'),
};

// Event execution operations (Phase 5A)
export const eventExecutionApi = {
  operations: (eventId: string, params?: { search?: string }) =>
    api.get(`/events/${eventId}/operations`, { params }),
  attendance: (eventId: string) => api.get(`/events/${eventId}/attendance`),
  checkin: (
    eventId: string,
    data: {
      registration_id?: string;
      registration_ids?: string[];
      token?: string;
      qr_data?: string;
      attendance_session_id?: string;
      checkin_method?: string;
      idempotency_key?: string;
      device_info?: Record<string, unknown>;
    }
  ) => api.post(`/events/${eventId}/checkin`, data),
  scan: (eventId: string, data: { token?: string; qr_data?: string }) =>
    api.post(`/events/${eventId}/scan`, data),
  assignStaff: (
    eventId: string,
    data: { user_id: string; role: string; expires_at?: string; permissions?: string[] }
  ) => api.post(`/events/${eventId}/staff`, data),
  reportIncident: (eventId: string, data: { type: string; severity?: string; notes?: string }) =>
    api.post(`/events/${eventId}/incident`, data),
  badge: (eventId: string, registrationId: string) =>
    api.get(`/events/${eventId}/badges/${registrationId}`),
  badgesExport: (eventId: string, type: 'attendee' | 'staff' = 'attendee') =>
    api.get(`/events/${eventId}/badges-export`, { params: { type } }),
};

// Revenue operations (Phase 4A)
export type EventFinancialsData = {
  event_id: string;
  currency: string;
  platform_fee_type: 'percent' | 'flat';
  platform_fee_value: number;
  tax_percentage: number;
  refund_policy_type: string;
  refund_deadline: string | null;
  allow_partial_refunds: boolean;
  gross_revenue: number;
  net_revenue: number;
  platform_commission: number;
  refund_total: number;
  payout_total: number;
  pending_payout_balance: number;
  paid_payout_balance: number;
  remaining_balance: number;
  payout_eligible: boolean;
  payout_eligible_at: string | null;
  kyc_approved: boolean;
  kyc_settlement_blocked: boolean;
  kyc_settlement_warning?: string | null;
  settlement_readiness?: Record<string, unknown>;
  revenue_splits?: unknown;
};

export const eventFinanceApi = {
  financials: (eventId: string) => api.get<{ success: boolean; data: EventFinancialsData }>(`/events/${eventId}/financials`),
  updateConfig: (
    eventId: string,
    data: {
      platform_fee_type?: 'percent' | 'flat';
      platform_fee_value?: number;
      tax_percentage?: number;
      currency?: string;
      refund_policy_type?: string;
      refund_deadline?: string | null;
      allow_partial_refunds?: boolean;
    }
  ) => api.put(`/events/${eventId}/financials`, data),
  requestPayout: (eventId: string, data: { amount: number; notes?: string }) =>
    api.post(`/events/${eventId}/request-payout`, data),
  issueRefund: (eventId: string, data: { registration_id: string; amount?: number; reason?: string }) =>
    api.post(`/events/${eventId}/refund`, data),
};

export const financeApi = {
  payouts: (params?: { page?: number; per_page?: number }) => api.get('/payouts', { params }),
};

// Organization Dashboard API (organization_admin | organization_manager)
export const organizationApi = {
  dashboard: () => api.get('/organization/dashboard'),
  finance: () => api.get('/organization/finance'),
  revenueRules: (params?: { event_id?: string }) => api.get('/organization/revenue-rules', { params }),
  saveRevenueRules: (data: {
    splits: Array<{ recipient_type: string; organizer_id?: string; percent: number }>;
    event_id?: string;
  }) => api.post('/organization/revenue-rules', data),
  events: (params?: {
    department_id?: string;
    club_id?: string;
    status?: string;
    approval_status?: string;
    assignment_role?: string;
    event_owner_type?: string;
    organizer_id?: string;
    include_archived?: boolean;
    q?: string;
    page?: number;
    per_page?: number;
  }) => api.get('/organization/events', { params }),
  bulkEvents: (data: {
    action: 'publish' | 'archive' | 'cancel' | 'suspend' | 'delete_draft' | 'assign';
    event_ids: string[];
    reason?: string;
    assignments?: Array<{ organizer_id: string; role?: string }>;
  }) => api.post('/organization/events/bulk', data),
  eventAssignments: (eventId: string) => api.get(`/organization/events/${eventId}/assignments`),
  assignOrganizers: (
    eventId: string,
    assignments: Array<{ organizer_id: string; role?: 'lead' | 'co_host' | 'support' }>
  ) => api.post(`/events/${eventId}/assign-organizers`, { assignments }),
  updateOrganizerAssignment: (
    eventId: string,
    organizerId: string,
    role: 'lead' | 'co_host' | 'support'
  ) => api.patch(`/events/${eventId}/assign-organizers/${organizerId}`, { role }),
  removeOrganizerAssignment: (eventId: string, organizerId: string) =>
    api.delete(`/events/${eventId}/assign-organizers/${organizerId}`),
  eventOrganizers: (eventId: string, params?: { page?: number; per_page?: number }) =>
    api.get(`/events/${eventId}/organizers`, { params }),
  createEvent: (data: any) => api.post('/organization/events', data),
  members: () => api.get('/organization/members'),
  inviteMember: (data: { email: string; role: string; organization_role_id?: string | null }) =>
    api.post('/organization/invite-member', data),
  updateMember: (id: string, data: { role?: string; status?: string; organization_role_id?: string | null }) =>
    api.patch(`/organization/members/${id}`, data),
  removeMember: (id: string) => api.delete(`/organization/members/${id}`),
  resendMemberInvite: (id: string) => api.post(`/organization/members/${id}/resend`),
  // Custom organization roles (additive to the system role string)
  roles: () => api.get('/organization/roles'),
  rolePermissions: () => api.get('/organization/roles/permissions'),
  createRole: (data: { role_name: string; description?: string; permissions?: string[] }) =>
    api.post('/organization/roles', data),
  updateRole: (roleId: string, data: { role_name?: string; description?: string; permissions?: string[] }) =>
    api.put(`/organization/roles/${roleId}`, data),
  deleteRole: (roleId: string) => api.delete(`/organization/roles/${roleId}`),
  // Invite links (token accept flow)
  invitations: () => api.get('/organization/invitations'),
  createInvite: (data: { email: string; role: string; organization_role_id?: string | null }) =>
    api.post('/organization/invitations', data),
  resendInvitation: (id: string) => api.post(`/organization/invitations/${id}/resend`),
  revokeInvitation: (id: string) => api.post(`/organization/invitations/${id}/revoke`),
  uploadLogo: (file: File, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append('logo', file);
    return api.post('/organization/profile/upload-logo', form, {
      onUploadProgress: (e) => {
        if (!onProgress || !e.total) return;
        onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
  },
  organizerStats: () => api.get('/organization/organizers/stats'),
  organizers: (params?: { q?: string; status?: string; min_events?: number; page?: number; per_page?: number }) =>
    api.get('/organization/organizers', { params }),
  organizer: (id: string) => api.get(`/organization/organizers/${id}`),
  updateOrganizer: (id: string, data: { status?: string; designation?: string; department?: string; access_permissions?: string[] }) =>
    api.patch(`/organization/organizers/${id}`, data),
  removeOrganizer: (id: string) => api.delete(`/organization/organizers/${id}`),
  createOrganizer: (data: any) => api.post('/organization/organizers', data),
  organizerRequests: () => api.get('/organization/organizer-requests'),
  organizerRequestDetail: (requestId: string) => api.get(`/organization/organizer-requests/${requestId}`),
  requestOrganizerAccess: (data: any) => api.post('/organization/organizer-requests', data),
  approveOrganizerRequest: (requestId: string, data?: { notes?: string; organization_role_id?: string | null }) =>
    api.put(`/organization/organizer-requests/${requestId}/approve`, data),
  rejectOrganizerRequest: (requestId: string, data: { notes: string }) =>
    api.put(`/organization/organizer-requests/${requestId}/reject`, data),
  clarifyOrganizerRequest: (requestId: string, data: { notes: string }) =>
    api.post(`/organization/organizer-requests/${requestId}/clarification`, data),
  departments: () => api.get('/organization/departments'),
  createDepartment: (data: { name: string }) => api.post('/organization/departments', data),
  clubs: () => api.get('/organization/clubs'),
  createClub: (data: { name: string }) => api.post('/organization/clubs', data),
  analytics: () => api.get('/organization/analytics'),
  getEventPromotion: (eventId: string) =>
    api.get<{ success: boolean; data: import('@/lib/event-promotion').EventPromotionDashboardData }>(
      `/organization/events/${eventId}/promotion`,
    ),
  updateEventPromotion: (eventId: string, data: FormData) =>
    api.put(`/organization/events/${eventId}/promotion`, data, {
      headers: { Accept: 'application/json' },
    }),
  settings: () => api.get('/organization/settings'),
  updateSettings: (data: any) =>
    api.put('/organization/settings', data, data instanceof FormData ? { headers: { Accept: 'application/json' } } : undefined),
};

// Teams API
export const teamsApi = {
  create: (data: { event_id: string; team_name: string; description?: string; min_size?: number; max_size?: number }) =>
    api.post('/teams', data),
  join: (data: { team_code: string; event_id?: string }) => api.post('/teams/join', data),
  get: (code: string) => api.get(`/teams/${code}`),
  invite: (data: { team_code: string; email: string }) => api.post('/teams/invite', data),
  acceptInvitation: (data: { token: string }) => api.post('/teams/invitations/accept', data),
  rejectInvitation: (data: { token: string }) => api.post('/teams/invitations/reject', data),
  joinRequests: (code: string) => api.get(`/teams/${code}/join-requests`),
  respondJoinRequest: (code: string, requestId: string, body: { status: 'approved' | 'rejected' }) =>
    api.put(`/teams/${code}/join-requests/${requestId}/respond`, body),
  leave: (id: string) => api.post(`/teams/${id}/leave`),
  getEventTeams: (eventId: string, params?: { search?: string; activity_id?: string }) =>
    api.get<{ success: boolean; data: any[] }>(`/events/${eventId}/teams`, { params }),
};

// Payments API (backend: /payments/initiate, /payments/verify, /payments/capture)
export const paymentsApi = {
  initiate: (
    data: { registration_id: string; gateway: 'stripe' | 'razorpay' | 'paypal' },
    axiosConfig?: Parameters<(typeof api)['post']>[2]
  ) => api.post('/payments/initiate', data, axiosConfig),
  capture: (data: { payment_id?: string; order_id: string }) =>
    api.post('/payments/capture', data),
  verify: (data: any) => api.post('/payments/verify', data),
  status: (id: string) => api.get(`/payments/${id}/status`),
  createOrder: (data: any) => api.post('/payments/initiate', data),
  verifyPayment: (data: any) => api.post('/payments/verify', data),
};

// Single canonical login client (admin vs platform endpoint chosen at runtime).
export const loginApi = {
  login: async (data: { email: string; password: string }, options?: { adminLogin?: boolean }) => {
    const email = typeof data?.email === 'string' ? data.email.trim() : '';
    const normalizedEmail = email.toLowerCase();
    const password = typeof data?.password === 'string' ? data.password : '';
    if (!email || !password) {
      return Promise.reject(new Error('Email and password are required'));
    }

    const endpoint = options?.adminLogin ? ADMIN_LOGIN_ENDPOINT : resolveLoginEndpoint();
    const response = await api.post(
      endpoint,
      { email: normalizedEmail, password },
      {
        withCredentials: true,
        timeout: AUTH_API_TIMEOUT_MS,
        headers: {
          'Content-Type': 'application/json',
          Authorization: '',
        },
        validateStatus: LOGIN_HTTP_ACCEPT,
      }
    );

    const contentType = String(response.headers?.['content-type'] ?? '');
    if (!isJsonApiPayload(response.data, contentType)) {
      const misroute = new Error(getLoginErrorMessageFromResponse(404)) as Error & {
        response?: { status: number; data?: unknown };
      };
      misroute.response = { status: 404, data: { message: 'Login endpoint not found' } };
      return Promise.reject(misroute);
    }

    if (response.status >= 200 && response.status < 300 && response.data) {
      if (!isAdminTwoFactorLoginResponse(response.data)) {
        persistAuthToken(response.data);
      }
    }

    return response;
  },
};

// Admin API (role: admin|super_admin)
export const adminApi = {
  login: (data: { email: string; password: string }) => loginApi.login(data, { adminLogin: true }),
  verifyTwoFactorLogin: async (challenge_token: string, code: string) => {
    const normalized = String(code ?? '').replace(/\s+/g, '');
    const res = await api.post(
      ADMIN_LOGIN_2FA_ENDPOINT,
      { challenge_token, code: normalized, otp: normalized },
      {
        withCredentials: true,
        timeout: AUTH_API_TIMEOUT_MS,
        headers: {
          'Content-Type': 'application/json',
          Authorization: '',
        },
        validateStatus: (status) => typeof status === 'number' && status < 500,
      }
    );
    if (res.status >= 200 && res.status < 300 && res.data) {
      persistAuthToken(res.data);
    }
    return res;
  },
  beginTwoFactorLoginReset: (challenge_token: string, recovery_code: string) =>
    api.post(
      ADMIN_LOGIN_2FA_RESET_SETUP_ENDPOINT,
      { challenge_token, recovery_code },
      {
        withCredentials: true,
        timeout: AUTH_API_TIMEOUT_MS,
        headers: {
          'Content-Type': 'application/json',
          Authorization: '',
        },
        validateStatus: (status) => typeof status === 'number' && status < 500,
      }
    ),
  verifyTwoFactorLoginReset: async (challenge_token: string, code: string) => {
    const res = await api.post(
      ADMIN_LOGIN_2FA_RESET_VERIFY_ENDPOINT,
      { challenge_token, code },
      {
        withCredentials: true,
        timeout: AUTH_API_TIMEOUT_MS,
        headers: {
          'Content-Type': 'application/json',
          Authorization: '',
        },
        validateStatus: (status) => typeof status === 'number' && status < 500,
      }
    );
    if (res.status >= 200 && res.status < 300 && res.data) {
      persistAuthToken(res.data);
    }
    return res;
  },
  me: (config?: AxiosRequestConfig) =>
    api.get('/admin/me', { timeout: AUTH_ME_TIMEOUT_MS, ...config }).then((res) => {
      traceMeResponse('/admin/me', res.status, res.data);
      return res;
    }),
  logout: () => api.post('/admin/logout'),
  changePassword: (data: { current_password: string; password: string; password_confirmation: string }) =>
    api.post('/admin/change-password', data),
  dashboard: () => api.get('/admin/dashboard'),
  badgeCounts: () =>
    api.get<{ success: boolean; data: Record<string, number> }>('/admin/badges'),
  getPublicStats: () => api.get('/public-stats'),
  organizationRegistrations: (params?: any) => api.get('/admin/registrations/organizations', { params }),
  showOrganizationRegistration: (id: string) => api.get(`/admin/registrations/organizations/${id}`),
  organizations: (params?: any) => api.get('/admin/organizations', { params }),
  showOrganization: (id: string) => api.get(`/admin/organizations/${id}`),
  organizationMembers: (id: string) => api.get(`/admin/organizations/${id}/members`),
  createOrganization: (payload: any) => api.post('/admin/organizations', payload),
  updateOrganization: (id: string, payload: any) => api.put(`/admin/organizations/${id}`, payload),
  deleteOrganization: (id: string) => api.delete(`/admin/organizations/${id}`),
  restoreOrganization: (id: string) => api.post(`/admin/organizations/${id}/restore`),
  bulkOrganizations: (payload: { ids: string[]; action: 'approve' | 'suspend' | 'delete' | 'restore' }) =>
    api.post('/admin/organizations/bulk', payload),
  exportOrganizations: (params?: any) =>
    api.get('/admin/organizations/export', { params, responseType: 'blob' }),
  approveOrganizationRegistration: (id: string) => api.put(`/admin/registrations/organizations/${id}/approve`),
  rejectOrganizationRegistration: (id: string) => api.put(`/admin/registrations/organizations/${id}/reject`),
  individualRegistrations: (params?: any) => api.get('/admin/registrations/individuals', { params }),
  approveIndividualRegistration: (id: string) => api.put(`/admin/registrations/individuals/${id}/approve`),
  rejectIndividualRegistration: (id: string) => api.put(`/admin/registrations/individuals/${id}/reject`),
  individualOrganizers: (params?: any) => api.get('/admin/individual-organizers', { params }),
  showIndividualOrganizer: (id: string) => api.get(`/admin/individual-organizers/${id}`),
  approveIndividualOrganizer: (id: string) => api.put(`/admin/individual-organizers/${id}/approve`),
  rejectIndividualOrganizer: (id: string, payload?: { reason?: string }) => api.put(`/admin/individual-organizers/${id}/reject`, payload),
  kycRecords: (params?: Record<string, unknown>) => api.get('/admin/kyc', { params }),
  showKycRecord: (id: string) => api.get(`/admin/kyc/${id}`),
  kycAuditLogs: (params?: Record<string, unknown>) => api.get('/admin/kyc/audit-logs', { params }),
  approveKyc: (id: string, payload?: { notes?: string }) => api.post(`/admin/kyc/${id}/approve`, payload),
  rejectKyc: (id: string, payload: { reason: string; notes?: string }) =>
    api.post(`/admin/kyc/${id}/reject`, payload),
  requestKycReupload: (id: string, payload?: { notes?: string }) =>
    api.post(`/admin/kyc/${id}/request-reupload`, payload),
  sendKycReminder: (id: string) => api.post(`/admin/kyc/${id}/send-reminder`),
  startKycReview: (id: string, payload?: { notes?: string }) =>
    api.post(`/admin/kyc/${id}/start-review`, payload),
  identityVerificationsList: (params?: Record<string, unknown>) => api.get('/admin/identity-verifications', { params }),
  identityVerificationShow: (id: string) => api.get(`/admin/identity-verifications/${id}`),
  identityVerificationReveal: (id: string) => api.post(`/admin/identity-verifications/${id}/reveal`),
  identityVerificationApprove: (id: string, payload?: { notes?: string }) => api.post(`/admin/identity-verifications/${id}/approve`, payload),
  identityVerificationReject: (id: string, payload: { preset: string; reason: string }) => api.post(`/admin/identity-verifications/${id}/reject`, payload),
  identityVerificationRequestResubmission: (id: string, payload: { notes: string }) => api.post(`/admin/identity-verifications/${id}/request-resubmission`, payload),
  settlementAccounts: (params?: Record<string, unknown>) => api.get('/admin/settlement-accounts', { params }),
  showSettlementAccount: (id: string) => api.get(`/admin/settlement-accounts/${id}`),
  settlementChangeRequests: (params?: Record<string, unknown>) =>
    api.get('/admin/settlement-accounts/change-requests', { params }),
  verifySettlementAccount: (id: string, payload?: { notes?: string }) =>
    api.post(`/admin/settlement-accounts/${id}/verify`, payload),
  rejectSettlementAccount: (id: string, payload: { reason: string; notes?: string }) =>
    api.post(`/admin/settlement-accounts/${id}/reject`, payload),
  holdSettlementAccount: (id: string, payload: { reason: string; notes?: string }) =>
    api.post(`/admin/settlement-accounts/${id}/on-hold`, payload),
  requestSettlementReupload: (id: string, payload?: { notes?: string }) =>
    api.post(`/admin/settlement-accounts/${id}/request-reupload`, payload),
  startSettlementReview: (id: string, payload?: { notes?: string }) =>
    api.post(`/admin/settlement-accounts/${id}/start-review`, payload),
  settlementQueue: (params?: Record<string, unknown>) => api.get('/admin/settlement-queue', { params }),
  showSettlementQueueItem: (id: string) => api.get(`/admin/settlement-queue/${id}`),
  startSettlementQueueReview: (id: string, payload?: { notes?: string }) =>
    api.post(`/admin/settlement-queue/${id}/start-review`, payload),
  approveSettlementRequest: (id: string, payload: { notes: string }) =>
    api.post(`/admin/settlement-queue/${id}/approve`, payload),
  rejectSettlementRequest: (id: string, payload: { notes: string }) =>
    api.post(`/admin/settlement-queue/${id}/reject`, payload),
  processSettlementRequest: (id: string, payload: { notes: string }) =>
    api.post(`/admin/settlement-queue/${id}/processing`, payload),
  markSettlementPaid: (id: string, payload: Record<string, unknown>) =>
    api.post(`/admin/settlement-queue/${id}/paid`, payload),
  freezeSettlementRequest: (id: string, payload: { reason: string; notes: string }) =>
    api.post(`/admin/settlement-queue/${id}/freeze`, payload),
  releaseSettlementRequest: (id: string, payload: { notes: string }) =>
    api.post(`/admin/settlement-queue/${id}/release`, payload),
  financeOperationsDashboard: (params?: Record<string, unknown>) => api.get('/admin/finance-operations/dashboard', { params }),
  financeOperationsRevenue: (params?: Record<string, unknown>) => api.get('/admin/finance-operations/revenue', { params }),
  financeOperationsSettlements: (params?: Record<string, unknown>) => api.get('/admin/finance-operations/settlements', { params }),
  financeOperationsRefunds: (params?: Record<string, unknown>) => api.get('/admin/finance-operations/refunds', { params }),
  financeOperationsBalances: (params?: Record<string, unknown>) => api.get('/admin/finance-operations/balances', { params }),
  financeOperationsLedger: (params?: Record<string, unknown>) => api.get('/admin/finance-operations/ledger', { params }),
  financeOperationsAuditLogs: (params?: Record<string, unknown>) => api.get('/admin/finance-operations/audit-logs', { params }),
  financeOperationsComplianceAlerts: (params?: Record<string, unknown>) => api.get('/admin/finance-operations/compliance-alerts', { params }),
  financeOperationsExport: (type: string, params?: Record<string, unknown>) =>
    api.get(`/admin/finance-operations/export/${type}`, { params, responseType: 'blob' }),
  eventGovernanceOverview: () => api.get('/admin/event-governance/overview'),
  eventGovernanceQueue: (params?: Record<string, unknown>) => api.get('/admin/event-governance', { params }),
  eventGovernanceAuditLogs: (params?: Record<string, unknown>) => api.get('/admin/event-governance/audit-logs', { params }),
  showEventGovernance: (id: string) => api.get(`/admin/event-governance/${id}`),
  holdEventGovernance: (id: string, payload: Record<string, unknown>) => api.post(`/admin/event-governance/${id}/hold`, payload),
  startEventGovernanceReview: (id: string, payload: Record<string, unknown>) => api.post(`/admin/event-governance/${id}/start-review`, payload),
  suspendEventGovernance: (id: string, payload: Record<string, unknown>) => api.post(`/admin/event-governance/${id}/suspend`, payload),
  reactivateEventGovernance: (id: string, payload: Record<string, unknown>) => api.post(`/admin/event-governance/${id}/reactivate`, payload),
  forceEventGovernanceRefunds: (id: string, payload: Record<string, unknown>) => api.post(`/admin/event-governance/${id}/force-refunds`, payload),
  releaseEventGovernanceSettlements: (id: string, payload: Record<string, unknown>) => api.post(`/admin/event-governance/${id}/release-settlements`, payload),
  userGovernanceOverview: () => api.get('/admin/user-governance/overview'),
  userGovernanceQueue: (params?: Record<string, unknown>) => api.get('/admin/user-governance', { params }),
  userGovernanceAuditLogs: (params?: Record<string, unknown>) => api.get('/admin/user-governance/audit-logs', { params }),
  showUserGovernance: (id: string) => api.get(`/admin/user-governance/${id}`),
  holdUserGovernance: (id: string, payload: Record<string, unknown>) => api.post(`/admin/user-governance/${id}/hold`, payload),
  startUserGovernanceReview: (id: string, payload: Record<string, unknown>) => api.post(`/admin/user-governance/${id}/start-review`, payload),
  suspendUserGovernance: (id: string, payload: Record<string, unknown>) => api.post(`/admin/user-governance/${id}/suspend`, payload),
  reactivateUserGovernance: (id: string, payload: Record<string, unknown>) => api.post(`/admin/user-governance/${id}/reactivate`, payload),
  freezeUserGovernanceFinancials: (id: string, payload: Record<string, unknown>) => api.post(`/admin/user-governance/${id}/freeze-financials`, payload),
  releaseUserGovernanceFinancials: (id: string, payload: Record<string, unknown>) => api.post(`/admin/user-governance/${id}/release-financials`, payload),
  refundQueue: (params?: Record<string, unknown>) => api.get('/admin/refund-queue', { params }),
  showRefundQueueItem: (id: string) => api.get(`/admin/refund-queue/${id}`),
  startRefundQueueReview: (id: string, payload?: { notes?: string }) =>
    api.post(`/admin/refund-queue/${id}/start-review`, payload),
  approveRefundQueueRequest: (id: string, payload: { notes: string }) =>
    api.post(`/admin/refund-queue/${id}/approve`, payload),
  rejectRefundQueueRequest: (id: string, payload: { notes: string }) =>
    api.post(`/admin/refund-queue/${id}/reject`, payload),
  processRefundRequest: (id: string, payload: { notes: string }) =>
    api.post(`/admin/refund-queue/${id}/processing`, payload),
  markRefundRefunded: (id: string, payload: Record<string, unknown>) =>
    api.post(`/admin/refund-queue/${id}/refunded`, payload),
  forceRefundRequest: (payload: Record<string, unknown>) => api.post('/admin/refund-queue/force', payload),
  organizerApprovals: (params?: Record<string, unknown>) => api.get('/admin/organizer-approvals', { params }),
  organizerApprovalPendingQueue: (params?: Record<string, unknown>) =>
    api.get('/admin/organizer-approvals/pending-queue', { params }),
  showOrganizerApproval: (id: string) => api.get(`/admin/organizer-approvals/${id}`),
  approveOrganizerRequest: (id: string, payload: { notes: string }) =>
    api.post(`/admin/organizer-approvals/${id}/approve`, payload),
  rejectOrganizerRequest: (id: string, payload: { notes: string }) =>
    api.post(`/admin/organizer-approvals/${id}/reject`, payload),
  holdOrganizerRequest: (id: string, payload: { notes: string }) =>
    api.post(`/admin/organizer-approvals/${id}/hold`, payload),
  suspendOrganizerRequest: (id: string, payload: { notes: string }) =>
    api.post(`/admin/organizer-approvals/${id}/suspend`, payload),
  reactivateOrganizerRequest: (id: string, payload: { notes: string }) =>
    api.post(`/admin/organizer-approvals/${id}/reactivate`, payload),
  sendOrganizerApprovalReminder: (id: string, payload: { reminder_type: string; notes?: string }) =>
    api.post(`/admin/organizer-approvals/${id}/send-reminder`, payload),
  customerUsers: (params?: any) => api.get('/admin/customers/users', { params }),
  exportCustomerUsers: (params?: any) =>
    api.get('/admin/customers/users/export', { params, responseType: 'blob' }),
  showCustomerUser: (id: string) => api.get(`/admin/customers/users/${id}`),
  updateCustomerUser: (id: string, data: any) => api.put(`/admin/customers/users/${id}`, data),
  sendCustomerPasswordReset: (id: string) => api.post(`/admin/customers/users/${id}/send-password-reset`),
  suspendCustomerUser: (id: string, reason?: string) =>
    api.post(`/admin/customers/users/${id}/suspend`, { reason }),
  deleteCustomerUser: (id: string) => api.delete(`/admin/customers/users/${id}`),
  assignCustomerUserRoles: (id: string, roles: string[]) =>
    api.put(`/admin/customers/users/${id}/roles`, { roles }),
  /** @deprecated Use customerUsers for platform customers */
  users: (params?: any) => api.get('/admin/customers/users', { params }),
  exportUsers: (params?: any) => api.get('/admin/customers/users/export', { params, responseType: 'blob' }),
  updateUser: (id: string, data: any) => api.put(`/admin/customers/users/${id}`, data),
  suspendUser: (id: string, reason?: string) => api.post(`/admin/customers/users/${id}/suspend`, { reason }),
  deleteUser: (id: string) => api.delete(`/admin/customers/users/${id}`),
  platformAdminUsers: (params?: any) => api.get('/admin/users', { params }),
  platformAdminUser: (id: string) => api.get(`/admin/users/${id}`),
  platformAdminAccessPreview: (id: string) => api.get(`/admin/users/${id}/access-preview`),
  createPlatformAdminUser: (data: any) => api.post('/admin/users', data),
  updatePlatformAdminUser: (id: string, data: any) => api.put(`/admin/users/${id}`, data),
  deletePlatformAdminUser: (id: string) => api.delete(`/admin/users/${id}`),
  invitePlatformAdmin: (id: string) => api.post(`/admin/users/${id}/invite`),
  resendPlatformAdminInvite: (id: string) => api.post(`/admin/users/${id}/invite/resend`),
  revokePlatformAdminInvite: (id: string) => api.post(`/admin/users/${id}/invite/revoke`),
  resetPlatformAdminPassword: (id: string) => api.post(`/admin/users/${id}/reset-password`),
  syncPlatformAdminRoles: (id: string, roleIds: string[], primaryRoleId?: string) =>
    api.put(`/admin/users/${id}/roles`, {
      role_ids: roleIds,
      primary_role_id: primaryRoleId ?? roleIds[0] ?? '',
    }),
  removePlatformAdminRole: (userId: string, roleId: string) =>
    api.delete(`/admin/users/${userId}/roles/${roleId}`),
  patchPlatformAdminTeam: (id: string, teamId: string | null) =>
    api.patch(`/admin/users/${id}/team`, { team_id: teamId }),
  platformAdminAuditLogs: (id: string, params?: { per_page?: number }) =>
    api.get(`/admin/users/${id}/audit-logs`, { params }),
  revokePlatformAdminAccess: (id: string) => api.post(`/admin/users/${id}/revoke-access`),
  restorePlatformAdminAccess: (id: string) => api.post(`/admin/users/${id}/restore-access`),
  syncPlatformAdminPermissions: (id: string, data: { grants?: string[]; denies?: string[]; reason?: string }) =>
    api.put(`/admin/users/${id}/permissions`, data),
  suspendPlatformAdmin: (id: string) => api.post(`/admin/users/${id}/suspend`),
  activatePlatformAdmin: (id: string) => api.post(`/admin/users/${id}/activate`),
  bulkInvitePlatformAdmins: (userIds: string[]) =>
    api.post('/admin/users/bulk-invite', { user_ids: userIds }),
  bulkSuspendPlatformAdmins: (userIds: string[]) =>
    api.post('/admin/users/bulk-suspend', { user_ids: userIds }),
  bulkRevokePlatformAdmins: (userIds: string[]) =>
    api.post('/admin/users/bulk-revoke-access', { user_ids: userIds }),
  adminTeams: (params?: any) => api.get('/admin/teams', { params }),
  adminTeam: (id: string) => api.get(`/admin/teams/${id}`),
  createAdminTeam: (data: any) => api.post('/admin/teams', data),
  updateAdminTeam: (id: string, data: any) => api.put(`/admin/teams/${id}`, data),
  deleteAdminTeam: (id: string) => api.delete(`/admin/teams/${id}`),
  syncAdminTeamMembers: (id: string, userIds: string[]) =>
    api.put(`/admin/teams/${id}/members`, { user_ids: userIds }),
  addAdminTeamMember: (id: string, payload: Record<string, unknown>) => api.post(`/admin/teams/${id}/members`, payload),
  removeAdminTeamMember: (id: string, userId: string) => api.delete(`/admin/teams/${id}/members/${userId}`),
  transferAdminTeamMember: (id: string, userId: string, payload: Record<string, unknown>) =>
    api.post(`/admin/teams/${id}/members/${userId}/transfer`, payload),
  adminTeamDashboard: (id: string, params?: Record<string, unknown>) => api.get(`/admin/teams/${id}/dashboard`, { params }),
  adminTeamAuditLogs: (params?: Record<string, unknown>) => api.get('/admin/teams/audit-logs', { params }),
  teamAssignmentsQueue: (params?: Record<string, unknown>) => api.get('/admin/case-assignments', { params }),
  teamAssignmentsDashboard: (params?: Record<string, unknown>) => api.get('/admin/case-assignments/dashboard', { params }),
  teamAssignmentsAuditLogs: (params?: Record<string, unknown>) => api.get('/admin/case-assignments/audit-logs', { params }),
  createTeamAssignment: (payload: Record<string, unknown>) => api.post('/admin/case-assignments', payload),
  escalateTeamAssignment: (id: string, payload: Record<string, unknown>) => api.post(`/admin/case-assignments/${id}/escalate`, payload),
  reassignTeamAssignment: (id: string, payload: Record<string, unknown>) => api.post(`/admin/case-assignments/${id}/reassign`, payload),
  transferTeamAssignment: (id: string, payload: Record<string, unknown>) => api.post(`/admin/case-assignments/${id}/transfer`, payload),
  completeTeamAssignment: (id: string, payload: Record<string, unknown>) => api.post(`/admin/case-assignments/${id}/complete`, payload),
  operationsCenterDashboard: (params?: Record<string, unknown>) => api.get('/admin/operations-center/dashboard', { params }),
  operationsCenterCases: (params?: Record<string, unknown>) => api.get('/admin/operations-center/cases', { params }),
  createOperationsCase: (payload: Record<string, unknown>) => api.post('/admin/operations-center/cases', payload),
  showOperationsCase: (id: string) => api.get(`/admin/operations-center/cases/${id}`),
  escalateOperationsCase: (id: string, payload: Record<string, unknown>) => api.post(`/admin/operations-center/cases/${id}/escalate`, payload),
  inviteOperationsCaseAdmin: (id: string, payload: Record<string, unknown>) => api.post(`/admin/operations-center/cases/${id}/invite-admin`, payload),
  inviteOperationsCaseTeam: (id: string, payload: Record<string, unknown>) => api.post(`/admin/operations-center/cases/${id}/invite-team`, payload),
  operationsCenterNotes: (params?: Record<string, unknown>) => api.get('/admin/operations-center/notes', { params }),
  createOperationsNote: (payload: Record<string, unknown>) => api.post('/admin/operations-center/notes', payload),
  operationsCenterConversations: (params?: Record<string, unknown>) => api.get('/admin/operations-center/conversations', { params }),
  joinOperationsConversation: (id: string) => api.post(`/admin/operations-center/conversations/${id}/join`),
  assignOperationsConversation: (id: string, payload: Record<string, unknown>) => api.post(`/admin/operations-center/conversations/${id}/assign`, payload),
  transferOperationsConversation: (id: string, payload: Record<string, unknown>) => api.post(`/admin/operations-center/conversations/${id}/transfer`, payload),
  operationsCenterAuditLogs: (params?: Record<string, unknown>) => api.get('/admin/operations-center/audit-logs', { params }),
  userIntelligence: (id: string) => api.get(`/admin/intelligence/users/${id}`),
  userIntelligenceTimeline: (id: string, params?: Record<string, unknown>) =>
    api.get(`/admin/intelligence/users/${id}/timeline`, { params }),
  organizerIntelligence: (id: string) => api.get(`/admin/intelligence/organizers/${id}`),
  organizerIntelligenceTimeline: (id: string, params?: Record<string, unknown>) =>
    api.get(`/admin/intelligence/organizers/${id}/timeline`, { params }),
  organizationIntelligence: (id: string) => api.get(`/admin/intelligence/organizations/${id}`),
  organizationIntelligenceTimeline: (id: string, params?: Record<string, unknown>) =>
    api.get(`/admin/intelligence/organizations/${id}/timeline`, { params }),
  intelligenceDocumentPreview: (type: string, id: string) => api.get(`/admin/intelligence/documents/${type}/${id}/preview`),
  notificationDeliveryOverview: () => api.get('/admin/notification-delivery/overview'),
  notificationDeliveries: (params?: Record<string, unknown>) => api.get('/admin/notification-delivery/deliveries', { params }),
  notificationDeliveryDetail: (id: string) => api.get(`/admin/notification-delivery/deliveries/${id}`),
  notificationDeliveryProviderHealth: () => api.get('/admin/notification-delivery/provider-health'),
  notificationDeliveryAuditLogs: (params?: Record<string, unknown>) => api.get('/admin/notification-delivery/audit-logs', { params }),
  retryNotificationDelivery: (id: string) => api.post(`/admin/notification-delivery/deliveries/${id}/retry`),
  retryNotificationDeliveryBulk: (payload: Record<string, unknown>) => api.post('/admin/notification-delivery/retry-bulk', payload),
  retryAllFailedNotifications: (params?: Record<string, unknown>) => api.post('/admin/notification-delivery/retry-all-failed', null, { params }),
  platformIntelligenceTimeline: (params?: { source?: string; per_page?: number; page?: number; q?: string }) =>
    api.get('/admin/intelligence/platform/timeline', { params }),
  showAdminInvite: (token: string) => api.get(`/admin-invite/${token}`),
  acceptAdminInvite: (data: { token: string; password: string; password_confirmation: string }) =>
    api.post('/admin-invite/accept', data),
  showAdminPasswordReset: (token: string) => api.get('/admin-invite/reset', { params: { token } }),
  acceptAdminPasswordReset: (data: { token: string; password: string; password_confirmation: string }) =>
    api.post('/admin-invite/reset', data),
  events: (params?: any) => api.get('/admin/events', { params }),
  updateEvent: (id: string, data: any) => api.put(`/admin/events/${id}`, data),
  deleteEvent: (id: string) => api.delete(`/admin/events/${id}`),
  approveEvent: (id: string) => api.put(`/admin/events/${id}/approve`),
  rejectEvent: (id: string, reason?: string) => api.put(`/admin/events/${id}/reject`, { reason }),
  requestChangesEvent: (id: string, reason?: string) => api.put(`/admin/events/${id}/request-changes`, { reason }),
  featureEvent: (id: string) => api.put(`/admin/events/${id}/feature`),
  organizers: (params?: any) => api.get('/admin/organizers', { params }),
  exportOrganizers: (params?: any) => api.get('/admin/organizers/export', { params, responseType: 'blob' }),
  approveOrganizer: (id: string) => api.put(`/admin/organizers/${id}/approve`),
  rejectOrganizer: (id: string) => api.put(`/admin/organizers/${id}/reject`),
  suspendOrganizer: (id: string) => api.put(`/admin/organizers/${id}/suspend`),
  organizerEvents: (id: string, params?: any) => api.get(`/admin/organizers/${id}/events`, { params }),
  subscriptions: (params?: { page?: number; status?: string; q?: string }) =>
    api.get('/admin/subscriptions', { params }),
  subscriptionDetail: (id: string) => api.get(`/admin/subscriptions/${id}`),
  subscriptionChangePlan: (id: string, data: { plan_id: string; change_type: string }) =>
    api.post(`/admin/subscriptions/${id}/change-plan`, data),
  subscriptionPause: (id: string) => api.post(`/admin/subscriptions/${id}/pause`),
  subscriptionCancel: (id: string) => api.post(`/admin/subscriptions/${id}/cancel`),
  subscriptionRenew: (id: string) => api.post(`/admin/subscriptions/${id}/renew`),
  updateSubscription: (id: string, data: any) => api.put(`/admin/subscriptions/${id}`, data),
  accessPermissionCatalog: () => api.get('/admin/access/permission-catalog'),
  accessBundles: (params?: { page?: number; per_page?: number; q?: string; active?: boolean }) =>
    api.get('/admin/access/bundles', { params }),
  createAccessBundle: (data: any) => api.post('/admin/access/bundles', data),
  updateAccessBundle: (id: string, data: any) => api.put(`/admin/access/bundles/${id}`, data),
  syncPlanBundles: (planId: string, bundleIds: string[]) =>
    api.put(`/admin/access/plans/${planId}/bundles`, { bundle_ids: bundleIds }),
  syncPlanActivities: (planId: string, activityKeys: string[]) =>
    api.put(`/admin/plans/${planId}/activities`, { activity_keys: activityKeys }),
  activities: () => api.get('/admin/activities'),
  features: () => api.get('/admin/features'),
  syncPlanFeatures: (
    planId: string,
    features: Array<{
      feature_id?: string;
      key?: string;
      enabled: boolean;
      limit_value?: string | number | null;
      configuration?: any;
    }>
  ) => api.put(`/admin/plans/${planId}/features`, { features }),
  getOrganizationOverrides: (organizationId: string) =>
    api.get(`/admin/organizations/${organizationId}/subscription-overrides`),
  saveOrganizationOverrides: (
    organizationId: string,
    overrides: Array<{
      feature_id: string;
      is_allowed?: boolean | null;
      limit_value?: string | number | null;
      configuration?: any;
      reason?: string;
      expires_at?: string | null;
      delete?: boolean;
    }>
  ) => api.put(`/admin/organizations/${organizationId}/subscription-overrides`, { overrides }),
  planAccessPreview: (planId: string) => api.get(`/admin/access/plans/${planId}/preview`),
  rbacRoles: (params?: { page?: number; per_page?: number; q?: string }) =>
    api.get('/admin/rbac/roles', { params }),
  rbacRole: (id: string) => api.get(`/admin/rbac/roles/${id}`),
  rbacCreateRole: (data: { name: string; slug?: string; description?: string; permission_ids?: string[] }) =>
    api.post('/admin/rbac/roles', data),
  rbacUpdateRole: (id: string, data: { name?: string; description?: string; permission_ids?: string[] }) =>
    api.put(`/admin/rbac/roles/${id}`, data),
  rbacDuplicateRole: (id: string, data: { name: string; slug?: string }) =>
    api.post(`/admin/rbac/roles/${id}/duplicate`, data),
  rbacDeleteRole: (id: string) => api.delete(`/admin/rbac/roles/${id}`),
  rbacPermissions: () => api.get('/admin/rbac/permissions'),
  rbacUpdateMatrix: (roleId: string, permissionIds: string[]) =>
    api.put('/admin/rbac/matrix', { role_id: roleId, permission_ids: permissionIds }),
  rbacTeamMembers: (params?: { page?: number; q?: string }) => api.get('/admin/rbac/team-members', { params }),
  rbacUser: (id: string) => api.get(`/admin/rbac/users/${id}`),
  rbacSyncUserRoles: (id: string, roleIds: string[]) =>
    api.put(`/admin/rbac/users/${id}/roles`, { role_ids: roleIds }),
  rbacSyncUserPermissions: (id: string, data: { grants?: string[]; denies?: string[] }) =>
    api.put(`/admin/rbac/users/${id}/permissions`, data),
  rbacAuditLogs: (params?: { page?: number; q?: string; action?: string }) =>
    api.get('/admin/rbac/audit-logs', { params }),
  revenue: () => api.get('/admin/revenue'),
  payments: () => api.get('/admin/payments'),
  transactions: (params?: any) => api.get('/admin/transactions', { params }),
  exportTransactions: (params?: any) => api.get('/admin/transactions/export', { params, responseType: 'blob' }),
  analytics: () => api.get('/admin/analytics'),
  analyticsInsights: () => api.get('/admin/analytics/insights'),
  analyticsRecommendations: () => api.get('/admin/analytics/recommendations'),
  platformReadiness: () => api.get('/ready'),
  settings: () => api.get('/admin/settings'),
  updateSettings: (data: any) => api.put('/admin/settings', data),
  settlements: (params?: any) => api.get('/admin/settlements', { params }),
  approveSettlement: (id: string) => api.put(`/admin/settlements/${id}/approve`),
  rejectSettlement: (id: string) => api.put(`/admin/settlements/${id}/reject`),
  markLegacySettlementPaid: (id: string) => api.put(`/admin/settlements/${id}/mark-paid`),
  promotions: () => api.get('/admin/promotions'),
  banners: () => api.get('/admin/banners'),
  createBanner: (data: any) => api.post('/admin/banners', data),
  updateBanner: (id: string, data: any) => api.put(`/admin/banners/${id}`, data),
  deleteBanner: (id: string) => api.delete(`/admin/banners/${id}`),
  categories: () => api.get('/admin/categories'),
  createCategory: (data: any) => api.post('/admin/categories', data),
  updateCategory: (id: string, data: any) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id: string) => api.delete(`/admin/categories/${id}`),
  eventTypes: (params?: { q?: string; is_active?: boolean }) => api.get('/admin/event-types', { params }),
  createEventType: (data: {
    key?: string;
    name: string;
    description?: string;
    is_active?: boolean;
    display_order?: number;
  }) => api.post('/admin/event-types', data),
  updateEventType: (
    id: string,
    data: Partial<{ key: string; name: string; description: string; is_active: boolean; display_order: number }>,
  ) => api.put(`/admin/event-types/${id}`, data),
  deleteEventType: (id: string) => api.delete(`/admin/event-types/${id}`),
  eventFrameworks: (params?: { q?: string; is_active?: boolean }) => api.get('/admin/event-frameworks', { params }),
  getEventFramework: (id: string) => api.get(`/admin/event-frameworks/${id}`),
  createEventFramework: (data: Record<string, unknown>) => api.post('/admin/event-frameworks', data),
  updateEventFramework: (id: string, data: Record<string, unknown>) => api.put(`/admin/event-frameworks/${id}`, data),
  deleteEventFramework: (id: string) => api.delete(`/admin/event-frameworks/${id}`),
  munCouncilTypes: () => api.get<{ success: boolean; data: Array<Record<string, unknown>> }>('/admin/mun/council-types'),
  createMunCouncilType: (data: Record<string, unknown>) => api.post('/admin/mun/council-types', data),
  updateMunCouncilType: (id: string, data: Record<string, unknown>) => api.put(`/admin/mun/council-types/${id}`, data),
  activateMunCouncilType: (id: string) => api.post(`/admin/mun/council-types/${id}/activate`),
  deactivateMunCouncilType: (id: string) => api.post(`/admin/mun/council-types/${id}/deactivate`),
  frameworkTemplates: (params?: { framework_id?: string; q?: string; is_active?: boolean }) =>
    api.get('/admin/framework-templates', { params }),
  getFrameworkTemplate: (id: string) => api.get(`/admin/framework-templates/${id}`),
  createFrameworkTemplate: (data: Record<string, unknown>) => api.post('/admin/framework-templates', data),
  updateFrameworkTemplate: (id: string, data: Record<string, unknown>) =>
    api.put(`/admin/framework-templates/${id}`, data),
  cloneFrameworkTemplate: (id: string, data?: { name?: string }) =>
    api.post(`/admin/framework-templates/${id}/clone`, data ?? {}),
  archiveFrameworkTemplate: (id: string) => api.post(`/admin/framework-templates/${id}/archive`),
  deleteFrameworkTemplate: (id: string) => api.delete(`/admin/framework-templates/${id}`),
  subscriptionMatrix: () => api.get('/admin/subscription-matrix'),
  getSubscriptionMatrix: (planId: string) => api.get(`/admin/subscription-matrix/${planId}`),
  updateSubscriptionMatrix: (planId: string, data: Record<string, unknown>) =>
    api.put(`/admin/subscription-matrix/${planId}`, data),
  eventTags: (params?: { q?: string; is_active?: boolean }) => api.get('/admin/event-tags', { params }),
  createEventTag: (data: { name: string; slug?: string; is_active?: boolean }) => api.post('/admin/event-tags', data),
  updateEventTag: (id: string, data: Partial<{ name: string; slug: string; is_active: boolean }>) =>
    api.put(`/admin/event-tags/${id}`, data),
  deleteEventTag: (id: string) => api.delete(`/admin/event-tags/${id}`),
  logs: (params?: any) => api.get('/admin/logs', { params }),
  pages: () => api.get('/admin/pages'),
  updatePage: (id: string, data: any) => api.put(`/admin/pages/${id}`, data),
  faqs: (params?: { page?: number; per_page?: number; q?: string }) => api.get('/admin/faqs', { params }),
  createFaq: (data: { question: string; answer: string; sort_order?: number }) => api.post('/admin/faqs', data),
  updateFaq: (id: string, data: Partial<{ question: string; answer: string; sort_order: number }>) =>
    api.put(`/admin/faqs/${id}`, data),
  deleteFaq: (id: string) => api.delete(`/admin/faqs/${id}`),
  blogs: (params?: { page?: number; per_page?: number; q?: string; published?: boolean }) =>
    api.get('/admin/blogs', { params }),
  createBlog: (data: {
    title: string;
    slug?: string;
    content: string;
    author?: string;
    featured_image?: string;
    published_at?: string | null;
  }) => api.post('/admin/blogs', data),
  updateBlog: (
    id: string,
    data: Partial<{
      title: string;
      slug: string;
      content: string;
      author: string;
      featured_image: string;
      published_at: string | null;
    }>,
  ) => api.put(`/admin/blogs/${id}`, data),
  deleteBlog: (id: string) => api.delete(`/admin/blogs/${id}`),
  juryApplications: (params?: { page?: number; per_page?: number; q?: string; status?: string }) =>
    api.get('/admin/jury-applications', { params }),
  juryAssignments: (params?: { page?: number; per_page?: number; q?: string; status?: string }) =>
    api.get('/admin/jury-assignments', { params }),
  updateJuryApplication: (id: string, data: { status?: string; admin_notes?: string }) =>
    api.put(`/admin/jury-applications/${id}`, data),
  approveJuryApplication: (id: string) => api.post(`/admin/jury-applications/${id}/approve`),
  rejectJuryApplication: (id: string) => api.post(`/admin/jury-applications/${id}/reject`),
  adminColleges: (params?: { page?: number; per_page?: number; q?: string; state?: string }) =>
    api.get('/admin/colleges', { params }),
  createCollege: (data: {
    name: string;
    slug?: string;
    logo?: string;
    banner?: string;
    description?: string;
    city?: string;
    state?: string;
    category?: string;
  }) => api.post('/admin/colleges', data),
  updateCollege: (
    id: string,
    data: Partial<{
      name: string;
      slug: string;
      logo: string;
      banner: string;
      description: string;
      city: string;
      state: string;
      category: string;
    }>,
  ) => api.put(`/admin/colleges/${id}`, data),
  deleteCollege: (id: string) => api.delete(`/admin/colleges/${id}`),
  participantTeams: (params?: { page?: number; per_page?: number; q?: string; status?: string; event_id?: string }) =>
    api.get('/admin/participant-teams', { params }),
  participantTeam: (id: string) => api.get(`/admin/participant-teams/${id}`),
  integrationsOverview: () => api.get('/admin/integrations/overview'),
  integrationsApiKeys: (params?: { page?: number; per_page?: number }) =>
    api.get('/admin/integrations/api-keys', { params }),
  integrationsWebhooks: (params?: { page?: number; per_page?: number }) =>
    api.get('/admin/integrations/webhooks', { params }),
  integrationsRequestLogs: (params?: { page?: number; per_page?: number; status_code?: number }) =>
    api.get('/admin/integrations/request-logs', { params }),
  sections: (params?: { page?: string }) => api.get('/admin/sections', { params }),
  updateSection: (id: string, data: Record<string, unknown>) => api.put(`/admin/sections/${id}`, data),
  createSectionItem: (sectionId: string, data: Record<string, unknown>) =>
    api.post(`/admin/sections/${sectionId}/items`, data),
  updateSectionItem: (id: string, data: Record<string, unknown>) => api.put(`/admin/section-items/${id}`, data),
  deleteSectionItem: (id: string) => api.delete(`/admin/section-items/${id}`),
  qaQuestions: (params?: { page?: number; status?: string; q?: string }) => api.get('/admin/qa/questions', { params }),
  answerQaQuestion: (id: string, data: { answer: string }) => api.put(`/admin/qa/questions/${id}/answer`, data),
  coupons: () => api.get('/admin/coupons'),
  createCoupon: (data: any) => api.post('/admin/coupons', data),
  updateCoupon: (id: string, data: any) => api.put(`/admin/coupons/${id}`, data),
  deleteCoupon: (id: string) => api.delete(`/admin/coupons/${id}`),
  plans: (params?: { page?: number; q?: string; is_active?: boolean; _ts?: number }) =>
    api.get('/admin/plans', {
      params,
      skipInFlightDedup: typeof params?._ts === 'number',
    } as ApiGetConfig),
  createPlan: async (data: any) => {
    const res = await api.post('/admin/plans', data);
    invalidateInFlightGets('/subscription-plans');
    invalidateInFlightGets('/plans');
    invalidateInFlightGets('/pricing');
    invalidateInFlightGets('/admin/plans');
    return res;
  },
  updatePlanV2: async (id: string, data: Record<string, unknown>, lookupKey?: string) => {
    const planKey = typeof lookupKey === 'string' && lookupKey.trim()
      ? lookupKey.trim().toLowerCase()
      : typeof data?.key === 'string'
        ? data.key.trim().toLowerCase()
        : '';
    let targetId = id;

    if (planKey) {
      const latest = await api.get('/admin/plans', {
        params: { page: 1, q: planKey, _ts: Date.now() },
        skipInFlightDedup: true,
      } as ApiGetConfig);
      const payload = latest?.data?.data;
      const list: Array<{ id?: unknown; key?: unknown }> = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      const matchingPlan = list.find((plan) => String(plan?.key ?? '').toLowerCase() === planKey);
      if (matchingPlan?.id) {
        targetId = String(matchingPlan.id);
      } else {
        throw new Error('This plan changed or no longer exists. Refresh the pricing page and try again.');
      }
    }

    const requestData = planKey ? { ...data, _lookup_key: planKey } : data;
    const res = await api.put(`/admin/plans/${targetId}`, requestData);
    invalidateInFlightGets('/subscription-plans');
    invalidateInFlightGets('/plans');
    invalidateInFlightGets('/pricing');
    invalidateInFlightGets('/admin/plans');
    return res;
  },
  deletePlan: async (id: string) => {
    const res = await api.delete(`/admin/plans/${id}`);
    invalidateInFlightGets('/subscription-plans');
    invalidateInFlightGets('/plans');
    invalidateInFlightGets('/pricing');
    invalidateInFlightGets('/admin/plans');
    return res;
  },
  togglePlanActive: async (id: string) => {
    const res = await api.patch(`/admin/plans/${id}/toggle-active`);
    invalidateInFlightGets('/subscription-plans');
    invalidateInFlightGets('/plans');
    invalidateInFlightGets('/pricing');
    invalidateInFlightGets('/admin/plans');
    return res;
  },
  togglePlanPopular: async (id: string) => {
    const res = await api.patch(`/admin/plans/${id}/toggle-popular`);
    invalidateInFlightGets('/subscription-plans');
    invalidateInFlightGets('/plans');
    invalidateInFlightGets('/pricing');
    invalidateInFlightGets('/admin/plans');
    return res;
  },
  refundRequests: (params?: { page?: number; status?: string; q?: string }) =>
    api.get('/admin/refund-requests', { params }),
  exportRefundRequests: (params?: { status?: string; q?: string }) =>
    api.get('/admin/refund-requests/export', { params, responseType: 'blob' }),
  approveRefundRequest: (id: string, admin_notes?: string) =>
    api.post(`/admin/refund-requests/${id}/approve`, { admin_notes }),
  rejectRefundRequest: (id: string, admin_notes?: string) =>
    api.post(`/admin/refund-requests/${id}/reject`, { admin_notes }),
  rbac: () =>
    api.get<{
      success: boolean;
      data: {
        roles: Array<{ id: string; name: string; permissions?: Array<{ id: string; name: string }> }>;
        permissions: Array<{ id: string; name: string }>;
      };
    }>('/admin/rbac'),
  assignUserRoles: (id: string, roles: string[]) => api.put(`/admin/customers/users/${id}/roles`, { roles }),
  // Testimonial Management
  listReviews: (params?: any) => {
    const safeParams = { ...(params ?? {}) } as Record<string, any>;
    if (!safeParams.status || safeParams.status === 'all') {
      delete safeParams.status;
    }
    if (!safeParams.search) {
      delete safeParams.search;
    }
    return api.get('/admin/organizer-reviews', { params: safeParams });
  },
  createReview: (data: any) => api.post('/admin/organizer-reviews', data),
  updateReview: (id: string, data: any) => api.put(`/admin/organizer-reviews/${id}`, data),
  deleteReview: (id: string) => api.delete(`/admin/organizer-reviews/${id}`),

  listSocialLinks: () => api.get<{ success: boolean; data: any[] }>('/admin/social-links'),
  createSocialLink: (data: {
    platform: string;
    url: string;
    sort_order?: number;
    is_enabled?: boolean;
  }) => api.post('/admin/social-links', data),
  updateSocialLink: (
    id: string,
    data: {
      platform: string;
      url: string;
      sort_order?: number;
      is_enabled?: boolean;
    }
  ) => api.put(`/admin/social-links/${id}`, data),
  deleteSocialLink: (id: string) => api.delete(`/admin/social-links/${id}`),

  // Admin Management: Contact Messages
  listMessages: (params?: { status?: string; page?: number }) =>
    api.get<{ success: boolean; data: any }>('/admin/contact-messages', { params }),
  updateMessageStatus: (id: string, status: 'unread' | 'read' | 'archived' | 'pending' | 'answered') =>
    api.patch(`/admin/contact-messages/${id}`, { status }),
  replyMessage: (id: string, body: string) =>
    api.post<{ success: boolean; message: string; data: any }>(`/admin/contact-messages/${id}/reply`, { body }),
  deleteMessage: (id: string) =>
    api.delete<{ success: boolean }>(`/admin/contact-messages/${id}`),

  // Admin Conversations (Admin ↔ Organization and Admin ↔ Individual Organizer)
  listAdminConversations: (params?: { type?: string }) =>
    api.get<{ success: boolean; data: any[] }>('/admin/admin-conversations', { params }),
  getMessagingRecipients: (q?: string) =>
    api.get<{ success: boolean; data: { organizations: any[]; individual_organizers: any[] } }>('/admin/admin-conversations/recipients', { params: { q } }),
  startAdminConversation: (data: { target_type: 'organization' | 'individual'; target_user_id: string; subject?: string; message?: string }) =>
    api.post<{ success: boolean; data: any }>('/admin/admin-conversations/start', data),
  getAdminConversationMessages: (id: string) =>
    api.get<{ success: boolean; data: { conversation: any; messages: any[] } }>(`/admin/admin-conversations/${id}/messages`),
  sendAdminConversationMessage: (id: string, message: string) =>
    api.post<{ success: boolean; data: any }>(`/admin/admin-conversations/${id}/messages`, { message }),

  // Platform Resources Management
  listResources: (params?: { type?: string; search?: string; page?: number; per_page?: number }) =>
    api.get<{ success: boolean; data: any }>('/admin/platform-resources', { params }),
  listPublicResources: (params?: { type?: string }) =>
    api.get<{ success: boolean; data: PlatformResource[] }>('/platform-resources', { params }),
  createResource: (data: any) =>
    api.post<{ success: boolean; data: PlatformResource }>('/admin/platform-resources', data),
  updateResource: (id: string, data: any) =>
    api.put<{ success: boolean; data: PlatformResource }>(`/admin/platform-resources/${id}`, data),
  deleteResource: (id: string) =>
    api.delete<{ success: boolean }>(`/admin/platform-resources/${id}`),
  // Resource Hub CMS CRUD
  resourceArticles: (params?: { page?: number; per_page?: number; q?: string }) => api.get('/admin/resources/articles', { params }),
  createResourceArticle: (data: any) => api.post('/admin/resources/articles', data),
  updateResourceArticle: (id: string, data: any) => api.put(`/admin/resources/articles/${id}`, data),
  deleteResourceArticle: (id: string) => api.delete(`/admin/resources/articles/${id}`),
  resourceVideos: (params?: { page?: number; per_page?: number; q?: string }) => api.get('/admin/resources/videos', { params }),
  createResourceVideo: (data: any) => api.post('/admin/resources/videos', data),
  updateResourceVideo: (id: string, data: any) => api.put(`/admin/resources/videos/${id}`, data),
  deleteResourceVideo: (id: string) => api.delete(`/admin/resources/videos/${id}`),
  resourceSuccessStories: (params?: { page?: number; per_page?: number; q?: string }) => api.get('/admin/resources/success-stories', { params }),
  createResourceSuccessStory: (data: any) => api.post('/admin/resources/success-stories', data),
  updateResourceSuccessStory: (id: string, data: any) => api.put(`/admin/resources/success-stories/${id}`, data),
  deleteResourceSuccessStory: (id: string) => api.delete(`/admin/resources/success-stories/${id}`),
  resourceFaqs: (params?: { page?: number; per_page?: number; q?: string }) => api.get('/admin/resources/faqs', { params }),
  createResourceFaq: (data: any) => api.post('/admin/resources/faqs', data),
  updateResourceFaq: (id: string, data: any) => api.put(`/admin/resources/faqs/${id}`, data),
  deleteResourceFaq: (id: string) => api.delete(`/admin/resources/faqs/${id}`),
  platformGuide: (audience: string) => api.get(`/admin/resources/platform-guides/${audience}`),
  updatePlatformGuide: (audience: string, data: Record<string, unknown>) =>
    api.put(`/admin/resources/platform-guides/${audience}`, data),
  resourceQuestions: (params?: { page?: number; per_page?: number; q?: string; is_answered?: boolean }) => api.get('/admin/resources/questions', { params }),
  respondResourceQuestion: (id: string, data: { answer: string }) => api.put(`/admin/resources/questions/${id}/respond`, data),
  // Event discovery CMS CRUD
  eventDiscoveryCategories: (params?: { page?: number; per_page?: number; q?: string }) => api.get('/admin/event-categories', { params }),
  createEventDiscoveryCategory: (data: any) => api.post('/admin/event-categories', data),
  updateEventDiscoveryCategory: (id: string, data: any) => api.put(`/admin/event-categories/${id}`, data),
  deleteEventDiscoveryCategory: (id: string) => api.delete(`/admin/event-categories/${id}`),
  categoryTemplates: (params?: { page?: number; per_page?: number; q?: string; category_id?: string }) => api.get('/admin/category-templates', { params }),
  createCategoryTemplate: (data: any) => api.post('/admin/category-templates', data),
  updateCategoryTemplate: (id: string, data: any) => api.put(`/admin/category-templates/${id}`, data),
  deleteCategoryTemplate: (id: string) => api.delete(`/admin/category-templates/${id}`),
  // Platform communication center
  platformAnnouncements: (params?: Record<string, string | number | boolean>) =>
    api.get('/admin/communications/announcements', { params }),
  createPlatformAnnouncement: (data: any) => api.post('/admin/communications/announcements', data),
  updatePlatformAnnouncement: (id: string, data: any) => api.put(`/admin/communications/announcements/${id}`, data),
  deletePlatformAnnouncement: (id: string) => api.delete(`/admin/communications/announcements/${id}`),
  publishPlatformAnnouncement: (id: string) => api.post(`/admin/communications/announcements/${id}/publish`),
  unpublishPlatformAnnouncement: (id: string) => api.post(`/admin/communications/announcements/${id}/unpublish`),
  platformEmailCampaigns: (params?: Record<string, string | number | boolean>) =>
    api.get('/admin/communications/email-campaigns', { params }),
  createPlatformEmailCampaign: (data: any) => api.post('/admin/communications/email-campaigns', data),
  updatePlatformEmailCampaign: (id: string, data: any) => api.put(`/admin/communications/email-campaigns/${id}`, data),
  deletePlatformEmailCampaign: (id: string) => api.delete(`/admin/communications/email-campaigns/${id}`),
  sendPlatformEmailCampaign: (id: string) => api.post(`/admin/communications/email-campaigns/${id}/send`),
  platformBroadcasts: (params?: Record<string, string | number | boolean>) =>
    api.get('/admin/communications/broadcasts', { params }),
  createPlatformBroadcast: (data: any) => api.post('/admin/communications/broadcasts', data),
  updatePlatformBroadcast: (id: string, data: any) => api.put(`/admin/communications/broadcasts/${id}`, data),
  deletePlatformBroadcast: (id: string) => api.delete(`/admin/communications/broadcasts/${id}`),
  sendPlatformBroadcast: (id: string) => api.post(`/admin/communications/broadcasts/${id}/send`),
  privacyPolicy: () => api.get('/admin/privacy-policy'),
  createPrivacyPolicy: (data: any) => api.post('/admin/privacy-policy', data),
  updatePrivacyPolicy: (id: string | number, data: any) => api.put(`/admin/privacy-policy/${id}`, data),
  deletePrivacyPolicy: (id: string | number) => api.delete(`/admin/privacy-policy/${id}`),
  privacyPolicySettings: () => api.get('/admin/privacy-policy/settings'),
  updatePrivacyPolicySettings: (data: any) => api.put('/admin/privacy-policy/settings', data),
  privacyPolicyRevisions: () => api.get('/admin/privacy-policy/revisions'),
};

// Coupons API (organizer-scoped; validate is public)
export const couponsApi = {
  list: (params?: any) => api.get('/organizer/coupons', { params }),
  create: (data: any) => api.post('/organizer/coupons', data),
  update: (id: string, data: any) => api.put(`/organizer/coupons/${id}`, data),
  delete: (id: string) => api.delete(`/organizer/coupons/${id}`),
  validate: (data: any) => api.post('/coupons/validate', data),
};

// Organizer profile (settings)
export const organizerProfileApi = {
  get: () => api.get('/organizer/profile'),
  update: (data: any) => api.put('/organizer/profile', data),
};

export const organizerFinanceApi = {
  get: () => api.get('/organizer/finance-settings', { skipGlobalForbidden: true } as Record<string, unknown>),
  update: (data: Record<string, unknown>) =>
    api.put('/organizer/finance-settings', data, { skipGlobalForbidden: true } as Record<string, unknown>),
};

/**
 * Event-scoped Financial Settings (fee bearers who are platform-controlled percentages are
 * read-only here, GST registration/mode, refund policy, settlement preference). Distinct from
 * `eventsApi.getEventFinanceSettings` (tax display + payment methods + bank details).
 */
export type EventFinancialSettingsData = {
  refund: {
    policy: string;
    deadline: string | null;
    allow_partial: boolean;
    approval_mode: string;
    options: string[];
  };
  platform_fee_bearer: 'organizer' | 'attendee';
  gateway_fee_bearer: 'organizer' | 'attendee';
  gst: {
    mode: 'inclusive' | 'exclusive';
    registered: boolean;
    gstin: string | null;
    legal_business_name: string | null;
    business_address: string | null;
  };
  settlement_preference: string;
  settlement_options: string[];
  platform_controlled: {
    platform_fee_percent: number;
    gateway_fee_percent: number;
    gst_percent: number;
    editable: boolean;
  };
};

export const eventFinancialSettingsApi = {
  show: (eventId: string) =>
    api.get<{ success: boolean; data: EventFinancialSettingsData }>(`/organizer/events/${eventId}/financial-settings`),
  update: (eventId: string, data: Record<string, unknown>) =>
    api.put<{ success: boolean; data: EventFinancialSettingsData }>(`/organizer/events/${eventId}/financial-settings`, data),
};

export const paymentMethodsApi = {
  list: () =>
    api.get<{
      success: boolean;
      data: Array<{ id: string; label: string; fee: string; description?: string | null }>;
      meta?: { platform_commission_percent?: number };
    }>('/payment-methods', { cacheTtlMs: 300000 } as any),
};

export const adminPaymentMethodsApi = {
  list: () => api.get('/admin/payment-methods'),
  create: (data: Record<string, unknown>) => api.post('/admin/payment-methods', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/admin/payment-methods/${id}`, data),
  deactivate: (id: string) => api.delete(`/admin/payment-methods/${id}`),
};

// Settings: notifications, subscription, security, certificates, API keys
export const settingsApi = {
  notificationPreferences: {
    get: () => api.get('/notifications/preferences'),
    update: (data: any) => api.put('/notifications/preferences', data),
  },
  subscription: {
    current: () => api.get('/organizer/subscription/current'),
    usage: () => api.get('/organizer/subscription/usage'),
    history: () => api.get('/organizer/subscription/history'),
    upgrade: (data: any) => api.post('/organizer/subscription/upgrade', data),
    downgrade: (data: any) => api.post('/organizer/subscription/downgrade', data),
    pause: () => api.post('/organizer/subscription/pause'),
    renew: () => api.post('/organizer/subscription/renew'),
    cancel: () => api.post('/organizer/subscription/cancel'),
  },
  changePassword: (data: {
    current_password?: string;
    password: string;
    password_confirmation: string;
  }) => api.put('/user/change-password', data),
  certificateTemplates: {
    list: () => api.get('/organizer/certificate/templates'),
    create: (data: any) => api.post('/organizer/certificate/templates', data),
  },
};

// Certificates API
export const certificatesApi = {
  // Organizer-scoped Templates
  templates: (eventId?: string) =>
    api.get('/organizer/certificate/templates', eventId ? { params: { event_id: eventId } } : undefined),
  createTemplate: (data: any) => api.post('/organizer/certificate/templates', data),
  updateTemplate: (id: string, data: any) => api.put(`/organizer/certificate/templates/${id}`, data),
  deleteTemplate: (id: string) => api.delete(`/organizer/certificate/templates/${id}`),

  // Event Signatures
  listSignatures: (eventId: string) => api.get(`/organizer/certificate/events/${eventId}/signatures`),
  createSignature: (eventId: string, data: FormData) =>
    api.post(`/organizer/certificate/events/${eventId}/signatures`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateSignature: (eventId: string, signatureId: string, data: FormData) =>
    api.post(`/organizer/certificate/events/${eventId}/signatures/${signatureId}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteSignature: (eventId: string, signatureId: string) =>
    api.delete(`/organizer/certificate/events/${eventId}/signatures/${signatureId}`),

  // Event-specific Certificates & Issuance
  list: (eventId: string) => api.get(`/organizer/certificate/events/${eventId}/certificates`),
  generateForEvent: (eventId: string, data: { template_id: string; scope: 'all' | 'selected' | 'winners' | 'organizer_team'; registration_ids?: string[]; certificate_type?: string }) =>
    api.post(`/organizer/certificate/events/${eventId}/generate`, data),
  publishForEvent: (eventId: string) => api.post(`/organizer/certificate/events/${eventId}/publish`, {}),
  emailForEvent: (eventId: string, certificateIds?: string[]) =>
    api.post(`/organizer/certificate/events/${eventId}/email`, certificateIds?.length ? { certificate_ids: certificateIds } : {}),
  bulkDownload: (eventId: string, certificateIds?: string[]) =>
    api.get(`/organizer/certificate/events/${eventId}/download/bulk`, {
      params: certificateIds?.length ? { certificate_ids: certificateIds } : undefined,
      responseType: 'blob',
    }),
  previewPdf: (eventId: string, templateId: string) =>
    api.post(`/organizer/certificate/events/${eventId}/preview`, { template_id: templateId }, { responseType: 'blob' }),
  testPdf: (eventId: string, templateId: string) =>
    api.post(`/organizer/certificate/events/${eventId}/test`, { template_id: templateId }, { responseType: 'blob' }),
  processRules: (eventId: string) =>
    api.post(`/organizer/certificate/events/${eventId}/process-rules`, {}),
  create: (eventId: string, data: { name: string; template_body: string; background_image?: string }) =>
    api.post(`/organizer/events/${eventId}/certificates`, data),
  update: (eventId: string, certId: string, data: any) =>
    api.put(`/organizer/events/${eventId}/certificates/${certId}`, data),
  issue: (eventId: string, certId: string, data: { registration_ids: string[] }) =>
    api.post(`/organizer/events/${eventId}/certificates/${certId}/issue`, { registration_ids: data.registration_ids }),

  // Common/Participant
  myCertificates: () => api.get('/certificates/my'),
  downloadCertificate: (id: string) =>
    api.get(`/certificates/${id}/download`, { responseType: 'blob' }),

  // Enhanced Dynamic Certificate Management
  uploadPdfTemplate: (templateId: string, formData: FormData) =>
    api.post(`/organizer/certificate/templates/${templateId}/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  listGroups: (eventId: string) =>
    api.get(`/organizer/certificate/events/${eventId}/groups`),
  createGroup: (eventId: string, data: any) =>
    api.post(`/organizer/certificate/events/${eventId}/groups`, data),
  updateGroup: (eventId: string, groupId: string, data: any) =>
    api.put(`/organizer/certificate/events/${eventId}/groups/${groupId}`, data),
  duplicateGroup: (eventId: string, groupId: string, name?: string) =>
    api.post(`/organizer/certificate/events/${eventId}/groups/${groupId}/duplicate`, { name }),
  deleteGroup: (eventId: string, groupId: string) =>
    api.delete(`/organizer/certificate/events/${eventId}/groups/${groupId}`),
  getDataSources: (eventId: string) =>
    api.get(`/organizer/certificate/events/${eventId}/data-sources`),
  sampleImportCsv: () =>
    api.get('/organizer/certificate/import/sample-csv', { responseType: 'blob' }),
  previewImport: (eventId: string, formData: FormData) =>
    api.post(`/organizer/certificate/events/${eventId}/import-preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  previewBatch: (eventId: string, data: any) =>
    api.post(`/organizer/certificate/events/${eventId}/preview-batch`, data),
  issueBatch: (eventId: string, data: any) =>
    api.post(`/organizer/certificate/events/${eventId}/issue-batch`, data),
  revokeCertificate: (certId: string, reason: string) =>
    api.post(`/organizer/certificate/${certId}/revoke`, { reason }),
  reissueCertificate: (certId: string, data: any) =>
    api.post(`/organizer/certificate/${certId}/reissue`, data),
};

// Phase 23A — Universal Credential Engine API
export const credentialsApi = {
  // Participant
  myCredentials: () => api.get('/credentials/my', { cacheTtlMs: 30000 }),
  getCredential: (id: string) => api.get(`/credentials/${id}`),
  downloadCredential: (id: string) => api.get(`/credentials/${id}/download`, { responseType: 'blob' }),
  participantRegenerate: (id: string) => api.post(`/credentials/${id}/regenerate`, {}),

  // Public verification
  publicVerify: (code: string) => api.get(`/verify/${code}`),

  // Organizer — event-level
  eventCredentials: (eventId: string, params?: Record<string, string>) =>
    api.get(`/events/${eventId}/credentials`, { params }),
  eventCredentialStats: (eventId: string) =>
    api.get(`/events/${eventId}/credentials/stats`),

  // Organizer — activity-level
  activityCredentials: (eventId: string, activityId: string) =>
    api.get(`/events/${eventId}/activities/${activityId}/credentials`),
  activityCredentialStats: (eventId: string, activityId: string) =>
    api.get(`/events/${eventId}/activities/${activityId}/credentials/stats`),
  bulkIssue: (eventId: string, activityId: string, data: Record<string, unknown>) =>
    api.post(`/events/${eventId}/activities/${activityId}/credentials/bulk-issue`, data),

  // Organizer — credential actions
  revokeCredential: (id: string, reason: string) =>
    api.post(`/credentials/${id}/revoke`, { reason }),
  regenerateCredential: (id: string, reason?: string) =>
    api.post(`/credentials/${id}/regenerate`, reason ? { reason } : {}),
  regenerationHistory: (id: string) => api.get(`/credentials/${id}/history`),

  // Credential Rules
  rulesIndex: (eventId: string) => api.get(`/events/${eventId}/credential-rules`),
  ruleCreate: (eventId: string, data: Record<string, unknown>) =>
    api.post(`/events/${eventId}/credential-rules`, data),
  ruleUpdate: (eventId: string, ruleId: string, data: Record<string, unknown>) =>
    api.put(`/events/${eventId}/credential-rules/${ruleId}`, data),
  ruleDelete: (eventId: string, ruleId: string) =>
    api.delete(`/events/${eventId}/credential-rules/${ruleId}`),

  // Admin
  adminTemplates: () => api.get('/admin/credentials/templates'),
  adminCreateTemplate: (data: Record<string, unknown>) =>
    api.post('/admin/credentials/templates', data),
  adminStats: () => api.get('/admin/credentials/stats'),
  adminReport: () => api.get('/admin/credentials/report'),
};

/** EDOP — Internal Enterprise Document Operations (org-scoped, DAVS-backed). */
export const edopApi = {
  catalog: () => api.get('/edop/v1/catalog'),
  dashboard: (organizationId: string, period?: string) =>
    api.get('/edop/v1/dashboard', { params: { organization_id: organizationId, ...(period ? { period } : {}) } }),
  search: (organizationId: string, params?: Record<string, string | number>) =>
    api.get('/edop/v1/search', { params: { organization_id: organizationId, ...params } }),
  employeeTimeline: (organizationId: string, params?: Record<string, string>) =>
    api.get('/edop/v1/employees/timeline', { params: { organization_id: organizationId, ...params } }),
  branding: (organizationId: string) =>
    api.get('/edop/v1/branding', { params: { organization_id: organizationId } }),
  updateBranding: (organizationId: string, branding: Record<string, unknown>) =>
    api.put('/edop/v1/branding', { organization_id: organizationId, branding }),
  documentTypes: (organizationId: string) =>
    api.get('/edop/v1/document-types', { params: { organization_id: organizationId } }),
  createDocumentType: (organizationId: string, data: Record<string, unknown>) =>
    api.post('/edop/v1/document-types', { organization_id: organizationId, ...data }),
  assignTemplate: (organizationId: string, data: Record<string, unknown>) =>
    api.post('/edop/v1/template-assignments', { organization_id: organizationId, ...data }),
  documentRequests: (organizationId: string, params?: Record<string, string | number>) =>
    api.get('/edop/v1/document-requests', { params: { organization_id: organizationId, ...params } }),
  createRequest: (organizationId: string, data: Record<string, unknown>) =>
    api.post('/edop/v1/document-requests', { organization_id: organizationId, ...data }),
  getRequest: (id: string) => api.get(`/edop/v1/document-requests/${id}`),
  build: (id: string) => api.post(`/edop/v1/document-requests/${id}/build`),
  submit: (id: string) => api.post(`/edop/v1/document-requests/${id}/submit`),
  issue: (id: string) => api.post(`/edop/v1/document-requests/${id}/issue`),
  validateRequest: (id: string) => api.get(`/edop/v1/document-requests/${id}/validate`),
  compatibility: (id: string) => api.get(`/edop/v1/document-requests/${id}/compatibility`),
  upgradeTemplate: (id: string) => api.post(`/edop/v1/document-requests/${id}/upgrade-template`),
  templateHealth: (organizationId: string) =>
    api.get('/edop/v1/templates/health', { params: { organization_id: organizationId } }),
  compareTemplates: (organizationId: string, leftVersionId: string, rightVersionId: string) =>
    api.get('/edop/v1/templates/compare', {
      params: { organization_id: organizationId, left_version_id: leftVersionId, right_version_id: rightVersionId },
    }),
  queues: (organizationId: string) =>
    api.get('/edop/v1/queues', { params: { organization_id: organizationId } }),
  retryFailure: (failureId: string) => api.post(`/edop/v1/queues/failures/${failureId}/retry`),
  bulk: (organizationId: string, data: Record<string, unknown>) =>
    api.post('/edop/v1/document-requests/bulk', { organization_id: organizationId, ...data }),
};

/** DAVS — Digital Authentication & Verification Service (public + admin). */
export const davsApi = {
  verify: (reference: string, params?: { version?: number }) =>
    api.get(`/davs/v1/verify/${encodeURIComponent(reference)}`, { params }),
  versions: (reference: string) =>
    api.get(`/davs/v1/verify/${encodeURIComponent(reference)}/versions`),
  timeline: (reference: string) =>
    api.get(`/davs/v1/verify/${encodeURIComponent(reference)}/timeline`),
  status: (reference: string) =>
    api.get(`/davs/v1/verify/${encodeURIComponent(reference)}/status`),
  dashboard: () => api.get('/davs/v1/dashboard'),
  catalog: () => api.get('/davs/v1/catalog'),
  auditLogs: (params?: Record<string, string | number | undefined>) =>
    api.get('/davs/v1/audit-logs', { params }),
  documentTypes: (params?: Record<string, string | number | undefined>) =>
    api.get('/davs/v1/document-types', { params }),
  createDocumentType: (data: Record<string, unknown>) => api.post('/davs/v1/document-types', data),
  documents: (params?: Record<string, string | number | undefined>) =>
    api.get('/davs/v1/documents', { params }),
  createDocument: (data: Record<string, unknown>) => api.post('/davs/v1/documents', data),
  showDocument: (id: string) => api.get(`/davs/v1/documents/${id}`),
  updateDocument: (id: string, data: Record<string, unknown>) => api.put(`/davs/v1/documents/${id}`, data),
  buildDocument: (data: Record<string, unknown>) => api.post('/davs/v1/documents/build', data),
  renderDocument: (id: string, data?: Record<string, unknown>) =>
    api.post(`/davs/v1/documents/${id}/render`, data ?? {}),
  publishDocument: (id: string, data?: Record<string, unknown>) =>
    api.post(`/davs/v1/documents/${id}/publish`, data ?? {}),
  revokeDocument: (id: string, data?: { reason?: string }) =>
    api.post(`/davs/v1/documents/${id}/revoke`, data ?? {}),
  restoreDocument: (id: string) => api.post(`/davs/v1/documents/${id}/restore`),
  archiveDocument: (id: string) => api.post(`/davs/v1/documents/${id}/archive`),
  documentVersions: (id: string) => api.get(`/davs/v1/documents/${id}/versions`),
  documentHistory: (id: string) => api.get(`/davs/v1/documents/${id}/history`),
  documentMetadata: (id: string) => api.get(`/davs/v1/documents/${id}/metadata`),
  downloadArtifact: (id: string) =>
    api.get(`/davs/v1/documents/${id}/artifact/download`, { responseType: 'blob' }),
  templates: (params?: Record<string, string | number | undefined>) =>
    api.get('/davs/v1/templates', { params }),
  createTemplate: (data: Record<string, unknown>) => api.post('/davs/v1/templates', data),
  showTemplate: (id: string) => api.get(`/davs/v1/templates/${id}`),
  publishTemplateVersion: (versionId: string) =>
    api.post(`/davs/v1/template-versions/${versionId}/publish`),
  createTemplateVersion: (templateId: string, data: Record<string, unknown>) =>
    api.post(`/davs/v1/templates/${templateId}/versions`, data),
  updateTemplate: (id: string, data: Record<string, unknown>) => api.put(`/davs/v1/templates/${id}`, data),
  showTemplateVersion: (versionId: string) => api.get(`/davs/v1/template-versions/${versionId}`),
  updateTemplateVersion: (versionId: string, data: Record<string, unknown>) =>
    api.put(`/davs/v1/template-versions/${versionId}`, data),
  previewDocument: (data: Record<string, unknown>) => api.post('/davs/v1/preview', data),
  importDocument: (data: Record<string, unknown>) => api.post('/davs/v1/documents/import', data),
  updateDocumentType: (id: string, data: Record<string, unknown>) =>
    api.put(`/davs/v1/document-types/${id}`, data),
  issuers: (params?: Record<string, string | number | undefined>) =>
    api.get('/davs/v1/issuers', { params }),
  letterheads: (params?: Record<string, string | number | undefined>) =>
    api.get('/davs/v1/letterheads', { params }),
  showLetterhead: (id: string) => api.get(`/davs/v1/letterheads/${id}`),
  createLetterhead: (data: FormData | Record<string, unknown>) =>
    api.post('/davs/v1/letterheads', data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
    }),
  updateLetterhead: (id: string, data: FormData | Record<string, unknown>) => {
    if (data instanceof FormData) {
      data.append('_method', 'PUT');
      return api.post(`/davs/v1/letterheads/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.put(`/davs/v1/letterheads/${id}`, data);
  },
  putLetterhead: (id: string, data: Record<string, unknown>) => api.put(`/davs/v1/letterheads/${id}`, data),
  qrRegistry: (params?: Record<string, string | number | undefined>) =>
    api.get('/davs/v1/qr-registry', { params }),
  createQrRegistry: (data: FormData | Record<string, unknown>) =>
    api.post('/davs/v1/qr-registry', data, {
      headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
    }),
  updateQrRegistry: (id: string, data: FormData | Record<string, unknown>) => {
    if (data instanceof FormData) {
      data.append('_method', 'PUT');
      return api.post(`/davs/v1/qr-registry/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.put(`/davs/v1/qr-registry/${id}`, data);
  },
  showQrRegistry: (id: string) => api.get(`/davs/v1/qr-registry/${id}`),
  suggestQrReference: (documentType?: string) =>
    api.get('/davs/v1/qr-registry/suggest-reference', { params: { document_type: documentType } }),
  qrRegistryAnalytics: (id: string) => api.get(`/davs/v1/qr-registry/${id}/analytics`),
  approvalTasks: (params?: Record<string, string | number | undefined>) =>
    api.get('/davs/v1/approval-tasks', { params }),
  approveTask: (id: string, data?: { comment?: string }) =>
    api.post(`/davs/v1/approval-tasks/${id}/approve`, data ?? {}),
  rejectTask: (id: string, data?: { comment?: string }) =>
    api.post(`/davs/v1/approval-tasks/${id}/reject`, data ?? {}),
  returnTask: (id: string, data?: { comment?: string }) =>
    api.post(`/davs/v1/approval-tasks/${id}/return`, data ?? {}),
  submitDocument: (id: string) => api.post(`/davs/v1/documents/${id}/submit`),
  uploadDocument: (data: FormData) =>
    api.post('/davs/v1/documents/upload', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateDocumentRecord: (id: string, data: Record<string, unknown>) =>
    api.patch(`/davs/v1/documents/${id}/record`, data),
  checkDocumentIntegrity: (id: string, data: FormData) =>
    api.post(`/davs/v1/documents/${id}/integrity`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  downloadDocumentFile: (id: string) =>
    api.get(`/davs/v1/documents/${id}/file`, { responseType: 'blob' }),
  replaceDocumentFile: (id: string, data: FormData) =>
    api.post(`/davs/v1/documents/${id}/file`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  issueDocument: (id: string) => api.post(`/davs/v1/documents/${id}/issue`, {}),
  reissueDocument: (id: string, data: FormData) =>
    api.post(`/davs/v1/documents/${id}/reissue`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  publicVerify: (reference: string) => api.post('/davs/v1/public/verify', { reference }),
  publicIntegrity: (reference: string, sha256: string) =>
    api.post('/davs/v1/public/integrity', { reference, sha256 }),
  publicRequestDownload: (reference: string, email?: string) =>
    api.post('/davs/v1/public/download/request', { reference, email: email || null }),
  publicConfirmDownload: (reference: string, email: string, code: string) =>
    api.post('/davs/v1/public/download/confirm', { reference, email, code }),
};

// Admin ↔ Organizer support chat API
export const supportChatApi = {
  // Organizer/admin: create new chat
  createChat: (data: {
    organization_id?: string | null;
    event_id?: string | null;
    message: string;
    category?: string | null;
  }) => api.post('/chats', data),
  // Organizer: my chats
  myChats: (params?: { page?: number }) => api.get('/chats/my', { params }),
  // Admin: all chats (support inbox)
  adminChats: (params?: { page?: number; status?: string }) => api.get('/admin/chats', { params }),
  // Common: messages & actions
  getMessages: (chatId: string) => api.get(`/chats/${chatId}/messages`),
  sendMessage: (chatId: string, data: { message: string; attachment?: string | null }) =>
    api.post(`/chats/${chatId}/messages`, data),
  closeChat: (chatId: string) => api.post(`/chats/${chatId}/close`),
};



// AI Assistant API (organizer)
export const aiAssistantApi = {
  eventDescription: (data: { prompt: string; event_id?: string }) => api.post('/organizer/ai/event-description', data),
  generateAgenda: (data: { prompt: string; event_id?: string }) => api.post('/organizer/ai/generate-agenda', data),
  generateEmail: (data: { prompt: string; event_id?: string }) => api.post('/organizer/ai/generate-email', data),
  certificateText: (data: { prompt: string; event_id?: string }) => api.post('/organizer/ai/certificate-text', data),
  analyticsSummary: (data: { event_id: string }) => api.post('/organizer/ai/analytics-summary', data),
};

// Quiz API (organizer & participant)
export const quizApi = {
  // Organizer (Event-level legacy)
  listForEvent: (eventId: string) => api.get(`/organizer/events/${eventId}/quizzes`),
  create: (eventId: string, data: { title: string; questions?: any[] }) => api.post(`/organizer/events/${eventId}/quizzes`, data),
  show: (eventId: string, quizId: string) => api.get(`/organizer/events/${eventId}/quizzes/${quizId}`),
  startSession: (eventId: string, quizId: string) => api.post(`/organizer/events/${eventId}/quizzes/${quizId}/session/start`),
  endSession: (eventId: string, quizId: string) => api.post(`/organizer/events/${eventId}/quizzes/${quizId}/session/end`),

  // Dedicated Quiz Competition Workspace APIs
  resolveForActivity: (activityId: string) => api.get<{ success: boolean; data: any }>(`/activities/${activityId}/quiz`),
  activityDashboard: (activityId: string) => api.get<{ success: boolean; data: any }>(`/activities/${activityId}/quiz/dashboard`),
  updateSettings: (activityId: string, data: Record<string, unknown>) => api.put<{ success: boolean; data: any }>(`/activities/${activityId}/quiz/settings`, data),
  participants: (activityId: string, params?: { search?: string; status?: string }) => api.get<{ success: boolean; data: any }>(`/activities/${activityId}/quiz/participants`, { params }),
  linkParticipant: (activityId: string, data: { registration_id?: string; team_id?: string }) => api.post<{ success: boolean; data: any }>(`/activities/${activityId}/quiz/participants/link`, data),
  syncParticipants: (activityId: string) => api.post<{ success: boolean; data: any; synced_count: number; message: string }>(`/activities/${activityId}/quiz/participants/sync`, {}),
  removeParticipant: (activityId: string, participantId: string) => api.delete<{ success: boolean; message: string }>(`/activities/${activityId}/quiz/participants/${participantId}`),
  questions: (activityId: string, params?: { type?: string; category?: string; difficulty?: string }) => api.get<{ success: boolean; data: any }>(`/activities/${activityId}/quiz/questions`, { params }),
  storeQuestion: (activityId: string, data: Record<string, unknown>) => api.post<{ success: boolean; data: any }>(`/activities/${activityId}/quiz/questions`, data),
  updateQuestion: (activityId: string, questionId: string, data: Record<string, unknown>) => api.put<{ success: boolean; data: any }>(`/activities/${activityId}/quiz/questions/${questionId}`, data),
  deleteQuestion: (activityId: string, questionId: string) => api.delete<{ success: boolean; message: string }>(`/activities/${activityId}/quiz/questions/${questionId}`),
  validateImport: (activityId: string, formData: FormData) => api.post<{ success: boolean; data?: any; total_rows: number; valid_count: number; error_count: number; valid_rows: any[]; invalid_rows: any[]; warnings: string[]; file_name: string }>(`/activities/${activityId}/quiz/questions/import-validate`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  confirmImport: (activityId: string, data: { valid_rows: any[]; invalid_rows?: any[]; file_name: string }) => api.post<{ success: boolean; data: any; message: string }>(`/activities/${activityId}/quiz/questions/import-confirm`, data),
  importHistory: (activityId: string) => api.get<{ success: boolean; data: any[] }>(`/activities/${activityId}/quiz/import-history`),
  downloadTemplate: () => api.get('/quiz/template/download', { responseType: 'blob' }),
  activityLeaderboard: (activityId: string, options?: AxiosRequestConfig) => api.get<{ success: boolean; data: any[]; visible: boolean; total?: number; message?: string; registration_mode?: string }>(`/activities/${activityId}/quiz/leaderboard`, options),

  // Participant Attempt & Lifecycle APIs
  participantStart: (activityId: string) =>
    api.post<{
      success: boolean;
      has_active_attempt: boolean;
      data?: any;
      message?: string;
      access?: Record<string, any>;
    }>('/quiz/attempt/start', {
      activity_id: activityId,
    }),
  participantState: (activityId: string) =>
    api.get<{
      success: boolean;
      has_active_attempt: boolean;
      data?: any;
      last_attempt?: any;
      timed_out?: boolean;
      message?: string;
      access?: Record<string, any>;
    }>(
      '/quiz/attempt/state',
      { params: { activity_id: activityId } },
    ),
  participantSaveAnswer: (
    activityId: string,
    data: {
      session_id: string;
      question_id?: string;
      answer?: any;
      is_marked_for_review?: boolean;
      time_spent_seconds?: number;
      current_question_index?: number;
    },
  ) =>
    api.post<{ success: boolean; data: any }>('/quiz/attempt/answer', {
      ...data,
      activity_id: activityId,
    }),
  participantRecordViolation: (activityId: string, data: { session_id: string; type: string; details?: string }) =>
    api.post<{ success: boolean; data: any }>('/quiz/attempt/violation', {
      ...data,
      activity_id: activityId,
    }),
  participantSubmit: (activityId: string, data: { session_id: string }) =>
    api.post<{ success: boolean; message: string; data: any }>('/quiz/attempt/submit', {
      ...data,
      activity_id: activityId,
    }),
  participantResult: (activityId: string) =>
    api.get<{ success: boolean; data: any }>('/quiz/attempt/result', { params: { activity_id: activityId } }),

  // Legacy Participant Join
  join: (data: { quiz_code?: string; join_code?: string }) =>
    api.post('/quiz/join', { join_code: data.join_code || data.quiz_code }),
  submitAnswer: (data: { quiz_session_id?: string; quiz_id?: string; question_id: string; answer?: any; answer_index?: number }) => api.post('/quiz/answer', data),
  leaderboard: (quizId: string) => api.get(`/quiz/${quizId}/leaderboard`),
};

// Coding Competition API (organizer & participant)
export const codingApi = {
  // Participant
  listProblems: (eventId: string) => api.get(`/events/${eventId}/coding/problems`),
  showProblem: (eventId: string, problemId: string) => api.get(`/events/${eventId}/coding/problems/${problemId}`),
  submitCode: (eventId: string, problemId: string, data: { source_code: string; language: string }) =>
    api.post(`/events/${eventId}/coding/problems/${problemId}/submit`, data),
  leaderboard: (eventId: string) => api.get(`/events/${eventId}/coding/leaderboard`),
  // Organizer
  manageProblems: (eventId: string) => api.get(`/organizer/events/${eventId}/coding/problems`),
  createProblem: (eventId: string, data: any) => api.post(`/organizer/events/${eventId}/coding/problems`, data),
  updateProblem: (eventId: string, problemId: string, data: any) =>
    api.put(`/organizer/events/${eventId}/coding/problems/${problemId}`, data),
};

// Network Profile & Matchmaking API
export const networkApi = {
  getProfile: () => api.get('/network-profile'),
  updateProfile: (data: { skills?: string[]; interests?: string[]; bio?: string; linkedin?: string; github?: string; is_visible?: boolean }) =>
    api.put('/network-profile', data),
  matchmaking: (params?: { event_id?: string }) => api.get('/network-profile/matchmaking', { params }),
};

// Marketing & Email Campaigns API
export const marketingApi = {
  listCampaigns: () => api.get('/organizer/campaigns'),
  sendCampaign: (data: {
    event_id: string;
    name?: string;
    subject: string;
    content: string;
    type?: string;
    scheduled_at?: string;
    send_now?: boolean;
    audience?: string;
    template_id?: string;
    registration_status?: string;
    ticket_id?: string;
    team_id?: string;
    emails?: string[];
  }) => api.post('/organizer/campaigns/send', data),
  retryCampaign: (id: string) => api.post(`/organizer/campaigns/${id}/retry`),
};

// Event Collaborators API
export const collaboratorsApi = {
  list: (eventId: string) => api.get(`/organizer/events/${eventId}/collaborators`),
  add: (eventId: string, data: { user_id: string; permissions: string[] }) =>
    api.post(`/organizer/events/${eventId}/collaborators`, data),
  remove: (eventId: string, userId: string) =>
    api.delete(`/organizer/events/${eventId}/collaborators/${userId}`),
};


// Communications (Phase 4B)
export const communicationsApi = {
  send: (eventId: string, data: Record<string, unknown>) =>
    api.post(`/events/${eventId}/communications/send`, data),
  schedule: (eventId: string, data: Record<string, unknown>) =>
    api.post(`/events/${eventId}/communications/schedule`, data),
  history: (eventId: string, params?: { per_page?: number; channel?: string }) =>
    api.get(`/events/${eventId}/communications/history`, { params }),
  audienceCounts: (eventId: string) => api.get(`/events/${eventId}/communications/audience-counts`),
  previewSegment: (eventId: string, filters: Record<string, unknown>) =>
    api.post(`/events/${eventId}/communications/preview-segment`, { filters }),
  segments: (eventId: string) => api.get(`/events/${eventId}/communications/segments`),
  saveSegment: (data: { event_id: string; name: string; filters: Record<string, unknown> }) =>
    api.post('/audiences/segment', data),
  feedbackSummary: (eventId: string) => api.get(`/events/${eventId}/communications/feedback-summary`),
  requestFeedback: (eventId: string) => api.post(`/events/${eventId}/communications/feedback-request`),
};

export const crmApi = {
  tag: (data: { event_id: string; user_id: string; tag: string }) => api.post('/crm/tag', data),
  addNote: (data: { event_id: string; user_id: string; note: string }) => api.post('/crm/note', data),
  profile: (eventId: string, userId: string) => api.get(`/crm/events/${eventId}/attendees/${userId}`),
};

// CONNECTED TO BACKEND: Notifications API (GET /notifications, PUT /notifications/{id}/read, POST /notifications/read-all)
export const notificationsApi = {
  list: (params?: { page?: number; unread_only?: boolean; category?: string }) =>
    api.get('/notifications', { params, cacheTtlMs: 15000, silent: true }),
  unreadCount: () =>
    api.get('/notifications/unread-count', { cacheTtlMs: 15000, silent: true }),
  markRead: (id: string) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  archive: (id: string) => api.post(`/notifications/${id}/archive`),
  preferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data: Record<string, unknown>) => api.put('/notifications/preferences', data),
};

// CONNECTED TO BACKEND: Admin Notification Policies (GET/PUT /admin/notification-policies)
export const adminNotificationPolicyApi = {
  list: () => api.get('/admin/notification-policies'),
  update: (
    eventKey: string,
    data: {
      priority?: string;
      mandatory?: boolean;
      enabled?: boolean;
      default_channels?: string[];
      optional_channels?: string[];
      sender_email?: string | null;
      email_template?: string | null;
      queue_enabled?: boolean;
      retry_count?: number;
      retry_interval?: number;
    }
  ) => api.put(`/admin/notification-policies/${eventKey}`, data),
};

// CONNECTED TO BACKEND: Admin Communication Senders (CRUD /admin/communication-senders)
export const adminCommunicationSenderApi = {
  list: () => api.get('/admin/communication-senders'),
  create: (data: { email: string; name: string; purpose?: string; is_active?: boolean }) =>
    api.post('/admin/communication-senders', data),
  update: (id: string, data: { email?: string; name?: string; purpose?: string; is_active?: boolean }) =>
    api.put(`/admin/communication-senders/${id}`, data),
  remove: (id: string) => api.delete(`/admin/communication-senders/${id}`),
};

// CONNECTED TO BACKEND: Legal Document Management System
export const legalApi = {
  list: (params?: { category?: string; organization_id?: string }) =>
    api.get('/legal', { params }),
  linkRegistry: () => api.get('/legal/links'),
  footerLinks: () => api.get('/legal/footer-links'),
  get: (slug: string, params?: { organization_id?: string }) =>
    api.get(`/legal/${slug}`, { params }),
  consent: (payload: { slug?: string; context?: string; slugs?: string[]; organization_id?: string }) =>
    api.post('/legal/consent', payload),
  withdrawConsent: (slug: string, context: string) =>
    api.post('/legal/consent/withdraw', { slug, context }),
  pendingConsent: (organizationId?: string) =>
    api.get('/legal/consent/pending', { params: organizationId ? { organization_id: organizationId } : undefined }),
};

export const LEGAL_CATEGORIES = [
  'Platform', 'Policies', 'Compliance', 'Agreements', 'Organization',
  'Employment', 'Events', 'Marketplace', 'Legal Notices',
] as const;

export const adminLegalApi = {
  list: (params?: Record<string, string | boolean | undefined>) =>
    api.get('/admin/legal-documents', { params }),
  categories: () => api.get('/admin/legal-documents/categories'),
  searchLinks: (q?: string) => api.get('/admin/legal-documents/search-links', { params: q ? { q } : undefined }),
  get: (id: string) => api.get(`/admin/legal-documents/${id}`),
  create: (data: Record<string, unknown>) => api.post('/admin/legal-documents', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/admin/legal-documents/${id}`, data),
  publish: (id: string, revision_notes?: string) =>
    api.post(`/admin/legal-documents/${id}/publish`, revision_notes ? { revision_notes } : {}),
  unpublish: (id: string) => api.post(`/admin/legal-documents/${id}/unpublish`),
  archive: (id: string) => api.post(`/admin/legal-documents/${id}/archive`),
  restore: (id: string) => api.post(`/admin/legal-documents/${id}/restore`),
  remove: (id: string) => api.delete(`/admin/legal-documents/${id}`),
  duplicate: (id: string) => api.post(`/admin/legal-documents/${id}/duplicate`),
  revisions: (id: string) => api.get(`/admin/legal-documents/${id}/revisions`),
  compareRevisions: (id: string, leftId: string, rightId: string) =>
    api.get(`/admin/legal-documents/${id}/revisions/compare`, { params: { left_id: leftId, right_id: rightId } }),
  restoreRevision: (id: string, revisionId: string) =>
    api.post(`/admin/legal-documents/${id}/revisions/${revisionId}/restore`),
  auditLogs: (id: string) => api.get(`/admin/legal-documents/${id}/audit-logs`),
  bulk: (action: string, ids: string[], category?: string) =>
    api.post('/admin/legal-documents/bulk', { action, ids, category }),
  export: (ids?: string[]) =>
    api.get('/admin/legal-documents/export', { params: ids?.length ? { ids } : undefined, responseType: 'blob' }),
  import: (documents: Record<string, unknown>[]) =>
    api.post('/admin/legal-documents/import', { documents }),
  preview: (markdown: string, slug?: string) => api.post('/admin/legal-documents/preview', { markdown, slug }),
  footerLinks: () => api.get('/admin/legal-documents/footer-links'),
  updateFooterLinks: (links: Array<{
    id?: string;
    legal_document_id?: string | null;
    label: string;
    href?: string | null;
    sort_order?: number;
    is_active?: boolean;
  }>) => api.put('/admin/legal-documents/footer-links', { links }),
  uploadImage: (id: string, form: FormData) =>
    api.post(`/admin/legal-documents/${id}/upload-image`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadAttachment: (id: string, form: FormData) =>
    api.post(`/admin/legal-documents/${id}/upload-attachment`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

/** Read-only compliance viewer for legal_consents. */
export const adminLegalConsentApi = {
  list: (params?: {
    search?: string;
    document?: string;
    organization_id?: string;
    user_role?: string;
    date_from?: string;
    date_to?: string;
    sort?: string;
    direction?: string;
    page?: number;
    per_page?: number;
  }) => api.get('/admin/legal-consents', { params }),
  get: (id: string) => api.get(`/admin/legal-consents/${id}`),
  filters: () => api.get('/admin/legal-consents/filters'),
  exportCsv: (params?: Record<string, string | undefined>) =>
    api.get('/admin/legal-consents/export', {
      params: { ...params, format: 'csv' },
      responseType: 'blob',
    }),
};

export const contactApi = {
  submit: (data: FormData | { name: string; email: string; subject: string; message: string }) =>
    api.post('/contact-messages', data),
};

export const endorsementsApi = {
  pending: () => api.get('/endorsements/pending'),
  act: (
    endorsementId: string,
    data: { action: 'approve' | 'reject'; signature?: string; comments?: string }
  ) => api.post(`/endorsements/${endorsementId}/act`, data),
};

// Phase 24B Part 2 — Marketplace, Settlement & Connected Accounts

export const organizerPaymentProfileApi = {
  show: () => api.get('/organizer/payment-profile'),
  create: (data: Record<string, unknown>) => api.post('/organizer/payment-profile', data),
  sync: () => api.post('/organizer/payment-profile/sync'),
  continueOnboarding: (data?: Record<string, unknown>) =>
    api.post('/organizer/payment-profile/continue-onboarding', data || {}),
  onboardingLink: (returnUrl: string) =>
    api.post('/organizer/payment-profile/onboarding-link', { return_url: returnUrl }),
  disconnect: () => api.delete('/organizer/payment-profile'),
};

export const organizerSettlementReadinessApi = {
  show: (eventId?: string) =>
    api.get('/organizer/settlement-readiness', {
      params: eventId ? { event_id: eventId } : undefined,
    }),
};

export const organizerDefaultSettlementAccountApi = {
  show: () => api.get('/organizer/settlement-account'),
  submit: (data: Record<string, unknown>) => api.post('/organizer/settlement-account', data),
  sync: () => api.post('/organizer/settlement-account/sync'),
  continueKyc: (data: Record<string, unknown> = {}) =>
    api.post('/organizer/settlement-account/continue-kyc', data),
  updateRegisteredAddress: (data: Record<string, unknown>) =>
    api.put('/organizer/settlement-account/registered-address', data),
};

export const organizerReportsApi = {
  summary: (from: string, to: string) => api.get('/organizer/reports/summary', { params: { from, to } }),
  paymentsCsvUrl: (from: string, to: string) => `/organizer/reports/payments/csv?from=${from}&to=${to}`,
};

export const adminSettlementRequestApi = {
  list: (params?: { status?: string; page?: number }) => api.get('/admin/settlement-requests', { params }),
  show: (id: string) => api.get(`/admin/settlement-requests/${id}`),
  startReview: (id: string, notes?: string) => api.post(`/admin/settlement-requests/${id}/start-review`, { notes }),
  approve: (id: string, notes: string) => api.post(`/admin/settlement-requests/${id}/approve`, { notes }),
  reject: (id: string, notes: string) => api.post(`/admin/settlement-requests/${id}/reject`, { notes }),
  markProcessing: (id: string, notes?: string) => api.post(`/admin/settlement-requests/${id}/mark-processing`, { notes }),
  markPaid: (id: string, data: { transfer_reference: string; transfer_channel: string; notes?: string }) =>
    api.post(`/admin/settlement-requests/${id}/mark-paid`, data),
  freeze: (id: string, reason: string, notes: string) => api.post(`/admin/settlement-requests/${id}/freeze`, { reason, notes }),
  release: (id: string, notes: string) => api.post(`/admin/settlement-requests/${id}/release`, { notes }),
};

export const adminFinanceApi = {
  summary: (from: string, to: string) => api.get('/admin/finance/summary', { params: { from, to } }),
  transactionsCsvUrl: (from: string, to: string) => `/admin/finance/transactions/csv?from=${from}&to=${to}`,
  feeRules: () => api.get('/admin/finance/fee-rules'),
  createFeeRule: (data: Record<string, unknown>) => api.post('/admin/finance/fee-rules', data),
  updateFeeRule: (id: string, data: Record<string, unknown>) => api.put(`/admin/finance/fee-rules/${id}`, data),
  deleteFeeRule: (id: string) => api.delete(`/admin/finance/fee-rules/${id}`),
  webhookLogs: (params?: Record<string, unknown>) => api.get('/admin/finance/webhook-logs', { params }),
  replayWebhook: (id: string) => api.post(`/admin/finance/webhook-logs/${id}/replay`),
};

export const maintenanceApi = {
  status: () => api.get<{ enabled: boolean; message?: string }>('/maintenance-status', { cacheTtlMs: 60000, suppressServerErrorToast: true } as any),
};

export default api;
